(() => {
  "use strict";

  let behaviorFlow = null;
  let behaviorVisit = 0, behaviorStep = "", behaviorStepStarted = 0, behaviorOpened = false;
  const behaviorSelections = new Map();
  const behaviorPending = new Set();
  function behaviorDetail(event, fields = {}) {
    try {
      if (!behaviorFlow || !state.open || state.overlay.hidden) return;
      behaviorCall("detail", behaviorFlow, event, {
        step: state.current, visit_index: behaviorVisit,
        rx_method: selectedMethod() || "none",
        elapsed_ms: Math.max(0, Math.min(3600000, Date.now() - behaviorStepStarted)),
        ...fields
      });
    } catch {}
  }
  function behaviorView() {
    try {
      if (!behaviorFlow || !state.open || state.overlay.hidden || behaviorStep === state.current) return;
      const panel = state.panels.get(state.current);
      if (!panel || panel.hidden) return;
      behaviorStep = state.current;
      behaviorVisit += 1;
      behaviorStepStarted = Date.now();
      behaviorSelections.clear();
      behaviorDetail("configurator_step_viewed");
      if (state.current === "review") {
        behaviorCall("mark", behaviorFlow, "configuration_completed");
        behaviorDetail("configurator_price_reviewed");
      }
    } catch {}
  }
  function behaviorCartContext(frameOnly) {
    try {
      return !frameOnly && behaviorFlow ? { handle: behaviorFlow, fields: { step: state.current, visit_index: behaviorVisit, rx_method: selectedMethod() || "none" } } : null;
    } catch { return null; }
  }
  function behaviorCartMatches(cart, pairId) {
    try { return Array.isArray(cart.items) && cart.items.some((item) => propertyValue(item, "_elookus_pair_id") === pairId); }
    catch { return false; }
  }
  function behaviorSelection(key) {
    try {
      if (!behaviorFlow || !state.open || state.overlay.hidden) return;
      let group = "", value = "";
      if (key === "type") { group = "type"; value = selectedType(); }
      else if (key === "method") { group = "method"; value = selectedMethod(); }
      else if (["function_prescription", "function_readers"].includes(key)) {
        group = "function";
        const family = selectedSolutionKey();
        value = { solution_clear: "clear", solution_readers_clear: "clear", solution_blue: "blue_light_filtering", solution_readers_blue: "blue_light_filtering", solution_photo: "photochromic", solution_tinted: "tinted_prescription" }[family] || "";
      } else if (["solution_clear", "solution_blue", "solution_photo", "solution_tinted", "solution_readers_clear", "solution_readers_blue"].includes(key)) {
        group = "solution";
        const solution = solutionFor(checkedControl(key), state.fields.get(key)?.family);
        value = { "1.61": "index_161", "1.67": "index_167", "1.74": "index_174" }[solution?.index] || "";
      }
      if (!group || !value || behaviorSelections.get(group) === value) return;
      behaviorSelections.set(group, value);
      behaviorDetail("configurator_option_selected", { option_group: group, option_value: value });
    } catch {}
  }
  function behaviorCall(method, ...args) {
    try { return window.ElookusBehaviorV1?.[method]?.(...args) ?? null; } catch { return null; }
  }

  if (window.__ELOOKUS_LENS_FLOW_V2__) return;
  window.__ELOOKUS_LENS_FLOW_V2__ = true;

  const FIELD_MAP = Object.freeze({
    radio_purchase_mode: { key: "purchase", step: "entry" },
    paragraph_frame_only_notice: { key: "frame_notice", step: "entry" },
    radio_prescription_type: { key: "type", step: "type" },
    dropdown_readers_power: { key: "readers_power", step: "type" },
    radio_prescription_method: { key: "method", step: "method" },
    file_prescription_upload: { key: "upload", step: "details" },
    paragraph_send_prescription_later: { key: "later_notice", step: "method" },
    dropdown_od_sph: { key: "od_sph", step: "details" },
    dropdown_os_sph: { key: "os_sph", step: "details" },
    dropdown_od_cyl: { key: "od_cyl", step: "details" },
    dropdown_os_cyl: { key: "os_cyl", step: "details" },
    dropdown_od_axis: { key: "od_axis", step: "details" },
    dropdown_os_axis: { key: "os_axis", step: "details" },
    radio_pd_mode: { key: "pd_mode", step: "details" },
    dropdown_total_pd: { key: "total_pd", step: "details" },
    number_od_pd: { key: "od_pd", step: "details" },
    number_os_pd: { key: "os_pd", step: "details" },
    radio_lens_function_prescription: { key: "function_prescription", step: "function" },
    radio_lens_function_readers: { key: "function_readers", step: "function" },
    radio_lens_solution_clear: { key: "solution_clear", step: "solution", family: "clear" },
    radio_lens_solution_blue_light_filtering: { key: "solution_blue", step: "solution", family: "blue_light_filtering" },
    radio_lens_solution_photochromic: { key: "solution_photo", step: "solution", family: "photochromic" },
    radio_lens_solution_tinted_prescription: { key: "solution_tinted", step: "solution", family: "tinted_prescription" },
    radio_lens_solution_readers_clear: { key: "solution_readers_clear", step: "solution", family: "clear" },
    radio_lens_solution_readers_blue_light_filtering: { key: "solution_readers_blue", step: "solution", family: "blue_light_filtering" },
    paragraph_range_notice_clear: { key: "range_clear", step: "solution" },
    paragraph_range_notice_blue_light_filtering: { key: "range_blue", step: "solution" },
    paragraph_range_notice_photochromic: { key: "range_photo", step: "solution" },
    paragraph_range_notice_tinted_prescription: { key: "range_tinted", step: "solution" },
    paragraph_range_notice_readers_clear: { key: "range_readers_clear", step: "solution" },
    paragraph_range_notice_readers_blue_light_filtering: { key: "range_readers_blue", step: "solution" },
  });

  const FALLBACKS = [
    [/purchase mode|购买方式/, "radio_purchase_mode"],
    [/prescription type|处方类型/, "radio_prescription_type"],
    [/prescription method|处方提供方式/, "radio_prescription_method"],
    [/prescription upload|处方上传/, "file_prescription_upload"],
    [/od.*sph|right eye sphere|右眼球镜/, "dropdown_od_sph"],
    [/os.*sph|left eye sphere|左眼球镜/, "dropdown_os_sph"],
    [/od.*cyl|right eye cylinder|右眼散光/, "dropdown_od_cyl"],
    [/os.*cyl|left eye cylinder|左眼散光/, "dropdown_os_cyl"],
    [/od.*axis|right eye axis|右眼散光轴位/, "dropdown_od_axis"],
    [/os.*axis|left eye axis|左眼散光轴位/, "dropdown_os_axis"],
    [/pd mode|瞳距模式/, "radio_pd_mode"],
    [/total pd|总瞳距/, "dropdown_total_pd"],
    [/od pd|right eye pd|右眼单眼瞳距/, "number_od_pd"],
    [/os pd|left eye pd|左眼单眼瞳距/, "number_os_pd"],
  ];

  const STEPS = [
    { id: "type", en: "Prescription Type", zh: "处方类型", intro: "Choose the option that matches how you use your glasses.", introZh: "请选择符合您用镜需求的处方类型。" },
    { id: "method", en: "Prescription Method", zh: "处方提供方式", intro: "Tell us how you would like to provide your prescription.", introZh: "请选择提供处方的方式。" },
    { id: "details", en: "Your Prescription", zh: "填写处方", intro: "Enter values exactly as shown on your prescription.", introZh: "请按处方准确填写每项数据。" },
    { id: "function", en: "Lens Function", zh: "镜片功能", intro: "Choose the lens function for your everyday needs.", introZh: "请选择适合日常需求的镜片功能。" },
    { id: "solution", en: "Lens Index", zh: "镜片折射率", intro: "Compare thickness, price and features. Final thickness depends on your prescription and frame.", introZh: "适合您当前选择的镜片选项。" },
    { id: "review", en: "Review Your Selection", zh: "确认选择", intro: "Check your selections before adding them to the cart.", introZh: "加入购物车前请核对您的选择。" },
  ];
  const MOBILE_STEP_LABELS = Object.freeze({
    type: ["Type", "类型"],
    method: ["Method", "方式"],
    details: ["Prescription", "处方"],
    function: ["Lens", "功能"],
    solution: ["Index", "折射率"],
    review: ["Review", "确认"],
  });

  const SOLUTIONS = [
    { id: "1_61_clear", family: "clear", index: "1.61", en: "1.61 Clear", zh: "1.61普通透明", sph: "-6.00 to +6.00", cyl: "0.00 to -2.00", recommended: "0.00 to -4.00", price: "$18.00", badge: ["Best Value", "高性价比"] },
    { id: "1_61_blue_light", family: "blue_light_filtering", index: "1.61", en: "1.61 Blue Light", zh: "1.61防蓝光", sph: "-6.00 to +6.00", cyl: "0.00 to -2.00", recommended: "0.00 to -4.00", price: "$23.00", badge: ["Best Value", "高性价比"], value: ["Only $5 more than Clear", "比普通透明镜片仅贵$5"] },
    { id: "1_61_high_astigmatism_blue_light", family: "blue_light_filtering", index: "1.61", en: "1.61 High Astigmatism Blue Light", zh: "1.61防蓝光高散光镜片方案", sph: "0.00 to -6.00", cyl: "-2.25 to -4.00", recommended: "Prescription routed", price: "$23.00", badge: ["High Astigmatism", "高散光方案"], automatic: true },
    { id: "1_67_clear", family: "clear", index: "1.67", en: "1.67 Clear", zh: "1.67普通透明", sph: "-1.50 to -8.00", cyl: "0.00 to -2.00", recommended: "-4.00 to -7.00", price: "$30.00", badge: ["Recommended", "推荐"] },
    { id: "1_67_blue_light", family: "blue_light_filtering", index: "1.67", en: "1.67 Blue Light", zh: "1.67防蓝光", sph: "-2.00 to -8.00", cyl: "0.00 to -2.00", recommended: "-4.00 to -7.00", price: "$38.00", badge: ["Recommended", "推荐"], value: ["Only $8 more than Clear", "比普通透明镜片仅贵$8"] },
    { id: "1_74_clear", family: "clear", index: "1.74", en: "1.74 Clear", zh: "1.74普通超薄", sph: "-3.00 to -12.00", cyl: "0.00 to -2.00", recommended: "-7.00 to -12.00", price: "$49.95", badge: ["Ultra Thin", "超薄"] },
    { id: "1_74_blue_light", family: "blue_light_filtering", index: "1.74", en: "1.74 Blue Light", zh: "1.74防蓝光超薄", sph: "-3.00 to -12.00", cyl: "0.00 to -2.00", recommended: "-7.00 to -12.00", price: "$59.95", badge: ["Ultra Thin", "超薄"], value: ["Only $10 more than Clear", "比普通透明镜片仅贵$10"] },
    { id: "1_61_photochromic", family: "photochromic", index: "1.61", en: "1.61 Photochromic", zh: "1.61变色", sph: "0.00 to -6.00", cyl: "0.00 to -2.00", recommended: "0.00 to -4.00", price: "$34.95", badge: ["Best Value", "高性价比"] },
    { id: "1_67_photochromic", family: "photochromic", index: "1.67", en: "1.67 Photochromic", zh: "1.67变色", sph: "-1.50 to -8.00", cyl: "0.00 to -2.00", recommended: "-4.00 to -7.00", price: "$49.95", badge: ["Recommended", "推荐"] },
    { id: "1_61_tinted", family: "tinted_prescription", index: "1.61", en: "1.61 Tinted", zh: "1.61染色", sph: "0.00 to -6.00", cyl: "0.00 to -2.00", recommended: "0.00 to -4.00", price: "$24.95", badge: ["Best Value", "高性价比"] },
    { id: "1_67_tinted", family: "tinted_prescription", index: "1.67", en: "1.67 Tinted", zh: "1.67染色", sph: "-3.00 to -8.00", cyl: "0.00 to -2.00", recommended: "-4.00 to -7.00", price: "$39.95", badge: ["Recommended", "推荐"] },
  ];

  const CHOICE_META = [
    [/select prescription lenses|选择处方镜片/, ["Select Prescription Lenses", "选择处方镜片", "Choose lenses using your prescription.", "按处方选择适合您的镜片。"]],
    [/frame only|仅购买镜框/, ["Frame Only", "仅购买镜框", "Buy the frame with non-prescription demo lenses.", "仅购买含无度数定型展示片的镜框。"]],
    [/single vision|单光处方/, ["Single Vision", "单光处方", "For one clear viewing distance.", "适用于单一视距的处方。"]],
    [/^readers?$|简易阅读镜/, ["Readers", "简易阅读镜", "The same reading power for both eyes.", "双眼使用相同阅读度数。"]],
    [/prescription reading|定制阅读镜/, ["Prescription Reading", "定制阅读镜", "A full near prescription for each eye.", "按左右眼近用处方定制。"]],
    [/upload|上传处方/, ["Upload a Prescription", "上传处方", "Upload your prescription file and continue.", "上传清晰的处方文件后继续。"]],
    [/manual|手动填写/, ["Enter Prescription Manually", "手动填写处方", "Enter SPH, CYL, AXIS and PD now.", "现在填写 SPH、CYL、AXIS 与 PD。"]],
    [/send.*later|稍后提供/, ["Send Prescription Later", "稍后提供处方", "Choose lenses now; production remains on hold.", "现在暂选镜片，收到处方前暂停生产。"]],
    [/clear lenses|普通透明镜片/, ["Clear Lenses", "普通透明镜片", "Clear everyday vision without an added treatment.", "清晰日常视野，不增加额外功能。"]],
    [/blue light|防蓝光/, ["Blue Light Filtering", "防蓝光镜片", "Added comfort for screen-heavy days.", "为长时间使用屏幕提供更多舒适度。"]],
    [/photochromic|变色镜片/, ["Photochromic", "变色镜片", "Clear indoors and darker outdoors.", "室内透明，户外自动变深。"]],
    [/tinted|染色镜片/, ["Tinted Prescription", "有度数染色镜片", "Prescription lenses with an approved tint specification.", "采用已批准染色规格的处方镜片。"]],
  ];

  const GROUP_SELECTOR = ".gpo-element,[data-option-id],[data-id^='radio_'],[data-id^='dropdown_'],[data-id^='file_'],[data-id^='number_'],[data-id^='paragraph_']";
  const UPLOAD_ACCEPT = ".jpg,.jpeg,.png,.pdf,.heic,image/jpeg,image/png,application/pdf,image/heic";
  const UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
  const DUAL_PD_MIN = 25;
  const DUAL_PD_MAX = 41;
  const DUAL_PD_STEP = 0.5;
  const DUAL_PD_TOTAL_MIN = 54;
  const DUAL_PD_TOTAL_MAX = 78;
  const FREE_SHIPPING_THRESHOLD_CENTS = 7900;
  const STANDARD_SHIPPING_CENTS = 799;
  const PositiveRxCore = (() => {
/** Isolated proposal. No DOM, network, cart mutation, or production approval. */
const deepFreeze = value => {
  if (value && typeof value === 'object') { Object.values(value).forEach(deepFreeze); Object.freeze(value); }
  return value;
};
const clone = value => JSON.parse(JSON.stringify(value));
function number(value, label) {
  if ((typeof value !== 'string' && typeof value !== 'number') || !/^[+-]?\d+(?:\.\d+)?$/.test(String(value).trim())) throw new Error(`${label}: invalid number`);
  const result = Number(value);
  if (!Number.isFinite(result)) throw new Error(`${label}: invalid number`);
  return result;
}
function power(value, label, min, max) {
  const result = number(value, label);
  if (result < min || result > max || !Number.isInteger(result * 4)) throw new Error(`${label}: outside supported input grid`);
  return result === 0 ? 0 : result;
}
function canonicalEye(raw) {
  const sph = power(raw.sph, 'SPH', -12, 6);
  const cyl = power(raw.cyl, 'CYL', -4, 4);
  let axis = null;
  if (cyl !== 0) {
    axis = number(raw.axis, 'AXIS');
    if (!Number.isInteger(axis) || axis < 1 || axis > 180) throw new Error('AXIS: expected 1..180');
  }
  return Object.freeze(cyl > 0
    ? { sph: sph + cyl, cyl: -cyl, axis: ((axis + 89) % 180) + 1 }
    : { sph, cyl, axis });
}
/** Display-derived limits only. Supplier/catalogue verification is still required. */
const DISPLAY_RANGES = deepFreeze([
  ['1_61_clear','clear',-6,6,-2,0,-4,0],
  ['1_61_blue_light','blue_light_filtering',-6,6,-2,0,-4,0],
  ['1_61_high_astigmatism_blue_light','blue_light_filtering',-6,0,-4,-2.25,null,null],
  ['1_67_clear','clear',-8,-1.5,-2,0,-7,-4],
  ['1_67_blue_light','blue_light_filtering',-8,-2,-2,0,-7,-4],
  ['1_74_clear','clear',-12,-3,-2,0,-12,-7],
  ['1_74_blue_light','blue_light_filtering',-12,-3,-2,0,-12,-7],
  ['1_61_photochromic','photochromic',-6,0,-2,0,-4,0],
  ['1_67_photochromic','photochromic',-8,-1.5,-2,0,-7,-4],
  ['1_61_tinted','tinted_prescription',-6,0,-2,0,-4,0],
  ['1_67_tinted','tinted_prescription',-8,-3,-2,0,-7,-4]
].map(([id,family,sphMin,sphMax,cylMin,cylMax,recommendMin,recommendMax]) => ({id,family,sphMin,sphMax,cylMin,cylMax,recommendMin,recommendMax})));
function makeSnapshot(raw, revision) {
  if (!Number.isInteger(revision) || revision < 0) throw new Error('Invalid revision');
  const original = clone(raw); // Preserve original formatting and every supplied field.
  const canonical = { od: canonicalEye(original.od), os: canonicalEye(original.os) };
  const positive = Number(original.od.cyl) > 0 || Number(original.os.cyl) > 0;
  return deepFreeze({revision,original,canonical,positive,notation:'minus-cylinder'});
}
function evaluate(snapshot, family) {
  const eyes = Object.values(snapshot.canonical);
  const nativeRepresentable = eyes.every(e => e.sph >= -12 && e.sph <= 6 && e.cyl >= -4 && e.cyl <= 0);
  const candidates = nativeRepresentable ? DISPLAY_RANGES.filter(r => r.family === family && eyes.every(e => e.sph >= r.sphMin && (e.sph <= Math.max(0, r.sphMax) || (e.sph > 0 && e.sph + e.cyl < 0 && e.cyl >= -2)) && e.cyl >= r.cylMin && e.cyl <= r.cylMax)) : [];
  const governingSph = Math.min(...eyes.map(e => e.sph));
  const meridians = eyes.flatMap(eye => [eye.sph, eye.sph + eye.cyl]);
  const recommendable = eyes.every(eye => Math.abs(eye.cyl) <= 2) && meridians.every(power => power <= 0);
  const governingPower = Math.max(...meridians.map(Math.abs));
  const recommendedIndex = recommendable ? (governingPower <= 3 ? '1_61_' : governingPower <= 6 ? '1_67_' : '1_74_') : null;
  return deepFreeze({nativeRepresentable,candidateIds:candidates.map(r => r.id),recommendedIds:candidates.filter(r => recommendedIndex && r.id.startsWith(recommendedIndex) && !r.id.includes('high_astigmatism')).map(r => r.id),governingSph});
}
/** A preview may display candidates, but positive-CYL ordering is intentionally disabled. */
function handoffDecision(snapshot, {currentRevision, family, solutionId}) {
  if (snapshot.revision !== currentRevision) return {allowed:false,reason:'stale-prescription'};
  const evaluated = evaluate(snapshot,family);
  if (!evaluated.nativeRepresentable) return {allowed:false,reason:'canonical-outside-native-range'};
  if (!evaluated.candidateIds.includes(solutionId)) return {allowed:false,reason:'solution-outside-range'};
  if (snapshot.positive) return {allowed:false,reason:'positive-cylinder-order-handoff-unverified'};
  // This isolated proposal is not a complete existing-order validator either.
  return {allowed:false,reason:'integration-not-verified'};
}
/** For synthetic inspection only, never an authorization to submit. No stale async data. */
function proposedProperties(snapshot, currentRevision) {
  if (snapshot.revision !== currentRevision) throw new Error('stale-prescription');
  return Object.freeze({
    '_elookus_rx_original_v1': JSON.stringify(snapshot.original),
    '_elookus_rx_canonical_minus_v1': JSON.stringify(snapshot.canonical),
    '_elookus_rx_notation_v1': snapshot.notation,
    '_elookus_rx_revision_v1': String(snapshot.revision)
  });
}

return {makeSnapshot,evaluate,proposedProperties};
  })();

  const NATIVE_RX_KEYS = new Set(["od_sph", "os_sph", "od_cyl", "os_cyl", "od_axis", "os_axis"]);
  const state = { root: null, entry: null, overlay: null, panels: new Map(), fields: new Map(), portals: new Map(), portalFrame: 0, renderFrame: 0, autoFrame: 0, autoToken: 0, portalOpen: null, portalsSuspended: false, confirmOpen: false, groups: [], unknown: [], current: "type", open: false, confirmedRx: false, clearing: false, cartActions: [], autoTimer: null, conditionTimer: null, cartStatus: "idle", cartToken: 0, cartOpen: false, cartTrigger: null, drawerReturnToFlow: true, cartRequest: 0, cartSnapshot: null, cartConfirmation: null, nativeCartSuppression: false, nativeCartSuppressionTimer: 0, uploadObservers: new Map(), productPriceCache: new Map(), rootObserver: null, variantTimer: 0, variantRevealTimer: 0, portalTouch: null, portalTouchFrame: 0, portalTouchScrollTop: 0, suppressedTouchGroup: null, suppressedTouchUntil: 0 };

  const norm = (value) => String(value || "").replace(/\s+/g, " ").trim().toLowerCase();
  const escapeHtml = (value) => String(value || "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));
  const setHidden = (element, hidden) => { if (element && element.hidden !== hidden) element.hidden = hidden; };
  const HAS_HAN = /[\u3400-\u9fff\uf900-\ufaff]/;

  function englishDisplayText(value) {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (!HAS_HAN.test(text)) return text;
    const englishParts = text.split(/\s*[·•]\s*/).map((part) => part.trim()).filter((part) => part && !HAS_HAN.test(part));
    if (englishParts.length) return englishParts.join(" · ");
    return text.replace(/\s*(?:\/|·|•)?\s*[\u3400-\u9fff\uf900-\ufaff][\s\S]*$/, "").trim();
  }

  function commonAncestor(elements) {
    if (!elements.length) return null;
    let node = elements[0].parentElement;
    while (node && !elements.every((element) => node.contains(element))) node = node.parentElement;
    return node;
  }

  function collectGroups(root) {
    const set = new Set();
    root.querySelectorAll(GROUP_SELECTOR).forEach((node) => set.add(node.closest(".gpo-element") || node));
    const groups = Array.from(set).filter((group) => group !== root && root.contains(group));
    return groups.filter((group) => !groups.some((other) => other !== group && other.contains(group)));
  }

  function identitySignature(group) {
    const nodes = [group, ...group.querySelectorAll("input,select,textarea,[data-id],[data-option-id]")];
    return norm(nodes.map((node) => [node.id, node.getAttribute("data-id"), node.getAttribute("data-option-id"), node.getAttribute("name")].filter(Boolean).join(" ")).join(" "));
  }

  function labelSignature(group) {
    return norm(Array.from(group.querySelectorAll("legend,.gpo-label,.gpo-element__label,label")).slice(0, 8).map((label) => label.textContent).join(" "));
  }

  function identify(group) {
    const identity = identitySignature(group);
    const exact = Object.keys(FIELD_MAP).find((id) => identity.includes(id));
    if (exact) return { id: exact, ...FIELD_MAP[exact], fallback: false };
    const label = labelSignature(group);
    const fallback = FALLBACKS.find(([pattern]) => pattern.test(label));
    return fallback ? { id: fallback[1], ...FIELD_MAP[fallback[1]], fallback: true } : null;
  }

  function findRoot() {
    const candidates = Array.from(document.querySelectorAll(".gpo-app,#gpo-app,[data-gpo-app],.gpo-container,[class*='gpo-app']"));
    const byRoot = candidates.find((root) => collectGroups(root).some((group) => identify(group)?.key === "purchase"));
    if (byRoot) return byRoot;
    const controls = Array.from(document.querySelectorAll("input[name*='properties['],select[name*='properties['],textarea[name*='properties[']"));
    const purchase = controls.find((control) => /purchase mode|购买方式/i.test(control.name || control.closest(".gpo-element")?.textContent || ""));
    return purchase ? purchase.closest(".gpo-app,[data-gpo-app],.gpo-container,form") : null;
  }

  function bilingual(en, _zh, tag = "span") {
    return `<${tag} class="elv2-label"><span>${escapeHtml(en)}</span></${tag}>`;
  }

  // In-place quick help; the URL remains a progressive fallback to the full guide.
  function guideLink(view, label) {
    const root = window.Shopify?.routes?.root || "/";
    let guideRoutes = {};
      try { guideRoutes = JSON.parse(document.getElementById("elookus-guide-routes")?.textContent || "{}"); } catch (_) {}
      const guideUrl = guideRoutes[view] || `${root}?view=${view}`;
      return `<a href="${escapeHtml(guideUrl)}" data-elookus-guide="${view}" aria-haspopup="dialog" target="_blank" rel="noopener">${escapeHtml(label)}</a>`;
  }

  function stepGuide(step) {
    const guides = {
      type: ["elookus-lens-purpose", "Compare lens purposes"],
      method: ["elookus-prescription", "How to read your prescription"],
      details: ["elookus-prescription", "Understand SPH, CYL and AXIS"],
      function: ["elookus-lens-guide", "Lens features explained"],
      solution: ["elookus-lens-guide", "Understand lens index and thickness"],
    };
    const guide = guides[step];
    return guide ? `<p class="elv2-guide-help">${guideLink(guide[0], guide[1])}</p>` : "";
  }

  function createUi() {
    const entry = document.createElement("section");
    entry.className = "elv2-entry";
    entry.innerHTML = `
      <div class="elv2-entry__options">
        <button type="button" class="elv2-entry-button elv2-entry-button--primary" data-elv2-start-lenses>${bilingual("Select Prescription Lenses", "选择处方镜片")}</button>
        <button type="button" class="elv2-entry-button elv2-entry-button--secondary" data-elv2-frame-only>${bilingual("Frame Only", "仅购买镜框")}</button>
      </div>
      <div class="elv2-frame-confirm" hidden>
        <p><strong>Frame Only</strong></p>
        <p>The frame includes non-prescription demo lenses and no prescription options or lens charges.</p>
        <div><button type="button" class="elv2-button elv2-button--primary" data-elv2-frame-cart>${bilingual("Add Frame to Cart", "仅将镜框加入购物车")}</button><button type="button" class="elv2-link" data-elv2-choose-lenses>${bilingual("Choose prescription lenses", "改选处方镜片")}</button></div>
      </div>
    `;

    const overlay = document.createElement("section");
    overlay.className = "elv2-overlay";
    overlay.hidden = true;
    overlay.setAttribute("aria-label", "ELOOKUS prescription lens guide");
    overlay.innerHTML = `
      <div class="elv2-app">
        <header class="elv2-header">
          <a class="elv2-brand" href="#" data-elv2-close aria-label="Close and return to product">ELOOKUS</a>
          <div class="elv2-mobile-progress"><strong></strong><span></span></div>
          <div class="elv2-header__actions">
            <button type="button" class="elv2-cart-trigger" data-elv2-cart-open aria-label="Open cart" aria-haspopup="dialog">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 4h2l2.1 10.1a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.6L20 8H7M9.5 20a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm8 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z"/></svg>
              <span class="elv2-cart-count" data-elv2-cart-count aria-label="0 items">0</span>
            </button>
            <button type="button" class="elv2-close" data-elv2-close>${bilingual("Close", "返回商品")}</button>
          </div>
        </header>
        <div class="elv2-layout">
          <aside class="elv2-product"><div class="elv2-product__media"></div><div class="elv2-product__copy"><span>Your frame</span><strong></strong><small>Lens choices are added to this frame.</small></div></aside>
          <main class="elv2-main">
            <nav class="elv2-steps" aria-label="Prescription lens steps"></nav>
            <div class="elv2-panels"></div>
            <p class="elv2-error" role="alert" hidden></p>
            <footer class="elv2-footer"><button type="button" class="elv2-button elv2-button--secondary" data-elv2-back>${bilingual("Back", "返回")}</button><button type="button" class="elv2-button elv2-button--primary" data-elv2-next>${bilingual("Continue", "继续")}</button></footer>
          </main>
        </div>
      </div>
      <div class="elv2-confirm" hidden><div class="elv2-confirm__shade"></div><section class="elv2-confirm__dialog" role="dialog" aria-modal="true" aria-labelledby="elv2-confirm-title">${bilingual("Check your prescription", "核对您的处方", "h2")}<p>Confirm every value against your prescription, including CYL 0.00, before continuing.</p><div class="elv2-confirm__table"></div><div class="elv2-confirm__pd"></div><p class="elv2-review-note">Customer confirmation does not mean production approval. The prescription will still be reviewed before production.</p><div class="elv2-confirm__actions"><button type="button" class="elv2-button elv2-button--secondary" data-elv2-edit>${bilingual("Edit", "编辑")}</button><button type="button" class="elv2-button elv2-button--primary" data-elv2-confirm>${bilingual("Confirm", "确认")}</button></div></section></div>
      <div class="elv2-cart-layer" data-elv2-cart-layer hidden>
        <div class="elv2-cart-shade" data-elv2-cart-close aria-hidden="true"></div>
        <aside class="elv2-cart-drawer" role="dialog" aria-modal="true" aria-labelledby="elv2-cart-title" aria-describedby="elv2-cart-status" tabindex="-1">
          <header class="elv2-cart-drawer__header"><div><h2 id="elv2-cart-title">Your cart</h2></div><button type="button" class="elv2-cart-drawer__close" data-elv2-cart-close aria-label="Close cart">×</button></header>
          <div class="elv2-cart-status" id="elv2-cart-status" role="status" aria-live="polite" aria-atomic="true"></div>
          <div class="elv2-cart-items" data-elv2-cart-items></div>
          <footer class="elv2-cart-drawer__footer" data-elv2-cart-footer hidden>
            <div class="free-shipping-bar elv2-cart-shipping" data-elv2-cart-shipping data-free-shipping-progress="0" style="--free-shipping-progress: 0">
              <div class="free-shipping-bar__message free-shipping-bar__message--no" data-elv2-cart-shipping-not-met></div>
              <div class="free-shipping-bar__message free-shipping-bar__message--yes">✓ You've unlocked FREE SHIPPING</div>
              <div class="elookus-free-shipping-progress" role="progressbar" aria-label="Free shipping progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><span class="elookus-free-shipping-progress__fill" aria-hidden="true"></span></div>
            </div>
            <dl class="elookus-cart-summary elv2-cart-summary" data-elv2-cart-summary><div><dt>Total</dt><dd data-elv2-cart-subtotal></dd></div></dl>
            <p class="cart-policies" style="grid-column:1/-1;margin:0;font-size:12px;line-height:1.5;color:var(--elv2-muted)">Shipping and taxes confirmed at checkout.</p>
            <a class="elv2-button elv2-button--primary elv2-cart-checkout" href="/checkout">${bilingual("Checkout", "结账")}</a>
            <div class="elv2-cart-drawer__secondary-actions">
              <button type="button" class="elv2-button elv2-button--secondary" data-elv2-cart-close>${bilingual("Continue Shopping", "继续购物")}</button>
              <a class="elv2-cart-view-link" href="/cart">${bilingual("View Full Cart", "查看完整购物车")}</a>
            </div>
          </footer>
        </aside>
      </div>
    `;

    const panelHost = overlay.querySelector(".elv2-panels");
    STEPS.forEach((step) => {
      const panel = document.createElement("section");
      panel.className = "elv2-panel";
      panel.dataset.step = step.id;
      panel.innerHTML = `${step.id === "solution" ? lensBundleHeader() : step.id === "function" ? lensFunctionHeader() : `<header class="elv2-panel__header">${bilingual(step.en, step.zh, "h1")}<p>${step.intro}</p>${stepGuide(step.id)}</header>`}<div class="elv2-panel__body"></div>`;
      if (step.id === "details") panel.querySelector(".elv2-panel__body").innerHTML = `<div class="elv2-rx-entry"><button type="button" class="elv2-button elv2-button--secondary" data-elv2-upload>Upload prescription</button><button type="button" class="elv2-button elv2-button--secondary" data-elv2-manual hidden>Enter manually instead</button><p data-elv2-upload-status role="status" aria-live="polite"></p></div><div data-elv2-manual-fields>${prescriptionLayout()}</div><p data-elv2-positive-rx-note class="elv2-review-note" hidden></p>`;
      if (step.id === "solution") panel.querySelector(".elv2-panel__body").insertAdjacentHTML("afterbegin", `<p class="elv2-filter-note" hidden></p>`);
      if (step.id === "review") panel.querySelector(".elv2-panel__body").innerHTML = `<div class="elv2-summary elv2-review-summary"></div><p class="elv2-review-note">Final production feasibility is reviewed with your prescription and selected frame.</p><section class="elv2-cart-feedback" data-elv2-cart-feedback data-state="idle" role="status" aria-live="polite" aria-atomic="true" tabindex="-1" hidden><strong data-elv2-cart-title></strong><span data-elv2-cart-copy></span></section>`;
      panelHost.append(panel);
      state.panels.set(step.id, panel);
    });

    document.addEventListener("submit",event => { if (event.target.matches?.('form[action*="/cart/add"]')) { if (!blockPositiveRxOrder(event)) blockInvalidLensOrder(event); } },true);
    document.addEventListener("click",event => { const button=event.target.closest?.('[data-elv2-native-cart],button[type="submit"],input[type="submit"],[name="add"]'); if (button && (button.dataset.elv2NativeCart || button.closest('form[action*="/cart/add"]'))) { if (!blockPositiveRxOrder(event)) blockInvalidLensOrder(event); } },true);
    entry.querySelector("[data-elv2-frame-cart]").addEventListener("click", submitFrameOnly);
    entry.querySelector("[data-elv2-choose-lenses]").addEventListener("click", selectPrescriptionEntry);
    entry.querySelector("[data-elv2-start-lenses]").addEventListener("click", selectPrescriptionEntry);
    entry.querySelector("[data-elv2-frame-only]").addEventListener("click", selectFrameOnlyEntry);
    overlay.querySelectorAll("[data-elv2-close]").forEach((button) => button.addEventListener("click", (event) => { event.preventDefault(); closeOverlay(); }));
    overlay.querySelector("[data-elv2-back]").addEventListener("click", goBack);
    overlay.querySelector("[data-elv2-next]").addEventListener("click", goNext);
    overlay.querySelector("[data-elv2-upload]").addEventListener("click", openPrescriptionPicker);
    overlay.querySelector("[data-elv2-manual]").addEventListener("click", () => {
      if (uploadBusy()) return showError("Wait for the upload to finish before switching to manual entry.", fieldGroup("upload"));
      clearField("upload"); selectRxMethod("manual"); render();
    });
    overlay.querySelector("[data-elv2-dual-toggle]").addEventListener("change", handlePdToggle);
    overlay.querySelector("[data-elv2-edit]").addEventListener("click", closeConfirmation);
    overlay.querySelector("[data-elv2-confirm]").addEventListener("click", confirmPrescription);
    overlay.querySelector("[data-elv2-cart-open]").addEventListener("click", (event) => openCartDrawer(event.currentTarget));
    overlay.querySelectorAll("[data-elv2-cart-close]").forEach((button) => button.addEventListener("click", closeCartDrawer));
    overlay.querySelector("[data-elv2-cart-items]").addEventListener("click", handleCartDrawerAction);
    document.addEventListener("keydown", handleCartKeydown);
    document.addEventListener("dispatch:cart-drawer:open", handleNativeCartDrawerOpen, true);
    ["cart:updated", "cart:refresh", "theme:cart:updated"].forEach((name) => document.addEventListener(name, handleThemeCartUpdate));
    state.entry = entry;
    state.overlay = overlay;
    state.root.append(overlay);
    return entry;
  }

  function prescriptionLayout() {
    const eye = (id, en) => `<section class="elv2-eye"><header><strong>${en}</strong></header><div class="elv2-eye__fields"><div><span>SPH</span><div data-elv2-slot="${id}_sph"></div></div><div><span>CYL</span><div data-elv2-slot="${id}_cyl"></div></div><div><span>AXIS</span><div data-elv2-slot="${id}_axis"></div></div></div></section>`;
    return `<div class="elv2-rx-help"><p>SPH −: Nearsighted. SPH +: Farsighted. Copy the sign on your prescription.</p><p>PL / Plano = SPH 0.00. DS / SPH in the CYL box = CYL 0.00.</p></div><div class="elv2-rx"><div class="elv2-rx__desktop-head"><span>Eye</span><span>SPH</span><span>CYL</span><span>AXIS</span></div>${eye("od", "OD (Right)")}${eye("os", "OS (Left)")}</div><section class="elv2-pd"><div class="elv2-pd__heading">${bilingual("PD (Pupillary Distance)", "瞳距", "h2")}</div><div class="elv2-pd-mode-source" data-elv2-slot="pd_mode"></div><div class="elv2-pd__values"><div data-elv2-slot="total_pd"></div><div data-elv2-slot="od_pd"></div><div data-elv2-slot="os_pd"></div></div><div class="elv2-pd-toggle-row"><label><input class="elv2-pd-toggle" type="checkbox" data-elv2-dual-toggle><span>2 PD numbers</span></label><p class="elv2-guide-help">${guideLink("elookus-pd-guide", "PD help")}</p></div></section>`;
  }

  function createNav() {
    const nav = state.overlay.querySelector(".elv2-steps");
    nav.replaceChildren();
    STEPS.forEach((step, index) => {
      const item = document.createElement("div");
      item.dataset.step = step.id;
      item.innerHTML = `<span>${index + 1}</span>${bilingual(step.en, step.zh, "strong")}`;
      nav.append(item);
    });
  }

  function captureProduct() {
    const productTitle = document.querySelector(".product-title") || Array.from(document.querySelectorAll("h1")).find((heading) => !state.overlay?.contains(heading));
    const title = productTitle?.textContent?.trim() || document.title.split("–")[0].trim();
    const image = document.querySelector(".product-media img,.product__media img,[data-media-id] img,.product-gallery img");
    productTitle?.classList.add("elv2-brand-heading");
    document.querySelectorAll(".elookus-specs-section__heading h2,.elookus-lenses h2,.elookus-delivery__heading h2").forEach((heading) => heading.classList.add("elv2-brand-heading"));
    state.overlay.querySelector(".elv2-product__copy strong").textContent = title || "Selected frame";
    const host = state.overlay.querySelector(".elv2-product__media");
    if (image?.currentSrc || image?.src) host.innerHTML = `<img src="${escapeHtml(image.currentSrc || image.src)}" alt="${escapeHtml(image.alt || title)}">`;
    else state.overlay.querySelector(".elv2-product").classList.add("is-without-image");
  }


  // Only this unpublished draft may expose positive notation. No shared Globo changes.
  const rawRxSources = new Map(), rawRxMirrors = new Map();
  let rawRx = null, rawRxRevision = 0, rawRxWriting = false;
  function positiveRxDraftEnabled() { return String(window.Shopify?.theme?.id || "") === "188935733496"; }
  function clearRawRxMetadata() {
    document.querySelectorAll?.('[data-elv2-cart-meta]').forEach(input => { if (/^properties\[_elookus_rx_/.test(input.name || "")) input.remove(); });
  }
  function rawRxHasPositive() { return Boolean(rawRx && ["od_cyl", "os_cyl"].some(key => Number(rawRx[key]) > 0)); }
  function rawRxSnapshot() {
    if (!rawRx) return null;
    if (selectedMethod() !== "manual" || !["single_vision", "prescription_reading"].includes(selectedType())) throw new Error("Manual prescription route is not active.");
    const eye = id => ({ sph: rawRx[`${id}_sph`], cyl: rawRx[`${id}_cyl`], axis: rawRx[`${id}_axis`] });
    return PositiveRxCore.makeSnapshot({od:eye("od"),os:eye("os"),type:selectedType(),method:selectedMethod(),pd:{mode:selectedPdMode(),total:valueText("total_pd"),od:valueText("od_pd"),os:valueText("os_pd")}},rawRxRevision);
  }
  function manualLensRoute() { return selectedMethod() === "manual" && ["single_vision", "prescription_reading"].includes(selectedType()); }
  function lensHighAstigmatism() {
    return manualLensRoute() && ["od_cyl", "os_cyl"].some(key => Math.abs(Number(rawRx ? rawRx[key] : numericValue(key))) > 2);
  }
  function lensEligibilitySnapshot() {
    if (!manualLensRoute()) return null;
    if (rawRx) return rawRxSnapshot();
    const eye = id => ({ sph: numericValue(`${id}_sph`), cyl: numericValue(`${id}_cyl`), axis: numericValue(`${id}_axis`) });
    return PositiveRxCore.makeSnapshot({od:eye("od"),os:eye("os")},0);
  }
  function rawRxCandidate(control, family) {
    if (!control || !family || control.disabled || !control.isConnected) return false;
    const field = Array.from(state.fields.values()).find(field => field.family === family && field.group?.contains(control));
    if (!field?.group?.isConnected || isAppHidden(field.group)) return false;
    const choice = choiceContainer(control);
    // Our unavailable class is reversible; never overwrite provider hiding.
    if (choice && (choice.hidden || choice.style?.display === "none" || choice.getAttribute?.("aria-hidden") === "true" || /(^|\s)(gpo-hidden|is-hidden|hidden|hide)(\s|$)/i.test(choice.className || ""))) return false;
    const solution = solutionFor(control,family);
    if (!solution || solution.automatic) return false;
    if (!manualLensRoute()) return true;
    if (lensHighAstigmatism()) return false;
    try { return PositiveRxCore.evaluate(lensEligibilitySnapshot(),family).candidateIds.includes(solution.id); }
    catch { return false; }
  }
  function blockInvalidLensOrder(event) {
    if (!manualLensRoute() || /frame only|仅购买镜框/i.test(controlText(checkedControl("purchase")))) return false;
    const key=selectedSolutionKey();
    if (rawRxCandidate(checkedControl(key),state.fields.get(key)?.family)) return false;
    event?.preventDefault(); event?.stopImmediatePropagation();
    showError("This lens is not available for your prescription. Please choose another lens.");
    return true;
  }
  function lensEligibilityStamp() {
    try { return JSON.stringify({type:selectedType(),method:selectedMethod(),rx:lensEligibilitySnapshot()}); }
    catch { return "invalid"; }
  }
  function rawRxValidation() {
    if (!rawRx) return "";
    try {
      const snapshot = rawRxSnapshot();
      if (!PositiveRxCore.evaluate(snapshot,"clear").nativeRepresentable) return "The equivalent minus-cylinder prescription is outside the supported range. Please upload your prescription for review.";
      return "";
    } catch { return "Check both eyes: SPH −12.00 to +6.00, CYL −4.00 to +4.00 in 0.25 steps, and AXIS 1–180 for nonzero CYL. Positive CYL is limited to the equivalent supported minus-cylinder range."; }
  }
  function writeRawRxCanonical() {
    if (!rawRx || rawRxWriting || rawRxValidation()) return false;
    const snapshot = rawRxSnapshot(), assignments = [];
    for (const eye of ["od","os"]) for (const part of ["sph","cyl","axis"]) {
      const key = `${eye}_${part}`, desired = snapshot.canonical[eye][part];
      const sources = rawRxSources.get(key);
      if (!sources?.length || sources.some(control => !control.isConnected)) return false;
      const select = sources.find(control => control.tagName === "SELECT");
      const choices = sources.filter(control => ["radio","checkbox"].includes(control.type));
      const matches = value => desired === null ? String(value).trim() === "" : String(value).trim() !== "" && Number(value) === desired;
      if (select) {
        if (select.disabled) return false;
        const option = Array.from(select.options).find(option => !option.disabled && matches(option.value));
        if (!option && desired !== null) return false;
        assignments.push([select,option?.value || "",false]);
      } else if (choices.length) {
        const match = choices.find(control => !control.disabled && matches(control.getAttribute("original-value") || control.value));
        if (!match && desired !== null) return false;
        choices.forEach(control => assignments.push([control,control === match,true]));
      } else {
        const input = sources.find(control => !control.disabled);
        if (!input) return false;
        assignments.push([input,desired === null ? "" : String(desired),false]);
      }
    }
    rawRxWriting = true;
    try {
      // Assign every value before emitting events, so observers never see a half-transposed eye.
      const changed = assignments.filter(([control,value,choice]) => (choice ? control.checked : control.value) !== value);
      changed.forEach(([control,value,choice]) => { if (choice) control.checked=value; else control.value=value; });
      changed.forEach(([control]) => { control.dispatchEvent(new Event("input",{bubbles:true})); control.dispatchEvent(new Event("change",{bubbles:true})); });
    } finally { rawRxWriting = false; }
    return snapshot.revision===rawRxRevision && assignments.every(([control,value,choice]) => control.isConnected && (choice ? control.checked : control.value)===value);
  }
  function clearRawZeroCylinderAxis(eye) {
    if (!rawRx || rawRxWriting || state.clearing) return;
    const cyl = String(rawRx[`${eye}_cyl`] ?? "").trim();
    if (!cyl || Number(cyl) !== 0) return;
    const key = `${eye}_axis`, mirror = rawRxMirrors.get(key);
    const rawChanged = Boolean(rawRx[key]);
    if (rawChanged) rawRx[key] = "";
    const sources = rawRxSources.get(key) || [];
    const changed = sources.filter(control => ["radio", "checkbox"].includes(control.type) ? control.checked : String(control.value || "") !== "");
    const mirrorChanged = Boolean(mirror?.value);
    if (rawChanged || mirrorChanged || changed.length) { rawRxRevision += 1; state.confirmedRx = false; clearRawRxMetadata(); }
    // A zero-CYL axis must clear even while the other eye is incomplete and a full
    // canonical transaction is blocked. Never restore a prior nonzero axis on re-entry.
    rawRxWriting = true;
    try {
      changed.forEach(control => { if (["radio", "checkbox"].includes(control.type)) control.checked = false; else control.value = ""; });
      if (mirror) mirror.value = "";
      changed.forEach(control => {
        control.dispatchEvent(new Event("input", { bubbles: true }));
        control.dispatchEvent(new Event("change", { bubbles: true }));
      });
      if (mirrorChanged) mirror.dispatchEvent?.(new Event("change", { bubbles: true }));
    } finally { rawRxWriting = false; }
  }

  function acceptRawRxEdit(key,value) {
    if (!positiveRxDraftEnabled() || rawRxWriting || state.clearing) return false;
    if (!rawRx && !(key.endsWith("_cyl") && (Number(value)>0 || Number(value)<-2))) return false;
    if (!rawRx) rawRx = Object.fromEntries(Array.from(NATIVE_RX_KEYS).map(key => [key,rawRxMirrors.get(key)?.value || ""]));
    rawRx[key] = value;
    // Zero cylinder has no axis in either the original or equivalent prescription.
    let clearedAxis = false;
    for (const eye of ["od", "os"]) {
      const cyl = String(rawRx[`${eye}_cyl`] ?? "").trim();
      if (cyl && Number(cyl) === 0) { clearedAxis ||= Boolean(rawRx[`${eye}_axis`]); rawRx[`${eye}_axis`] = ""; }
    }
    rawRxRevision += 1; state.confirmedRx = false;
    if (clearedAxis) clearRawRxMetadata();
    writeRawRxCanonical();
    for (const eye of ["od", "os"]) clearRawZeroCylinderAxis(eye);
    return true;
  }
  function rawRxNeedsHighAstigmatism() { return Boolean(rawRx && ["od_cyl","os_cyl"].some(key => Math.abs(Number(rawRx[key]))>2)); }
  function rawRxEquivalentText() {
    if (!rawRx) return "";
    try {
      const snapshot=rawRxSnapshot();
      return "Original prescription shown above. Equivalent minus-cylinder: " + ["od","os"].map(eye => {
        const v=snapshot.canonical[eye]; return `${eye.toUpperCase()} SPH ${v.sph.toFixed(2)}, CYL ${v.cyl.toFixed(2)}, AXIS ${v.axis ?? "None"}`;
      }).join(" · ") + (rawRxNeedsHighAstigmatism() ? ". High-astigmatism lenses are required when either eye has CYL greater than +2.00 or less than −2.00. No verified high-astigmatism option is configured. Upload your prescription for review" : "");
    } catch { return "Complete the original prescription to see its equivalent minus-cylinder values."; }
  }
  function refreshRawRxOptions() {
    state.fields.forEach((field,key) => {
      if (!key.startsWith("solution_") || !field.family) return;
      controls(key).filter(control => control.type === "radio").forEach(control => {
        const choice=choiceContainer(control); if (!choice) return;
        const eligible=rawRxCandidate(control,field.family);
        choice.classList.toggle("elv2-rx-unavailable",!eligible);
        choice.setAttribute("aria-disabled",String(!eligible));
        choice.setAttribute("tabindex",eligible ? "0" : "-1");
        choice.title=eligible ? "" : "Not available for this prescription. Please choose another lens.";
        if (!eligible && control.checked) { state.confirmedRx=false; control.checked=false; control.dispatchEvent(new Event("change",{bubbles:true})); }
        const solution=solutionFor(control,field.family), badge=choice.querySelector(".elv2-badge");
        if (solution && badge) { badge.textContent=solutionBadge(solution)[0]; badge.dataset.recommended=String(badge.textContent === "Recommended"); }
      });
    });
    const note=state.overlay?.querySelector("[data-elv2-positive-rx-note]");
    if (note) { note.hidden=!rawRx; note.textContent=rawRxEquivalentText(); }
  }
  function positiveRxOrderBlocked() { return Boolean(rawRx && selectedMethod()==="manual" && rawRxNeedsHighAstigmatism()); }
  function blockPositiveRxOrder(event) {
    if (!positiveRxOrderBlocked()) return false;
    event?.preventDefault(); event?.stopImmediatePropagation();
    showError(rawRxNeedsHighAstigmatism() ? "High-astigmatism lenses are required when either CYL is above 2.00 in magnitude. No verified high-astigmatism option is configured, so this prescription cannot be added to the cart. Upload your prescription for review." : "Positive-CYL ordering is not yet enabled. Your original prescription has been kept. This draft can preview equivalent values and lens options, but cannot add this prescription to the cart.");
    return true;
  }
  function rawRxRequestStamp() {
    return JSON.stringify({revision:rawRxRevision,native:rawRx ? Array.from(rawRxSources).map(([key,list])=>[key,list.map(c=>[c.value,c.checked,c.disabled,c.isConnected])]) : null,positive:rawRxHasPositive(),type:selectedType(),method:selectedMethod(),solution:selectedSolutionKey(),selection:checkedControl(selectedSolutionKey())?.value,variant:cartRequestContext()?.form?.querySelector('[name="id"]')?.value});
  }

  function fieldGroup(key) { return state.fields.get(key)?.group || null; }
  function controls(key) { return Array.from(fieldGroup(key)?.querySelectorAll("input:not([type='hidden']),select,textarea") || []); }
  function checkedControl(key) {
    const fieldControls = controls(key);
    const mirrorSelect = fieldControls.find((control) => control.matches("select[data-elv2-control-mirror]"));
    if (mirrorSelect) return mirrorSelect.value ? mirrorSelect : null;
    return fieldControls.find((control) => (control.type === "radio" || control.type === "checkbox") ? control.checked : String(control.value || "").trim() !== "");
  }
  function controlText(control) {
    if (!control) return "";
    if (control.tagName === "SELECT") return control.selectedOptions?.[0]?.textContent?.trim() || control.value;
    const enrichedChoice = control.closest("[data-elv2-choice-value]");
    if (enrichedChoice) return enrichedChoice.dataset.elv2ChoiceValue;
    const label = control.id ? document.querySelector(`label[for="${CSS.escape(control.id)}"]`) : control.closest("label");
    return (label?.textContent || control.value || "").replace(/\s+/g, " ").trim();
  }
  function valueText(key) {
    const control = checkedControl(key);
    if (!control) return "";
    const field = state.fields.get(key);
    if (key.startsWith("solution_") && field?.family) {
      const solution = solutionFor(control, field.family);
      if (solution) return `${solution.en} · ${solution.price} USD`;
    }
    return englishDisplayText(controlText(control));
  }

  function uploadBusy() {
    const group = fieldGroup("upload");
    return Boolean(group && (group.dataset.elv2UploadState === "pending" || group.querySelector(".gpo-uploading") || group.querySelector("[data-gpo-image-editor-loading]")));
  }

  function selectRxMethod(method) {
    const matcher = method === "manual" ? /manual|enter|手动/i : /upload|上传/i;
    const control = controls("method").find((item) => matcher.test(controlText(item)));
    if (!control || control.disabled) return false;
    if (!control.checked) { control.checked = true; control.dispatchEvent(new Event("change", { bubbles: true })); }
    return true;
  }

  function openPrescriptionPicker() {
    if (uploadBusy()) return showError("Wait for the upload to finish before choosing another file.", fieldGroup("upload"));
    const input = controls("upload").find((control) => control.type === "file");
    if (!input) return showError("The upload control is not ready. Please try again.", fieldGroup("upload"));
    // Keep the page and manual data unchanged while the OS picker is open.
    // Native click stays in the user's synchronous activation stack (including iOS).
    state.rxPickerRaw = rawRx ? {...rawRx} : null;
    state.rxPickerMethod = selectedMethod();
    state.rxPickerDraft = state.rxPickerMethod === "manual" ? ["od_sph","os_sph","od_cyl","os_cyl","pd_mode","od_axis","os_axis","total_pd","od_pd","os_pd"].map((key) => {
      const control = checkedControl(key);
      return control ? { key, control, value: control.value } : null;
    }).filter(Boolean) : null;
    state.rxPicking = true;
    state.rxPickerDisabled = input.disabled;
    input.disabled = false;
    input.click();
  }

  function freezeManualEntry() {
    if (state.rxPendingSnapshot || state.rxPickerMethod !== "manual") return;
    const host = state.overlay.querySelector("[data-elv2-manual-fields]");
    const snapshot = host.cloneNode(true);
    snapshot.removeAttribute("data-elv2-manual-fields");
    snapshot.classList.add("elv2-pending-prescription");
    snapshot.setAttribute("aria-label", "Prescription values while upload is in progress");
    snapshot.setAttribute("aria-busy", "true");
    snapshot.querySelectorAll("[data-elv2-slot]").forEach((slot) => {
      const key = slot.dataset.elv2Slot;
      if (key === "pd_mode") return;
      const source = fieldGroup(key);
      const select = source?.querySelector("select[data-elv2-control-mirror]");
      if (select && !slot.classList.contains("elv2-axis-slot-none")) {
        if (key.endsWith("pd")) {
          const label = document.createElement("span");
          label.className = "elv2-pending-pd-label";
          label.textContent = source.querySelector(".gpo-label")?.textContent?.trim() || select.getAttribute("aria-label");
          slot.replaceChildren(label);
        } else slot.replaceChildren();
        const mirror = select.cloneNode(true);
        mirror.value = select.value;
        const style = window.getComputedStyle(select);
        ["height","minHeight","fontSize","lineHeight","padding","border","borderRadius","color","backgroundColor"].forEach((key) => { mirror.style[key] = style[key]; });
        mirror.style.width = "100%";
        mirror.style.opacity = "1";
        slot.append(mirror);
      }
      if (key === "total_pd") slot.classList.add("elv2-pending-total");
    });
    snapshot.querySelectorAll("*").forEach((node) => {
      node.removeAttribute("id"); node.removeAttribute("name"); node.removeAttribute("for");
      Array.from(node.attributes).filter((attr) => attr.name.startsWith("data-elv2-")).forEach((attr) => node.removeAttribute(attr.name));
      if (node.matches("input,select,textarea,button")) { node.disabled = true; node.tabIndex = -1; }
      if (node.matches("a")) { node.removeAttribute("href"); node.tabIndex = -1; }
    });
    snapshot.hidden = false;
    host.after(snapshot);
    state.rxPendingSnapshot = snapshot;
  }

  function releaseManualSnapshot() {
    state.rxPendingSnapshot?.remove();
    state.rxPendingSnapshot = null;
  }

  function recoverManualUploadFailure(message) {
    const restore = Boolean(state.rxPickerDraft);
    state.rxPicking = false;
    state.rxPickerMethod = null;
    if (restore) {
      clearField("upload");
      restorePickerManualDraft();
    }
    releaseManualSnapshot();
    state.rxUploadMessage = message;
    render();
    showError(message);
  }

  function restorePickerManualDraft() {
    const draft = state.rxPickerDraft;
    state.rxPickerDraft = null;
    selectRxMethod("manual");
    if (state.rxPickerRaw) { rawRx={...state.rxPickerRaw}; rawRxRevision+=1; }
    state.rxPickerRaw=null;
    if (draft) draft.forEach(({ control, value }) => {
      if (!control.isConnected) return;
      if (["radio", "checkbox"].includes(control.type)) control.checked = true;
      else control.value = value;
      control.dispatchEvent(new Event("input", { bubbles: true }));
      control.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  function renderRxEntry() {
    const upload = selectedMethod() === "upload";
    const group = fieldGroup("upload");
    const selection = group && uploadSelections.get(group);
    const status = group?.dataset.elv2UploadState || "empty";
    const waitingFromManual = Boolean(state.rxPendingSnapshot);
    setHidden(state.overlay.querySelector("[data-elv2-manual-fields]"), upload);
    setHidden(state.overlay.querySelector("[data-elv2-manual]"), !upload || waitingFromManual);
    const button = state.overlay.querySelector("[data-elv2-upload]");
    const buttonText = upload && !waitingFromManual ? "Replace prescription" : "Upload prescription";
    if (button.textContent !== buttonText) button.textContent = buttonText;
    button.disabled = uploadBusy();
    const message = state.overlay.querySelector("[data-elv2-upload-status]");
    const statusText = status === "pending" ? `${selection?.name || "File"} · Uploading…`
      : status === "complete" ? `${selection?.name || "File"} · Uploaded. Ready to continue.`
      : ["invalid", "unconfirmed"].includes(status) ? "Upload not confirmed. Choose a valid file or enter your prescription manually."
      : state.rxUploadMessage || "";
    if (message.textContent !== statusText) message.textContent = statusText;
  }

  function uploadFileError(input) {
    const file = input?.files?.[0];
    if (!file) return "Choose a JPG, PNG, PDF or HEIC prescription file.";
    const extension = (file.name.match(/\.([a-z0-9]+)$/i)?.[1] || "").toLowerCase();
    const allowedExtension = ["jpg", "jpeg", "png", "pdf", "heic"].includes(extension);
    const allowedType = !file.type || ["image/jpeg", "image/png", "application/pdf", "image/heic"].includes(file.type.toLowerCase());
    if (!allowedExtension || !allowedType) return "Use JPG, PNG, PDF or HEIC only. WEBP is not enabled.";
    if (file.size > UPLOAD_MAX_BYTES) return "The prescription file must be 10 MB or smaller.";
    return "";
  }

  // Globo clears input.files after upload. Track the validated selection separately.
  const uploadSelections = new WeakMap();

  function uploadReference(input) {
    const group = input?.closest("[data-elv2-field='upload']");
    const selection = group && uploadSelections.get(group);
    if (!selection || !selection.settled || group.querySelector(".gpo-uploading") || input.dataset.hasFile !== "true") return "";
    const cards = Array.from(group.querySelectorAll(".gpo-f-card"));
    if (cards.length !== 1 || !group.classList.contains("upload-success")) return "";
    const reference = cards[0].querySelector("a.gpo-f-link[href]")?.getAttribute("href") || "";
    if (!/^https:\/\/.+\.(?:jpe?g|png|webp|pdf)(?:[?#]|$)/i.test(reference)) return "";
    // A preview alone is insufficient: this exact URL must be in this item's form.
    const form = cartRequestContext()?.form;
    const propertyName = `properties[${input.getAttribute("data-field-name")}]`;
    const stored = form && Array.from(form.querySelectorAll('input[type="hidden"]')).find((element) => element.name === propertyName);
    return stored?.value === reference && (!selection.reference || selection.reference === reference) ? reference : "";
  }

  function completeUploadIfReady(input) {
    const group = input?.closest("[data-elv2-field='upload']");
    const reference = uploadReference(input);
    if (!group || !reference) return false;
    const selection = uploadSelections.get(group);
    const newlyComplete = group.dataset.elv2UploadState !== "complete";
    selection.reference = reference;
    group.dataset.elv2UploadState = "complete";
    group.dataset.elv2UploadReference = "confirmed";
    group.classList.remove("elv2-invalid");
    if (newlyComplete && state.current === "method" && selectedMethod() === "upload") {
      const error = state.overlay.querySelector(".elv2-error");
      error.hidden = true;
      window.setTimeout(() => {
        if (uploadSelections.get(group) === selection && state.current === "method" && selectedMethod() === "upload" && validateStep()) advance();
      }, 0);
    }
    if (newlyComplete && state.current === "details") state.overlay.querySelector(".elv2-error").hidden = true;
    state.rxPickerDraft = null;
    state.rxPickerRaw = null;
    state.rxPickerMethod = null;
    state.rxUploadMessage = "";
    releaseManualSnapshot();
    renderRxEntry();
    return true;
  }

  function configureUploadField(group) {
    const input = group.querySelector('input[type="file"]');
    if (!input || input.dataset.elv2UploadConfigured === "true") return;
    input.dataset.elv2UploadConfigured = "true";
    input.accept = UPLOAD_ACCEPT;
    input.addEventListener("cancel", () => {
      state.rxPicking = false;
      input.disabled = Boolean(state.rxPickerDisabled);
      // Opening the picker no longer changes method or any prescription value.
      state.rxPickerDraft = null;
      state.rxPickerRaw = null;
      state.rxPickerMethod = null;
      render();
    });
    const select = (event) => {
      if (state.clearing) { uploadSelections.delete(group); return; }
      if (group.dataset.elv2UploadState === "pending" || group.querySelector(".gpo-uploading") || input.hasAttribute("data-gpo-image-editor-loading")) {
        event.stopImmediatePropagation();
        input.value = "";
        showError("Wait for this upload to finish before choosing another file.", group);
        return;
      }
      if (!input.files?.length) {
        state.rxPicking = false;
        input.disabled = Boolean(state.rxPickerDisabled);
        state.rxPickerDraft = null;
        state.rxPickerRaw = null;
        state.rxPickerMethod = null;
        return; // Browsers may report cancellation as an empty change instead.
      }
      const message = uploadFileError(input);
      if (message) {
        event.stopImmediatePropagation();
        input.value = "";
        uploadSelections.delete(group);
        group.dataset.elv2UploadState = "invalid";
        state.rxPicking = false;
        input.disabled = Boolean(state.rxPickerDisabled);
        if (state.rxPickerMethod === "manual") restorePickerManualDraft();
        state.rxPickerMethod = null;
        showError(message, group);
        return;
      }
      const selectedFiles = input.files;
      const selectedFileName = selectedFiles[0].name;
      const selectedFile = selectedFiles[0];
      freezeManualEntry();
      state.rxPicking = false;
      state.rxUploadMessage = "";
      if (!selectRxMethod("upload")) {
        event.stopImmediatePropagation(); input.value = "";
        recoverManualUploadFailure("The upload method is not ready. Please try again.");
        return;
      }
      // Globo's synchronous condition update may clear its input. Restore the
      // SAME native FileList before its original change handler reads it.
      // No new uploader, synthetic FileList, async picker or alternate storage.
      try { if (input.files?.[0] !== selectedFile) input.files = selectedFiles; }
      catch (_) {
        event.stopImmediatePropagation(); input.value = "";
        recoverManualUploadFailure("Your browser could not attach that file. Please try again or enter your prescription manually.");
        return;
      }
      if (input.files?.length !== 1 || input.files[0] !== selectedFile) {
        event.stopImmediatePropagation(); input.value = "";
        recoverManualUploadFailure("The selected file could not be retained. Please try again or enter your prescription manually.");
        return;
      }
      // Globo otherwise appends the prior file even for a single-file input.
      // Use its own removal handler so the hidden property is cleared too.
      group.querySelectorAll(".gpo-f-rm").forEach((button) => button.dispatchEvent(new Event("gpoFakeRemove")));
      // A provider removal hook may also clear the native input on replacement.
      // Recheck after cleanup, before allowing the original upload handler to run.
      try { if (input.files?.[0] !== selectedFile) input.files = selectedFiles; } catch (_) { /* fail closed below */ }
      if (input.files?.length !== 1 || input.files[0] !== selectedFile) {
        event.stopImmediatePropagation(); input.value = "";
        uploadSelections.delete(group);
        group.dataset.elv2UploadState = "unconfirmed";
        recoverManualUploadFailure("The replacement file could not be retained. Please choose it again or enter your prescription manually.");
        return;
      }
      uploadSelections.set(group, { settled: false, reference: "", name: selectedFileName });
      group.dataset.elv2UploadState = "pending";
      delete group.dataset.elv2UploadReference;
      render();
    };
    input.addEventListener("change", select, true);
    input.addEventListener("drop", select, true);
    input.addEventListener("gpoFileSettled", () => {
      const selection = uploadSelections.get(group);
      if (!selection) return;
      selection.settled = true;
      if (!completeUploadIfReady(input)) {
        group.dataset.elv2UploadState = "unconfirmed";
        delete group.dataset.elv2UploadReference;
        // Let the provider's synchronous hidden-property update finish, then
        // restore the saved form on failure. Identity guard rejects stale events.
        window.setTimeout(() => {
          if (uploadSelections.get(group) !== selection || group.querySelector(".gpo-uploading")) return;
          if (completeUploadIfReady(input)) { render(); return; }
          recoverManualUploadFailure(state.rxPickerDraft ? "Upload not confirmed. Your manual prescription has been kept. Please choose another file or continue entering it manually." : "Upload not confirmed. Choose another file or enter your prescription manually.");
        }, 0);
      } else render();
      renderRxEntry();
    });
  }

  function normalizedDualPdValue(value) {
    const number = Number.parseFloat(String(value || "").trim());
    if (!Number.isFinite(number) || number < DUAL_PD_MIN || number > DUAL_PD_MAX || !Number.isInteger(number / DUAL_PD_STEP)) return "";
    return number.toFixed(1);
  }

  function configureDualPdField(group) {
    const input = group.querySelector('input:not([type="hidden"])');
    if (!input || group.querySelector("select[data-elv2-dual-pd-select]")) return;
    input.min = String(DUAL_PD_MIN);
    input.max = String(DUAL_PD_MAX);
    input.step = String(DUAL_PD_STEP);
    input.hidden = true;
    input.tabIndex = -1;
    input.setAttribute("aria-hidden", "true");

    const select = document.createElement("select");
    select.dataset.elv2ControlMirror = "true";
    select.dataset.elv2PdSelect = "true";
    select.dataset.elv2DualPdSelect = "true";
    select.className = "elv2-dual-pd-select";
    select.setAttribute("aria-label", englishDisplayText(group.dataset.elv2Label) || "Single-eye PD");
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Select";
    select.append(placeholder);
    for (let halves = DUAL_PD_MIN * 2; halves <= DUAL_PD_MAX * 2; halves += 1) {
      const value = (halves / 2).toFixed(1);
      const option = document.createElement("option");
      option.value = value;
      option.textContent = value;
      select.append(option);
    }

    const syncFromInput = () => { select.value = normalizedDualPdValue(input.value); syncMobilePdPickers(); };
    syncFromInput();
    select.addEventListener("change", () => {
      input.value = select.value;
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    input.addEventListener("input", syncFromInput);
    input.addEventListener("change", syncFromInput);
    input.before(select);
    attachMobilePdPicker(select, group, group.dataset.elv2Field);
  }

  function normalizedTotalPdValue(value) {
    const match = String(value || "").match(/[+-]?\d+(?:\.\d+)?/);
    const number = match ? Number(match[0]) : NaN;
    if (!Number.isFinite(number) || number < DUAL_PD_TOTAL_MIN || number > DUAL_PD_TOTAL_MAX || !Number.isInteger(number / DUAL_PD_STEP)) return "";
    return number.toFixed(1);
  }

  function configureTotalPdField(group) {
    if (group.querySelector("select[data-elv2-total-pd-select]")) return;
    const sourceControls = Array.from(group.querySelectorAll("input:not([type='hidden']),select,textarea"));
    if (!sourceControls.length) return;
    group.classList.add("elv2-native-total-pd");

    const select = document.createElement("select");
    select.dataset.elv2ControlMirror = "true";
    select.dataset.elv2PdSelect = "true";
    select.dataset.elv2TotalPdSelect = "true";
    select.className = "elv2-total-pd-select";
    select.setAttribute("aria-label", englishDisplayText(group.dataset.elv2Label) || "Total PD");
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Select";
    select.append(placeholder);
    for (let halves = DUAL_PD_TOTAL_MIN * 2; halves <= DUAL_PD_TOTAL_MAX * 2; halves += 1) {
      const value = (halves / 2).toFixed(1);
      const option = document.createElement("option");
      option.value = value;
      option.textContent = value;
      select.append(option);
    }

    const sourceValue = (control) => normalizedTotalPdValue(control.getAttribute("original-value") || control.value || controlText(control));
    const syncFromSource = () => {
      const selected = sourceControls.find((control) => (control.type === "radio" || control.type === "checkbox") ? control.checked : String(control.value || "").trim() !== "");
      select.value = selected ? sourceValue(selected) : "";
      syncMobilePdPickers();
    };
    sourceControls.forEach((control) => {
      control.hidden = true;
      control.tabIndex = -1;
      control.setAttribute("aria-hidden", "true");
      control.addEventListener("input", syncFromSource);
      control.addEventListener("change", syncFromSource);
    });
    syncFromSource();
    select.addEventListener("change", () => {
      const value = select.value;
      sourceControls.forEach((control) => {
        const choice = control.type === "radio" || control.type === "checkbox";
        const selected = choice ? sourceValue(control) === value : false;
        const changed = choice ? control.checked !== selected : control.value !== value;
        if (!changed) return;
        if (choice) control.checked = selected;
        else control.value = value;
        control.dispatchEvent(new Event("input", { bubbles: true }));
        control.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
    const fieldLabel = group.querySelector(":scope > .gpo-label,:scope > label.gpo-label,:scope > .gpo-element__label,:scope > legend");
    if (fieldLabel) fieldLabel.after(select);
    else group.prepend(select);
    attachMobilePdPicker(select, group, "total_pd");
  }

  // Mobile picker stages a choice locally; completion uses the existing mirror change path.
  let mobileRxPicker = null;
  const mobilePdPickers = new Map();

  function mobilePdPickerAllowed(key) {
    return state.open && state.current === "details" && selectedMethod() === "manual"
      && ["single_vision", "prescription_reading"].includes(selectedType())
      && (key === "total_pd" ? selectedPdMode() === "total" : ["od_pd", "os_pd"].includes(key) && selectedPdMode() === "dual");
  }

  function syncMobilePdPickers() {
    mobilePdPickers.forEach(({ select, trigger, group }, key) => {
      const enabled = mobilePdPickerAllowed(key);
      group.classList.toggle("elv2-mobile-pd-enabled", enabled);
      trigger.disabled = !enabled || select.disabled;
      trigger.textContent = select.selectedOptions[0]?.textContent || "Select";
      const label = key === "total_pd" ? "Total PD" : key === "od_pd" ? "PD OD (Right)" : "PD OS (Left)";
      trigger.setAttribute("aria-label", `${label}: ${trigger.textContent}`);
    });
    if (!mobileRxPickerIsValid()) closeMobileRxPicker();
  }

  function attachMobilePdPicker(select, group, key) {
    if (!["total_pd", "od_pd", "os_pd"].includes(key) || typeof document.createElement("dialog").showModal !== "function") return;
    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "elv2-pd-mobile-trigger";
    trigger.setAttribute("aria-haspopup", "dialog");
    trigger.setAttribute("aria-expanded", "false");
    trigger.addEventListener("click", () => openMobileRxPicker(select, trigger, group, key));
    select.addEventListener("change", syncMobilePdPickers);
    select.after(trigger);
    mobilePdPickers.set(key, { select, trigger, group });
    syncMobilePdPickers();
  }


  function closeMobileRxPicker(restoreFocus = true) {
    if (!mobileRxPicker) return;
    const { dialog, trigger, resize, focusGuard, cleanup } = mobileRxPicker;
    cleanup?.();
    mobileRxPicker = null;
    window.removeEventListener("resize", resize);
    document.removeEventListener("focusin", focusGuard);
    trigger.setAttribute("aria-expanded", "false");
    dialog.close();
    dialog.remove();
    if (restoreFocus && trigger.isConnected && trigger.getClientRects().length) trigger.focus({ preventScroll: true });
  }

  let mobileReadersPicker = null;

  function mobileReadersSourceAvailable(value) {
    const entry = mobileReadersPicker;
    if (!entry || !entry.group.isConnected || !value) return false;
    return entry.records.some(record => record.value === value && record.source.isConnected !== false && entry.group.contains(record.source) && String(record.source.getAttribute?.("original-value") || record.source.value || "").trim() === value && !record.source.disabled
      && (!entry.sourceSelect || (!entry.sourceSelect.disabled && Array.from(entry.sourceSelect.options).includes(record.source))));
  }

  function configureMobileReadersPicker(group) {
    if (mobileReadersPicker?.group === group || typeof document.createElement("dialog").showModal !== "function") return;
    // Keep Globo's desktop markup, options, focusability and dropdown behavior intact.
    const trigger = group.querySelector(".dropdown-button");
    if (!trigger) return;
    const sourceControls = Array.from(group.querySelectorAll("input:not([type='hidden']),select,textarea"));
    const sourceSelect = sourceControls.find(control => control.tagName === "SELECT");
    const sourceValue = control => String(control.getAttribute?.("original-value") || control.value || "").trim();
    const records = (sourceSelect ? Array.from(sourceSelect.options) : sourceControls.filter(control => control.type === "radio")).map(source => ({ source, value: sourceValue(source) })).filter(record => record.value && Number.isFinite(Number(record.value)));
    const unique = [...new Map(records.map(record => [record.value, record])).values()].sort((a, b) => Number(a.value) - Number(b.value));
    // Fail back to Globo if its configured range differs; never invent provider choices.
    if (unique.length !== 13 || unique.some((record, i) => Number(record.value) !== 1 + i / 4)) return;
    const select = document.createElement("select");
    // Detached and unnamed: only the original controls participate in validation/submission.
    const placeholder = document.createElement("option");
    placeholder.value = ""; placeholder.textContent = "Select"; select.append(placeholder);
    unique.forEach(record => {
      const option = document.createElement("option");
      option.value = record.value; option.textContent = `+${Number(record.value).toFixed(2)}`;
      select.append(option);
    });
    const sync = () => {
      if (mobileReadersPicker?.select !== select) return;
      const selected = sourceSelect || sourceControls.find(control => control.type === "radio" && control.checked);
      select.value = selected ? sourceValue(selected) : "";
      Array.from(select.options).forEach(option => { option.disabled = Boolean(option.value) && !mobileReadersSourceAvailable(option.value); });
      select.disabled = sourceSelect ? sourceSelect.disabled : !sourceControls.some(control => control.type === "radio" && !control.disabled);
      if (!mobileRxPickerIsValid()) closeMobileRxPicker();
    };
    mobileReadersPicker = { group, trigger, select, sourceSelect, records, sync };
    sourceControls.forEach(control => { control.addEventListener("input", sync); control.addEventListener("change", sync); });
    select.addEventListener("change", () => {
      const value = select.value;
      if (mobileReadersPicker?.select !== select || !mobileReadersSourceAvailable(value)) { sync(); return; }
      const selectedSource = sourceSelect || sourceControls.find(control => control.type === "radio" && sourceValue(control) === value);
      // Update the entire radio group before notifying Globo: observers see one complete choice.
      if (sourceSelect) sourceSelect.value = value;
      else sourceControls.filter(control => control.type === "radio").forEach(control => { control.checked = sourceValue(control) === value; });
      // Reconfirming an existing power follows the same single original event path.
      selectedSource.dispatchEvent(new Event("input", { bubbles: true }));
      selectedSource.dispatchEvent(new Event("change", { bubbles: true }));
      sync();
    });
    const open = event => {
      if (!window.matchMedia("(max-width: 749px)").matches || !state.open || state.current !== "type" || selectedType() !== "readers") return;
      if (event.type === "keydown" && !["Enter", " "].includes(event.key)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      sync();
      openMobileRxPicker(select, trigger, group, "readers_power");
    };
    trigger.addEventListener("click", open, true);
    trigger.addEventListener("keydown", open, true);
    group.classList.add("elv2-readers-wheel");
    sync();
  }

  function mobileRxPickerIsValid() {
    const picker = mobileRxPicker;
    return !picker || (state.open && (picker.key === "readers_power" ? (state.current === "type" && selectedType() === "readers" && mobileReadersPicker?.select === picker.select) : (state.current === "details" && selectedMethod() === "manual"))
      && selectedType() === picker.type && (!picker.pdMode || (selectedPdMode() === picker.pdMode && mobilePdPickerAllowed(picker.key) && mobilePdPickers.get(picker.key)?.select === picker.select)) && window.matchMedia("(max-width: 749px)").matches
      && picker.trigger.isConnected && picker.trigger.getClientRects().length > 0
      && !picker.select.disabled && picker.group.dataset.elv2AxisRequired !== "false" && !isAppHidden(picker.group) && picker.select.value === picker.initialValue);
  }

  function openMobileRxPicker(select, trigger, group, key) {
    const isReaders = key === "readers_power";
    if (!state.open || (isReaders ? (state.current !== "type" || selectedType() !== "readers" || mobileReadersPicker?.select !== select) : (state.current !== "details" || selectedMethod() !== "manual" || !["single_vision", "prescription_reading"].includes(selectedType())))) return;
    const isPd = ["total_pd", "od_pd", "os_pd"].includes(key);
    if (isPd && !mobilePdPickerAllowed(key)) return;
    if (!window.matchMedia("(max-width: 749px)").matches || select.disabled || group.dataset.elv2AxisRequired === "false") return;
    closeDesktopRxDropdown(false);
    closeMobileRxPicker(false);
    const wheelValues = new Set();
    const options = Array.from(select.options).filter(option => {
      if (!option.value || option.disabled || wheelValues.has(option.value)) return false;
      wheelValues.add(option.value);
      return true;
    }).map(option => {
      const match = option.textContent.match(/[+−-]?\d+(?:\.\d+)?/);
      const number = match ? Number(match[0].replace("−", "-")) : NaN;
      return { value: option.value, label: number === 0 ? "0.00" : option.textContent, number };
    });
    if (!options.length || options.some(option => !Number.isFinite(option.number))) return;
    options.sort((a, b) => a.number - b.number);
    const zero = options.findIndex(option => option.number === 0);
    if (zero >= 0 && /_(sph|cyl)$/.test(key)) options.splice(zero + 1, 0, { ...options[zero], label: key.endsWith("_sph") ? "PL" : "DS / SPH" });
    const label = isReaders ? "Readers Power" : isPd ? (key === "total_pd" ? "Total PD" : key === "od_pd" ? "PD OD (Right)" : "PD OS (Left)") : `${key.endsWith("_sph") ? "SPH" : key.endsWith("_axis") ? "AXIS" : "CYL"} ${key.startsWith("od_") ? "OD (Right)" : "OS (Left)"}`;
    const dialog = document.createElement("dialog");
    dialog.className = "elv2-rx-picker elv2-rx-picker--fluid";
    dialog.setAttribute("aria-labelledby", "elv2-rx-picker-title");
    dialog.setAttribute("aria-modal", "true");
    dialog.innerHTML = `<header class="elv2-rx-picker__header"><button type="button" data-rx-cancel>Cancel</button><h2 id="elv2-rx-picker-title">${label}</h2><button type="button" data-rx-save>Save</button></header><p id="elv2-rx-picker-help">Scroll to choose · Tap outside to finish<span class="elv2-rx-picker__sr">. Keyboard: Enter to apply, Escape to cancel.</span></p><div class="elv2-rx-picker__wheel"><div class="elv2-rx-picker__band" aria-hidden="true"></div><div class="elv2-rx-picker__list" role="listbox" aria-label="${label}" aria-describedby="elv2-rx-picker-help" tabindex="0"></div></div>`;
    const list = dialog.querySelector('[role="listbox"]');
    const cancel = dialog.querySelector('[data-rx-cancel]');
    const save = dialog.querySelector('[data-rx-save]');
    save.disabled = !select.value;
    let pending = select.value, pendingIndex = options.findIndex(option => option.value === pending);
    const initialAnchor = isReaders ? 0 : isPd ? options.findIndex(option => option.number === (key === "total_pd" ? 63 : 31.5)) : key.endsWith("_axis") ? options.findIndex(option => option.number === 90) : zero;
    let active = pendingIndex >= 0 ? pendingIndex : Math.max(0, initialAnchor);
    let dirty = false, scrollIntent = false, scrolled = false, gestureActive = false, frame = 0;
    let programmedTop = null, firstCenter = 0, rowHeight = 44;
    let gestureTop = 0, gestureY = 0, suppressClick = false, backdropPress = false;
    const rows = options.map((option, index) => {
      const row = document.createElement("div");
      row.id = `elv2-rx-option-${index}`;
      row.setAttribute("role", "option");
      row.setAttribute("aria-selected", String(index === pendingIndex));
      row.classList.toggle("is-active", index === active);
      row.textContent = option.label;
      if (option.number === 0 && option.label !== "0.00") row.setAttribute("aria-label", `${option.label}, 0.00`);
      row.addEventListener("click", event => {
        if (mobileRxPicker?.dialog !== dialog || (suppressClick && event.detail !== 0)) return;
        scrolled = false; dirty = true; setActive(index, true); centerActive();
        list.focus({ preventScroll: true });
        // Assistive-technology activation has no pointer backdrop action available.
        if (event.detail === 0) commit();
      });
      list.append(row);
      return row;
    });
    const setActive = (index, selected) => {
      if (active !== index) { rows[active].classList.toggle("is-active", false); rows[index].classList.toggle("is-active", true); active = index; }
      if (selected && pendingIndex !== index) {
        if (pendingIndex >= 0) rows[pendingIndex].setAttribute("aria-selected", "false");
        rows[index].setAttribute("aria-selected", "true");
        pendingIndex = index;
      }
      if (selected) { pending = options[index].value; save.disabled = false; }
      list.setAttribute("aria-activedescendant", rows[index].id);
    };
    const measure = () => { rowHeight = rows[0].offsetHeight || 44; firstCenter = rows[0].offsetTop + rowHeight / 2; };
    const centerActive = () => {
      programmedTop = Math.max(0, firstCenter + active * rowHeight - list.clientHeight / 2);
      list.scrollTop = programmedTop;
    };
    const followScroll = () => {
      frame = 0;
      if (mobileRxPicker?.dialog !== dialog || !scrollIntent || !scrolled) return;
      const index = Math.max(0, Math.min(rows.length - 1, Math.round((list.scrollTop + list.clientHeight / 2 - firstCenter) / rowHeight)));
      dirty = true;
      setActive(index, true);
    };
    const beginGesture = event => {
      scrollIntent = true; gestureActive = true; gestureTop = list.scrollTop;
      gestureY = event.clientY ?? event.touches?.[0]?.clientY ?? 0;
      suppressClick = false;
    };
    list.addEventListener("pointerdown", beginGesture, { passive: true });
    list.addEventListener("touchstart", beginGesture, { passive: true });
    list.addEventListener("pointermove", event => { if (Math.abs(event.clientY - gestureY) > 6) suppressClick = true; }, { passive: true });
    list.addEventListener("touchmove", event => { if (Math.abs((event.touches?.[0]?.clientY ?? gestureY) - gestureY) > 6) suppressClick = true; }, { passive: true });
    ["pointerup", "pointercancel", "touchend", "touchcancel"].forEach(name => list.addEventListener(name, () => { gestureActive = false; }, { passive: true }));
    list.addEventListener("wheel", () => { scrollIntent = true; suppressClick = true; }, { passive: true });
    list.addEventListener("scroll", () => {
      if (programmedTop !== null && Math.abs(list.scrollTop - programmedTop) < 1) return;
      if (!scrollIntent || mobileRxPicker?.dialog !== dialog) return;
      programmedTop = null; scrolled = true;
      if (Math.abs(list.scrollTop - gestureTop) > 3) suppressClick = true;
      if (!frame) frame = window.requestAnimationFrame(followScroll);
    }, { passive: true });
    const dismiss = () => { if (mobileRxPicker?.dialog === dialog) closeMobileRxPicker(); };
    const commit = () => {
      if (mobileRxPicker?.dialog !== dialog) return;
      if (frame) { window.cancelAnimationFrame(frame); frame = 0; }
      followScroll();
      const live = Array.from(select.options).some(option => option.value === pending && !option.disabled);
      if ((!dirty && !isReaders) || !mobileRxPickerIsValid() || !live || (isReaders && !mobileReadersSourceAvailable(pending))) { dismiss(); return; }
      dismiss();
      if (select.value !== pending || isReaders) { select.value = pending; select.dispatchEvent(new Event("change", { bubbles: true })); }
    };
    const outside = event => { const r = dialog.getBoundingClientRect(); return event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom); };
    dialog.addEventListener("pointerdown", event => { backdropPress = outside(event); });
    dialog.addEventListener("click", event => { if (backdropPress && outside(event)) commit(); backdropPress = false; });
    cancel.addEventListener("click", dismiss);
    save.addEventListener("click", commit);
    dialog.addEventListener("cancel", event => { event.preventDefault(); dismiss(); });
    const openedWidth = window.innerWidth;
    const resize = () => {
      if (mobileRxPicker?.dialog !== dialog) return;
      if (!mobileRxPickerIsValid() || Math.abs(window.innerWidth - openedWidth) > 1) { dismiss(); return; }
      // Mobile browser chrome changes height; do not dismiss or turn this into an edit.
      measure();
      if (gestureActive || scrolled) {
        // Do not pull a moving wheel back to a stale highlighted row.
        programmedTop = null;
        if (frame) { window.cancelAnimationFrame(frame); frame = 0; }
        if (scrolled) followScroll();
      } else centerActive();
    };
    const focusGuard = event => { if (!dialog.contains(event.target)) list.focus({ preventScroll: true }); };
    const cleanup = () => { if (frame) window.cancelAnimationFrame(frame); window.removeEventListener("blur", dismiss); };
    mobileRxPicker = { dialog, trigger, select, group, resize, focusGuard, cleanup, key, pdMode: isPd ? selectedPdMode() : "", initialValue: select.value, type: selectedType() };
    dialog.addEventListener("keydown", event => {
      event.stopPropagation();
      if (event.key === "Escape") { event.preventDefault(); dismiss(); return; }
      if (event.key === "Tab") {
        event.preventDefault();
        const stops = [cancel, list, save].filter(control => !control.disabled);
        const current = stops.indexOf(document.activeElement);
        stops[(current + (event.shiftKey ? -1 : 1) + stops.length) % stops.length].focus();
        return;
      }
      if (event.target !== list) return;
      const movement = { ArrowDown: 1, ArrowUp: -1, PageDown: 5, PageUp: -5 }[event.key];
      if (movement || event.key === "Home" || event.key === "End") {
        event.preventDefault();
        const index = event.key === "Home" ? 0 : event.key === "End" ? rows.length - 1 : Math.max(0, Math.min(rows.length - 1, active + movement));
        dirty = true; scrolled = false; setActive(index, true); centerActive();
      } else if (event.key === " " || event.key === "Enter") {
        event.preventDefault(); dirty = true; scrolled = false; setActive(active, true);
        if (event.key === "Enter") commit();
      }
    });
    list.setAttribute("aria-activedescendant", rows[active].id);
    document.body.append(dialog);
    trigger.setAttribute("aria-expanded", "true");
    dialog.showModal();
    list.focus({ preventScroll: true });
    measure(); centerActive();
    window.addEventListener("resize", resize);
    window.addEventListener("blur", dismiss);
    document.addEventListener("focusin", focusGuard);
  }

  let desktopRxDropdown = null;

  function desktopRxDropdownIsValid() {
    const picker = desktopRxDropdown;
    return !picker || (state.open && state.current === "details" && selectedMethod() === "manual"
      && selectedType() === picker.type && !window.matchMedia("(max-width: 749px)").matches
      && picker.trigger.isConnected && picker.select.isConnected && picker.group.isConnected && picker.group.contains(picker.trigger) && picker.group.contains(picker.select) && picker.trigger.getClientRects().length > 0
      && state.fields?.get(picker.key)?.group === picker.group
      && !picker.select.disabled && !isAppHidden(picker.group)
      && picker.select.value === picker.initialValue && picker.select.selectedIndex === picker.initialIndex);
  }

  function closeDesktopRxDropdown(restoreFocus = true) {
    if (!desktopRxDropdown) return;
    const picker = desktopRxDropdown;
    desktopRxDropdown = null;
    picker.cleanup();
    picker.trigger.setAttribute("aria-expanded", "false");
    const mobile = window.matchMedia("(max-width: 749px)").matches;
    picker.trigger.setAttribute("aria-haspopup", mobile ? "dialog" : "listbox");
    if (mobile) picker.trigger.removeAttribute("role");
    picker.trigger.removeAttribute("aria-controls");
    picker.trigger.removeAttribute("aria-activedescendant");
    picker.list.remove();
    if (restoreFocus && picker.trigger.isConnected && picker.trigger.getClientRects().length) picker.trigger.focus({ preventScroll: true });
  }

  function openDesktopRxDropdown(select, trigger, group, key) {
    if (window.matchMedia("(max-width: 749px)").matches || !/^(od|os)_(sph|cyl)$/.test(key)
      || !state.open || state.current !== "details" || selectedMethod() !== "manual"
      || !["single_vision", "prescription_reading"].includes(selectedType()) || select.disabled || isAppHidden(group)) return;
    if (desktopRxDropdown?.select === select) { closeDesktopRxDropdown(); return; }
    closeDesktopRxDropdown(false);
    closeMobileRxPicker(false);
    const options = Array.from(select.options).map((option, index) => ({ option, index, value: option.value, label: option.textContent }))
      .filter(({ option }) => option.value && !option.disabled && !option.hidden);
    if (!options.length) return;
    const list = document.createElement("div");
    list.className = "elv2-rx-desktop-dropdown";
    list.id = `elv2-rx-dropdown-${key}`;
    list.setAttribute("role", "listbox");
    list.setAttribute("aria-label", `${key.endsWith("_sph") ? "SPH" : "CYL"} ${key.startsWith("od_") ? "OD (Right)" : "OS (Left)"}`);
    let active = options.findIndex(record => record.index === select.selectedIndex);
    if (active < 0) active = Math.max(0, options.findIndex(record => Number(record.option.value) === 0));
    const rows = options.map((record, index) => {
      const row = document.createElement("div");
      row.id = `${list.id}-${index}`;
      row.className = "elv2-rx-desktop-dropdown__option";
      row.setAttribute("role", "option");
      row.setAttribute("aria-selected", String(record.index === select.selectedIndex));
      row.textContent = record.label;
      row.addEventListener("pointerdown", event => { event.preventDefault(); });
      row.addEventListener("click", () => commit(index));
      row.addEventListener("pointermove", () => markActive(index));
      list.append(row);
      return row;
    });
    const markActive = index => {
      rows[active].classList.toggle("is-active", false);
      active = index;
      rows[active].classList.toggle("is-active", true);
      trigger.setAttribute("aria-activedescendant", rows[active].id);
    };
    const reveal = centered => {
      const row = rows[active];
      if (centered) list.scrollTop = Math.max(0, row.offsetTop - (list.clientHeight - row.offsetHeight) / 2);
      else if (row.offsetTop < list.scrollTop) list.scrollTop = row.offsetTop;
      else if (row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight;
    };
    const dismiss = restore => { if (desktopRxDropdown?.list === list) closeDesktopRxDropdown(restore); };
    const commit = index => {
      if (desktopRxDropdown?.list !== list) return;
      const record = options[index];
      if (!desktopRxDropdownIsValid() || !record || select.options[record.index] !== record.option || record.option.value !== record.value || record.option.textContent !== record.label || record.option.disabled || record.option.hidden || !record.option.value) { dismiss(true); return; }
      dismiss(true);
      // Preserve the selected alias row while writing only the real source value.
      select.selectedIndex = record.index;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    };
    const outside = event => { if (!list.contains(event.target) && !trigger.contains(event.target)) dismiss(false); };
    const focus = event => { if (!list.contains(event.target) && !trigger.contains(event.target)) dismiss(false); };
    const scroll = event => { if (event.target !== list && !list.contains(event.target)) dismiss(false); };
    const resize = () => dismiss(false);
    const keydown = event => {
      if (desktopRxDropdown?.list !== list || (event.target !== trigger && !list.contains(event.target))) return;
      if (event.key === "Tab") { dismiss(false); return; }
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); dismiss(true); return; }
      if (event.key === " ") { event.preventDefault(); event.stopPropagation(); return; }
      if (event.key === "Enter") { event.preventDefault(); event.stopPropagation(); commit(active); return; }
      const movement = { ArrowDown: 1, ArrowUp: -1, PageDown: 5, PageUp: -5 }[event.key];
      if (!movement && event.key !== "Home" && event.key !== "End") return;
      event.preventDefault(); event.stopPropagation();
      markActive(event.key === "Home" ? 0 : event.key === "End" ? rows.length - 1 : Math.max(0, Math.min(rows.length - 1, active + movement)));
      reveal(false);
    };
    const cleanup = () => {
      document.removeEventListener("pointerdown", outside, true);
      document.removeEventListener("focusin", focus, true);
      document.removeEventListener("keydown", keydown, true);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", resize);
    };
    desktopRxDropdown = { select, trigger, group, key, list, cleanup, type: selectedType(), initialValue: select.value, initialIndex: select.selectedIndex };
    trigger.setAttribute("role", "combobox");
    trigger.setAttribute("aria-haspopup", "listbox");
    trigger.setAttribute("aria-expanded", "true");
    trigger.setAttribute("aria-controls", list.id);
    document.body.append(list);
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(Math.max(rect.width, 128), window.innerWidth - 24);
    const below = window.innerHeight - rect.bottom - 12, above = rect.top - 12;
    const placeAbove = below < 502 && above > below;
    const height = Math.min(502, window.innerHeight - 24);
    if (height < 44) { dismiss(false); return; }
    list.style.width = `${width}px`;
    list.style.left = `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`;
    list.style.maxHeight = `${height}px`;
    const preferredTop = placeAbove ? rect.top - height : rect.bottom;
    list.style.top = `${Math.max(12, Math.min(preferredTop, window.innerHeight - height - 12))}px`;
    markActive(active); reveal(true);
    trigger.focus({ preventScroll: true });
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("focusin", focus, true);
    document.addEventListener("keydown", keydown, true);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", resize);
  }

  function configureNativeRxDropdown(group, key) {
    if (group.querySelector("select[data-elv2-native-rx-select]")) return;
    const sourceControls = Array.from(group.querySelectorAll("input:not([type='hidden']),select,textarea"));
    if (!sourceControls.length) return;
    const sourceSelect = sourceControls.find((control) => control.tagName === "SELECT");
    const records = sourceSelect
      ? Array.from(sourceSelect.options).map((option) => ({ value: String(option.value || "").trim(), label: option.textContent?.replace(/\s+/g, " ").trim() || String(option.value || "").trim() }))
      : sourceControls.filter((control) => control.type === "radio" || control.type === "checkbox").map((control) => {
        const value = String(control.getAttribute("original-value") || control.value || "").trim();
        return { value, label: controlText(control) || value };
      });
    if (positiveRxDraftEnabled() && key.endsWith("_axis") && !records.length) {
      for (let axis=1;axis<=180;axis++) records.push({value:String(axis),label:String(axis)});
    }
    const choices = records.filter((record) => record.value && !/^(select|choose|请选择)$/i.test(record.value));
    if (!choices.length) return;
    if (positiveRxDraftEnabled()) {
      rawRxSources.set(key,sourceControls);
      if (key.endsWith("_cyl")) for (let value=.25;value<=4;value+=.25) choices.push({value:`+${value.toFixed(2)}`,label:`+${value.toFixed(2)}`});
    }
    const centeredNumeric = /_(sph|cyl)$/.test(key);
    if (centeredNumeric) choices.sort((a, b) => Number(a.value) - Number(b.value));
    group.classList.add("elv2-native-rx-dropdown");

    const select = document.createElement("select");
    select.dataset.elv2ControlMirror = "true";
    select.dataset.elv2NativeRxSelect = "true";
    select.dataset.elv2NativeRxKey = key;
    select.className = "elv2-native-rx-select";
    select.setAttribute("aria-label", englishDisplayText(group.dataset.elv2Label) || key.toUpperCase());
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Select";
    // Preserve an empty initial value without offering it as a prescription option.
    placeholder.hidden = centeredNumeric;
    placeholder.disabled = centeredNumeric;
    if (!centeredNumeric) select.append(placeholder);
    let placeholderInserted = !centeredNumeric;
    const seen = new Set();
    choices.forEach(({ value, label }) => {
      if (seen.has(value)) return;
      seen.add(value);
      // Keep the genuinely empty selection adjacent to zero, never select zero implicitly.
      if (!placeholderInserted && Number(value) >= 0) {
        select.append(placeholder);
        placeholderInserted = true;
      }
      const option = document.createElement("option");
      option.value = value;
      option.textContent = englishDisplayText(label);
      if (centeredNumeric && Number(value) === 0) option.textContent = "0.00";
      select.append(option);
      if (centeredNumeric && Number(value) === 0) {
        const alias = document.createElement("option");
        alias.value = value;
        alias.textContent = key.endsWith("_sph") ? "PL" : "DS / SPH";
        select.append(alias);
      }
    });

    if (!placeholderInserted) select.append(placeholder);
    select.value = "";

    if (positiveRxDraftEnabled()) rawRxMirrors.set(key,select);
    let mobileTrigger = null;
    const syncMobileTrigger = () => {
      if (!mobileTrigger) return;
      mobileTrigger.textContent = select.value && Number(select.value) === 0 ? "0.00" : select.selectedOptions[0]?.textContent || "Select";
      mobileTrigger.disabled = select.disabled;
      mobileTrigger.setAttribute("aria-label", `${key.endsWith("_sph") ? "SPH" : key.endsWith("_axis") ? "AXIS" : "CYL"} ${key.startsWith("od_") ? "OD (Right)" : "OS (Left)"}: ${mobileTrigger.textContent}`);
    };
    let applyingMirror = false;
    // Reassigning the same value resets duplicate-value option selection to the first row.
    const syncValue = value => { if (select.value !== value) select.value = value; };
    const syncFromSource = () => {
      if (rawRxWriting || applyingMirror) return;
      if (rawRx && NATIVE_RX_KEYS.has(key)) { syncValue(rawRx[key] || ""); syncMobileTrigger(); if (!desktopRxDropdownIsValid()) closeDesktopRxDropdown(false); return; }
      if (sourceSelect) syncValue(String(sourceSelect.value || "").trim());
      else {
        const selected = sourceControls.find((control) => (control.type === "radio" || control.type === "checkbox") && control.checked);
        syncValue(selected ? String(selected.getAttribute("original-value") || selected.value || "").trim() : sourceControls.find(control => !["radio","checkbox"].includes(control.type))?.value || "");
      }
      syncMobileTrigger();
      if (!mobileRxPickerIsValid()) closeMobileRxPicker();
      if (!desktopRxDropdownIsValid()) closeDesktopRxDropdown(false);
    };
    sourceControls.forEach((control) => {
      control.hidden = true;
      control.tabIndex = -1;
      control.setAttribute("aria-hidden", "true");
      control.addEventListener("input", syncFromSource);
      control.addEventListener("change", syncFromSource);
    });
    syncFromSource();
    select.addEventListener("change", () => {
      const value = select.value;
      if (acceptRawRxEdit(key,value)) { syncMobileTrigger(); return; }
      applyingMirror = true;
      try {
      sourceControls.forEach((control) => {
        const choice = control.type === "radio" || control.type === "checkbox";
        const sourceValue = String(control.getAttribute("original-value") || control.value || "").trim();
        const selected = choice ? sourceValue === value : false;
        const changed = choice ? control.checked !== selected : control.value !== value;
        if (!changed) return;
        if (choice) control.checked = selected;
        else control.value = value;
        control.dispatchEvent(new Event("input", { bubbles: true }));
        control.dispatchEvent(new Event("change", { bubbles: true }));
      });
      } finally { applyingMirror = false; syncFromSource(); }
    });
    const fieldLabel = group.querySelector(":scope > .gpo-label,:scope > label.gpo-label,:scope > .gpo-element__label,:scope > legend");
    if (fieldLabel) fieldLabel.after(select);
    else group.prepend(select);
    if (/_(sph|cyl|axis)$/.test(key) && typeof document.createElement("dialog").showModal === "function") {
      mobileTrigger = document.createElement("button");
      mobileTrigger.type = "button";
      mobileTrigger.className = "elv2-rx-mobile-trigger";
      mobileTrigger.setAttribute("aria-haspopup", "dialog");
      mobileTrigger.setAttribute("aria-expanded", "false");
      const desktopField = /^(od|os)_(sph|cyl)$/.test(key);
      const syncTriggerRole = () => {
        const desktop = desktopField && !window.matchMedia("(max-width: 749px)").matches;
        mobileTrigger.setAttribute("aria-haspopup", desktop ? "listbox" : "dialog");
        if (desktop) mobileTrigger.setAttribute("role", "combobox"); else mobileTrigger.removeAttribute("role");
      };
      mobileTrigger.addEventListener("focus", syncTriggerRole);
      mobileTrigger.addEventListener("click", () => {
        syncTriggerRole();
        if (desktopField && !window.matchMedia("(max-width: 749px)").matches) openDesktopRxDropdown(select, mobileTrigger, group, key);
        else openMobileRxPicker(select, mobileTrigger, group, key);
      });
      mobileTrigger.addEventListener("keydown", event => {
        if (desktopField && !window.matchMedia("(max-width: 749px)").matches && !desktopRxDropdown && ["ArrowDown", "ArrowUp"].includes(event.key)) {
          event.preventDefault(); syncTriggerRole(); openDesktopRxDropdown(select, mobileTrigger, group, key);
        }
      });
      syncTriggerRole();
      select.addEventListener("change", syncMobileTrigger);
      select.after(mobileTrigger);
      group.classList.add("elv2-has-mobile-rx-picker");
      if (/^(od|os)_(sph|cyl)$/.test(key)) group.classList.add("elv2-has-desktop-rx-picker");
      syncMobileTrigger();
    }


  }

  function validateUpload() {
    const input = controls("upload").find((control) => control.type === "file");
    if (!completeUploadIfReady(input)) return showError("Wait until the prescription upload is complete before continuing.", fieldGroup("upload"), "upload_incomplete");
    return true;
  }

  function numericValue(key) {
    const control = checkedControl(key);
    if (!control) return null;
    const candidates = control.tagName === "SELECT" ? [control.selectedOptions?.[0]?.textContent, control.value] : [control.value, controlText(control)];
    for (const candidate of candidates) {
      const match = String(candidate || "").match(/[+-]?\d+(?:\.\d+)?/);
      if (match && Number.isFinite(Number(match[0]))) return Number(match[0]);
    }
    return null;
  }

  function selectedType() {
    const value = norm(valueText("type"));
    if (/prescription reading|定制阅读镜/.test(value)) return "prescription_reading";
    if (/readers|简易阅读镜/.test(value)) return "readers";
    if (/single vision|单光处方/.test(value)) return "single_vision";
    return "";
  }

  function selectedMethod() {
    const value = norm(valueText("method"));
    if (/upload|上传/.test(value)) return "upload";
    if (/manual|enter|手动/.test(value)) return "manual";
    if (/later|稍后/.test(value)) return "later";
    return "";
  }

  function selectedPdMode() {
    const value = norm(valueText("pd_mode"));
    if (/dual|left.*right|左右|单眼/.test(value)) return "dual";
    if (/total|single|总瞳距/.test(value)) return "total";
    return "";
  }

  function route() {
    const ids = ["type"];
    if (selectedType() !== "readers") {
      ids.push("details");
    }
    ids.push("function", "solution", "review");
    return ids;
  }

  function isAppHidden(group) {
    if (!group || group.hidden || group.getAttribute("aria-hidden") === "true") return true;
    if (norm(group.getAttribute("data-clo-display")) === "show") return true;
    if (/(^|\s)(gpo-hidden|is-hidden|hidden|hide)(\s|$)/i.test(group.className || "")) return true;
    let node = group;
    while (node instanceof HTMLElement) {
      const style = window.getComputedStyle(node);
      if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return true;
      if (node === state.root) break;
      node = node.parentElement;
    }
    return false;
  }

  function portalSlot(field) {
    if (["od_sph", "os_sph", "od_cyl", "os_cyl", "od_axis", "os_axis", "pd_mode", "total_pd", "od_pd", "os_pd"].includes(field.key)) {
      return state.overlay.querySelector(`[data-elv2-slot="${field.key}"]`);
    }
    const existing = state.overlay.querySelector(`.elv2-field-slot[data-elv2-slot-for="${CSS.escape(field.key)}"]`);
    if (existing) {
      existing.style.minHeight = "";
      return existing;
    }
    const slot = document.createElement("div");
    slot.className = "elv2-field-slot";
    slot.dataset.elv2SlotFor = field.key;
    if (field.key === "upload") state.overlay.querySelector(".elv2-rx-entry")?.after(slot);
    else state.panels.get(field.step)?.querySelector(".elv2-panel__body")?.append(slot);
    return slot;
  }

  function setDataState(element, key, value) {
    const next = String(value);
    if (element?.dataset[key] !== next) element.dataset[key] = next;
  }

  function setPortalOpen(group) {
    if (state.portalOpen === group) return;
    if (state.portalOpen) setDataState(state.portalOpen, "elv2PortalOpen", false);
    state.portalOpen = group || null;
    if (group) setDataState(group, "elv2PortalOpen", true);
  }

  function clearPortalOpen(group = state.portalOpen) {
    if (!group || state.portalOpen !== group) return;
    setDataState(group, "elv2PortalOpen", false);
    state.portalOpen = null;
  }

  function releasePortalInteraction() {
    const active = document.activeElement;
    if (active instanceof HTMLElement && state.root?.contains(active)) active.blur();
    clearPortalOpen();
    state.portalTouch = null;
    if (state.portalTouchFrame) window.cancelAnimationFrame(state.portalTouchFrame);
    state.portalTouchFrame = 0;
  }

  function handlePortalPointerDown(event) {
    const group = event.target.closest?.(".elv2-portal-field");
    const trigger = event.target.closest?.(".dropdown-button");
    if (group && trigger && state.portalOpen === group) clearPortalOpen(group);
    else if (group) setPortalOpen(group);
    else clearPortalOpen();
  }

  function handlePortalFocusIn(event) {
    const group = event.target.closest?.(".elv2-portal-field");
    if (group) setPortalOpen(group);
  }

  function handlePortalFocusOut(event) {
    const group = event.target.closest?.(".elv2-portal-field");
    if (!group || group.contains(event.relatedTarget)) return;
    window.requestAnimationFrame(() => {
      if (!group.contains(document.activeElement)) clearPortalOpen(group);
    });
  }

  function nestedTouchScroller(target, group) {
    let node = target instanceof Element ? target : null;
    while (node && node !== group) {
      const style = window.getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
      node = node.parentElement;
    }
    return null;
  }

  function handlePortalTouchStart(event) {
    if (!state.open || event.touches.length !== 1) return;
    const group = event.target.closest?.('.elv2-portal-field[data-elv2-portal-active="true"]');
    const main = state.overlay?.querySelector(".elv2-main");
    if (!group || !main || nestedTouchScroller(event.target, group)) return;
    const touch = event.touches[0];
    state.portalTouch = { group, main, identifier: touch.identifier, startX: touch.clientX, startY: touch.clientY, startScrollTop: main.scrollTop, dragging: false };
  }

  function handlePortalTouchMove(event) {
    const gesture = state.portalTouch;
    if (!gesture) return;
    const touch = Array.from(event.touches).find((item) => item.identifier === gesture.identifier);
    if (!touch) return;
    const dx = touch.clientX - gesture.startX;
    const dy = touch.clientY - gesture.startY;
    if (!gesture.dragging) {
      if (Math.hypot(dx, dy) < 8) return;
      if (Math.abs(dy) <= Math.abs(dx)) { state.portalTouch = null; return; }
      gesture.dragging = true;
      clearPortalOpen(gesture.group);
    }
    event.preventDefault();
    const max = Math.max(0, gesture.main.scrollHeight - gesture.main.clientHeight);
    state.portalTouchScrollTop = Math.max(0, Math.min(max, gesture.startScrollTop - dy));
    if (state.portalTouchFrame) return;
    state.portalTouchFrame = window.requestAnimationFrame(() => {
      state.portalTouchFrame = 0;
      if (gesture.main.isConnected) gesture.main.scrollTop = state.portalTouchScrollTop;
    });
  }

  function finishPortalTouch(cancelled = false) {
    const gesture = state.portalTouch;
    state.portalTouch = null;
    if (!cancelled && gesture?.dragging) {
      state.suppressedTouchGroup = gesture.group;
      state.suppressedTouchUntil = window.performance.now() + 500;
    }
  }

  function handlePortalTouchEnd() { finishPortalTouch(false); }
  function handlePortalTouchCancel() { finishPortalTouch(true); }

  function suppressDraggedPortalClick(event) {
    const group = event.target.closest?.(".elv2-portal-field");
    if (!group || group !== state.suppressedTouchGroup || window.performance.now() > state.suppressedTouchUntil) return;
    state.suppressedTouchGroup = null;
    state.suppressedTouchUntil = 0;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function preparePortalStates() {
    state.fields.forEach((field, key) => {
      if (field.step === "entry") return;
      const group = field.group;
      const slot = state.portals.get(key);
      if (!slot) return;
      const axisDisabled = key.endsWith("_axis") && group.dataset.elv2AxisRequired === "false";
      const visible = !state.portalsSuspended && !axisDisabled && !(key === "upload" && state.rxPendingSnapshot) && state.open && field.step === state.current && !isAppHidden(group);
      setDataState(group, "elv2PortalActive", visible);
      if (!visible) {
        setDataState(group, "elv2PortalOffscreen", true);
        if (!state.portalsSuspended) {
          const minHeight = axisDisabled && field.step === state.current ? "" : "0px";
          if (slot.style.minHeight !== minHeight) slot.style.minHeight = minHeight;
        }
        clearPortalOpen(group);
      }
    });
  }

  function syncPortals() {
    state.portalFrame = 0;
    if (state.portalsSuspended) return;
    const main = state.overlay?.querySelector(".elv2-main");
    const mainRect = main?.getBoundingClientRect();
    const measurements = [];
    state.fields.forEach((field, key) => {
      if (field.step === "entry") return;
      const group = field.group;
      const slot = state.portals.get(key);
      if (!slot || group.dataset.elv2PortalActive !== "true") return;
      measurements.push({ group, slot, slotRect: slot.getBoundingClientRect(), height: Math.max(1, Math.ceil(group.getBoundingClientRect().height)) });
    });
    let needsFollowup = false;
    measurements.forEach(({ group, slot, slotRect, height }) => {
      const left = `${Math.round(slotRect.left)}px`;
      const top = `${Math.round(slotRect.top)}px`;
      const width = `${Math.round(slotRect.width)}px`;
      const minHeight = `${height}px`;
      if (group.style.left !== left) group.style.left = left;
      if (group.style.top !== top) group.style.top = top;
      if (group.style.width !== width) group.style.width = width;
      if (slot.style.minHeight !== minHeight) { slot.style.minHeight = minHeight; needsFollowup = true; }
      const offscreen = !mainRect || slotRect.bottom <= mainRect.top || slotRect.top >= mainRect.bottom;
      setDataState(group, "elv2PortalOffscreen", offscreen);
    });
    if (needsFollowup && !state.portalFrame) state.portalFrame = window.requestAnimationFrame(syncPortals);
  }

  function schedulePortalSync() {
    preparePortalStates();
    if (state.portalsSuspended) {
      if (state.portalFrame) window.cancelAnimationFrame(state.portalFrame);
      state.portalFrame = 0;
      return;
    }
    if (state.portalFrame) return;
    state.portalFrame = window.requestAnimationFrame(syncPortals);
  }

  function scheduleRender() {
    if (state.renderFrame) return;
    state.renderFrame = window.requestAnimationFrame(() => {
      state.renderFrame = 0;
      render();
    });
  }

  function suspendPortals() {
    releasePortalInteraction();
    state.confirmOpen = true;
    state.portalsSuspended = true;
    preparePortalStates();
    if (state.portalFrame) window.cancelAnimationFrame(state.portalFrame);
    state.portalFrame = 0;
  }

  function resumePortals() {
    state.confirmOpen = false;
    if (!state.portalsSuspended) return;
    state.portalsSuspended = false;
    schedulePortalSync();
  }

  function visibilityStyleSignature(value) {
    return String(value || "").split(";").map((part) => part.replace(/\s+/g, "").toLowerCase()).filter((part) => /^(display|visibility):/.test(part)).sort().join(";");
  }

  function visibilityClassSignature(value) {
    return String(value || "").split(/\s+/).filter((name) => /(^|[-_])(gpo-hidden|is-hidden|hidden|hide)([-_]|$)/i.test(name)).sort().join(" ");
  }

  function addsGloboGroup(mutation) {
    if (mutation.target.closest?.(".elv2-portal-field")) return false;
    return Array.from(mutation.addedNodes).some((node) => node instanceof Element && (node.matches(GROUP_SELECTOR) || node.querySelector(GROUP_SELECTOR)));
  }

  function clearField(key) {
    if (state.clearing || rawRxWriting) return;
    if (rawRx && NATIVE_RX_KEYS.has(key)) {
      if (rawRx[key]) rawRxRevision+=1;
      rawRx[key]="";
      if (Array.from(NATIVE_RX_KEYS).every(key => !rawRx[key])) { rawRx=null; clearRawRxMetadata(); }
    }
    const fieldControls = controls(key);
    if (key === "upload") {
      const group = fieldGroup(key);
      if (group) {
        uploadSelections.delete(group);
        group.querySelectorAll(".gpo-f-rm").forEach((button) => button.dispatchEvent(new Event("gpoFakeRemove")));
        delete group.dataset.elv2UploadReference;
        group.dataset.elv2UploadState = "empty";
      }
    }
    state.clearing = true;
    try {
      fieldControls.forEach((control) => {
        let changed = false;
        if ((control.type === "radio" || control.type === "checkbox") && control.checked) { control.checked = false; changed = true; }
        else if (control.type === "file" && control.value) { control.value = ""; changed = true; }
        else if (!["radio", "checkbox", "file"].includes(control.type) && control.value) { control.value = ""; changed = true; }
        if (changed) control.dispatchEvent(new Event("change", { bubbles: true }));
      });
    } finally { state.clearing = false; }
    fieldGroup(key)?.classList.remove("elv2-invalid");
  }

  function syncAxis(eye) {
    const cyl = numericValue(`${eye}_cyl`);
    const axisGroup = fieldGroup(`${eye}_axis`);
    if (!axisGroup) return;
    const required = cyl !== null && cyl !== 0;
    axisGroup.classList.toggle("elv2-axis-none", !required);
    axisGroup.dataset.elv2AxisRequired = String(required);
    state.portals.get(`${eye}_axis`)?.classList.toggle("elv2-axis-slot-none", !required);
    if (!required && !rawRx) clearField(`${eye}_axis`);
    else if (cyl === 0 && rawRx) clearRawZeroCylinderAxis(eye);
  }

  function cleanInvalidRoutes() {
    const type = selectedType();
    const method = selectedMethod();
    const pdMode = selectedPdMode();
    if (type === "readers") ["method", "upload", "od_sph", "os_sph", "od_cyl", "os_cyl", "od_axis", "os_axis", "pd_mode", "total_pd", "od_pd", "os_pd"].forEach(clearField);
    else if (method !== "manual" && !state.rxPicking) ["od_sph", "os_sph", "od_cyl", "os_cyl", "od_axis", "os_axis", "pd_mode", "total_pd", "od_pd", "os_pd"].forEach(clearField);
    if (method !== "upload") clearField("upload");
    if (pdMode === "total") ["od_pd", "os_pd"].forEach(clearField);
    if (pdMode === "dual") clearField("total_pd");
    state.fields.forEach((field, key) => {
      if (key.startsWith("solution_") && isAppHidden(field.group)) clearField(key);
    });
  }

  function showError(message, group, failureCode = "input_invalid") {
    behaviorDetail("configurator_validation_failed", { failure_code: failureCode });
    const error = state.overlay.querySelector(".elv2-error");
    error.innerHTML = message;
    error.hidden = false;
    if (group) { group.classList.add("elv2-invalid"); group.scrollIntoView({ behavior: "smooth", block: "center" }); group.querySelector("input,select,textarea")?.focus({ preventScroll: true }); }
    return false;
  }

  function requireChoice(key, en, _zh) {
    const group = fieldGroup(key);
    group?.classList.remove("elv2-invalid");
    return checkedControl(key) ? true : showError(`Please select ${en}.`, group, "choice_required");
  }

  // Pure rule results are shared by Continue and correction-only feedback.
  // Editing never reveals a new error, moves focus, or emits validation analytics.
  function detailsValidationRules() {
    const rules = [];
    const add = (id, keys, groupKey, message, valid, failureCode = "input_invalid") => rules.push({ id, keys, groupKey, message, valid, failureCode });
    for (const [key, label] of [["od_sph", "OD SPH"], ["os_sph", "OS SPH"], ["od_cyl", "OD CYL"], ["os_cyl", "OS CYL"]]) {
      add(key, [key], key, `Please select ${label}.`, Boolean(checkedControl(key)), "choice_required");
    }
    for (const eye of ["od", "os"]) {
      const cyl = numericValue(`${eye}_cyl`), axis = numericValue(`${eye}_axis`);
      add(`${eye}_axis`, [`${eye}_cyl`, `${eye}_axis`], `${eye}_axis`, "AXIS must be an integer from 1 to 180 when CYL is not 0.00.", cyl === null || cyl === 0 || (Number.isInteger(axis) && axis >= 1 && axis <= 180));
    }
    add("pd_mode", ["pd_mode"], "pd_mode", "Please select PD Mode.", Boolean(checkedControl("pd_mode")), "choice_required");
    const mode = selectedPdMode();
    if (mode === "total") {
      const total = numericValue("total_pd");
      add("total_pd", ["pd_mode", "total_pd"], "total_pd", "Total PD must be 54–78 mm in 0.5 mm steps.", total !== null && total >= 54 && total <= 78 && Number.isInteger(total * 2));
    } else if (mode === "dual") {
      const od = numericValue("od_pd"), os = numericValue("os_pd");
      const keys = ["pd_mode", "od_pd", "os_pd"];
      add("dual_pd_required", keys, od === null ? "od_pd" : "os_pd", "Enter both OD PD and OS PD.", od !== null && os !== null);
      add("od_pd_range", keys, "od_pd", "OD PD must be 25–41 mm in 0.5 mm steps.", od !== null && od >= DUAL_PD_MIN && od <= DUAL_PD_MAX && Number.isInteger(od / DUAL_PD_STEP));
      add("os_pd_range", keys, "os_pd", "OS PD must be 25–41 mm in 0.5 mm steps.", os !== null && os >= DUAL_PD_MIN && os <= DUAL_PD_MAX && Number.isInteger(os / DUAL_PD_STEP));
      const sum = od + os;
      add("dual_pd_sum", keys, "od_pd", "OD PD + OS PD must total 54–78 mm in 0.5 mm steps.", od !== null && os !== null && sum >= DUAL_PD_TOTAL_MIN && sum <= DUAL_PD_TOTAL_MAX && Number.isInteger(sum / DUAL_PD_STEP));
    } else add("pd_mode_known", ["pd_mode"], "pd_mode", "Choose Total PD or Dual PD.", false);
    const rawError=rawRxValidation();
    if (rawError) add("raw_rx",Array.from(NATIVE_RX_KEYS),"od_cyl",rawError,false);
    return rules;
  }

  function validateDetails() {
    const rule = detailsValidationRules().find((candidate) => !candidate.valid);
    if (!rule) return true;
    state.detailsError = { id: rule.id, keys: rule.keys, groupKey: rule.groupKey };
    return showError(rule.message, fieldGroup(rule.groupKey), rule.failureCode);
  }

  function refreshDetailsError(key) {
    const active = state.detailsError;
    if (state.current !== "details" || !active || (!active.keys.includes(key) && !active.dirty)) return;
    active.dirty = false;
    const rule = detailsValidationRules().find((candidate) => candidate.id === active.id);
    if (rule && !rule.valid) return;
    fieldGroup(active.groupKey)?.classList.remove("elv2-invalid");
    state.overlay.querySelector(".elv2-error").hidden = true;
    state.detailsError = null;
  }

  function validateStep() {
    state.detailsError = null;
    state.overlay.querySelector(".elv2-error").hidden = true;
    state.overlay.querySelectorAll(".elv2-invalid").forEach((group) => group.classList.remove("elv2-invalid"));
    if (state.current === "type") {
      if (!requireChoice("type", "Prescription Type", "处方类型")) return false;
      if (selectedType() === "readers" && !requireChoice("readers_power", "Readers Power", "简易阅读镜度数")) return false;
    }
    if (state.current === "method") {
      if (!requireChoice("method", "Prescription Method", "处方提供方式")) return false;
      if (selectedMethod() === "upload" && !validateUpload()) return false;
    }
    if (state.current === "details" && !(selectedMethod() === "upload" ? validateUpload() : selectedMethod() === "manual" && validateDetails())) return false;
    if (rawRx && ["function","solution","review"].includes(state.current) && rawRxValidation()) return showError(rawRxValidation(),fieldGroup("od_cyl"));
    if (state.current === "function") {
      const keys = selectedType() === "readers" ? ["function_readers"] : ["function_prescription"];
      if (!keys.some(checkedControl)) return showError("Please select a Lens Function.", fieldGroup(keys[0]), "choice_required");
    }
    if (state.current === "solution" && manualLensRoute()) {
      const key=selectedSolutionKey(), control=checkedControl(key), family=state.fields.get(key)?.family;
      if (!control || !rawRxCandidate(control,family)) return showError("This lens is not available for your prescription. Choose another option or upload your prescription for review.",fieldGroup(key));
    }
    if (state.current === "solution") {
      const keys = Array.from(state.fields.keys()).filter((key) => key.startsWith("solution_") && !isAppHidden(fieldGroup(key)));
      if (!keys.some(checkedControl)) return showError("Please select a Lens Index.", fieldGroup(keys[0]), "choice_required");
    }
    return true;
  }

  function openOverlay() {
    const wasOpen = state.open;
    state.open = true;
    state.overlay.hidden = false;
    document.documentElement.classList.add("elv2-page-locked");
    render();
    if (!wasOpen && state.open && !state.overlay.hidden) {
      behaviorFlow = behaviorCall("begin", "configurator", "product");
      behaviorVisit = 0; behaviorStep = ""; behaviorSelections.clear();
      behaviorView();
      if (behaviorOpened) behaviorDetail("configurator_restarted");
      behaviorOpened = true;
    }
    schedulePortalSync();
    refreshCartBadge();
    state.overlay.querySelector("[data-elv2-close]").focus();
  }

  function closeOverlay() {
    closeDesktopRxDropdown(false);
    closeMobileRxPicker(false);
    behaviorDetail("configurator_closed");
    if (!behaviorPending.has(behaviorFlow)) behaviorCall("end", behaviorFlow);
    behaviorFlow = null;
    const resetCompletedSelection = state.cartStatus === "success";
    if (state.cartOpen) closeCartDrawer();
    releasePortalInteraction();
    state.open = false;
    state.overlay.hidden = true;
    document.documentElement.classList.remove("elv2-page-locked");
    if (resetCompletedSelection) resetFlowAfterSuccessfulAdd();
    schedulePortalSync();
  }

  function resetFlowAfterSuccessfulAdd() {
    state.rxPickerRaw=null;
    state.cartToken += 1;
    state.cartConfirmation = null;
    state.fields.forEach((_field, key) => clearField(key));
    document.querySelectorAll('form[action*="/cart/add"] [data-elv2-cart-meta]').forEach((input) => input.remove());
    state.current = "type";
    state.confirmedRx = false;
    setCartStatus("idle");
    render();
  }

  function selectPrescriptionEntry() {
    if (window.ElookusAvailability && !window.ElookusAvailability.canPurchase()) return;
    const option = controls("purchase").find((control) => /select prescription|选择处方镜片/i.test(controlText(control)));
    activateOriginalControl(option);
    openOverlay();
  }

  function selectFrameOnlyEntry() {
    if (window.ElookusAvailability && !window.ElookusAvailability.canPurchase()) return;
    const option = controls("purchase").find((control) => /frame only|仅购买镜框/i.test(controlText(control)));
    activateOriginalControl(option);
    setHidden(state.entry.querySelector(".elv2-frame-confirm"), false);
    state.entry.querySelector("[data-elv2-frame-cart]")?.focus();
  }

  function activateOriginalControl(control) {
    if (!control) return false;
    if (control instanceof HTMLInputElement && ["radio", "checkbox"].includes(control.type)) {
      const checked = control.type === "radio" ? true : !control.checked;
      control.click();
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "checked")?.set;
      if (setter) setter.call(control, checked);
      else control.checked = checked;
      control.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
      control.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
      control.closest("[data-elv2-field]")?.querySelectorAll(".elv2-choice-card,.elv2-sales-card").forEach((card) => {
        card.setAttribute("aria-checked", String(Boolean(card.querySelector("input[type='radio']:checked"))));
      });
      return control.checked === checked;
    }
    control.click();
    return true;
  }

  function purchaseModeChanged(control) {
    if (!control?.checked) return;
    const frame = /frame only|仅购买镜框/i.test(controlText(control));
    state.entry.querySelector(".elv2-frame-confirm").hidden = !frame;
    if (frame) {
      state.current = "type";
      state.confirmedRx = false;
    } else openOverlay();
  }

  function findCartActions() {
    const scope = state.root.closest(".product-info,.product-form,form[action*='/cart/add']") || state.root.closest("form") || document;
    const found = Array.from(scope.querySelectorAll("button[name='add'],[data-add-to-cart],.product-form__submit,.add-to-cart,button[type='submit']")).filter((button) => {
      if (state.overlay.contains(button) || state.entry.contains(button)) return false;
      return button.matches("button[name='add'],[data-add-to-cart],.product-form__submit,.add-to-cart") || /add to cart|加入购物车/i.test(button.textContent || button.value || "");
    });
    if (found.length) {
      state.cartActions = found;
      found.forEach((button) => { button.dataset.elv2NativeCart = "true"; button.hidden = true; });
    }
    return state.cartActions;
  }

  function positionEntry() {
    const action = findCartActions()[0];
    const target = action?.closest(".buy-buttons-row,.product-form__buttons,[data-product-form-buttons]") || action;
    if (target && target.parentElement && state.entry.nextElementSibling !== target) target.before(state.entry);
  }

  function cartRequestContext() {
    const action = findCartActions().find((button) => !button.disabled) || findCartActions()[0];
    const form = action?.form || action?.closest("form[action*='/cart/add']") || null;
    return action && form ? { action, form } : null;
  }

  function clickNativeCart(action) {
    if (!action) return showError("The store cart button is not ready. Please try again.");
    if (action.disabled) return showError("The store cart button is not ready. Please try again.");
    action.click();
    return true;
  }

  function renderCartStatus(focus = false) {
    const button = state.overlay?.querySelector("[data-elv2-next]");
    const feedback = state.overlay?.querySelector("[data-elv2-cart-feedback]");
    if (!button || !feedback) return;
    const title = feedback.querySelector("[data-elv2-cart-title]");
    const copy = feedback.querySelector("[data-elv2-cart-copy]");
    const actions = feedback.querySelector("[data-elv2-cart-actions]");
    const pending = state.cartStatus === "pending";
    const success = state.cartStatus === "success";
    const failed = state.cartStatus === "error";
    button.disabled = pending || success;
    button.setAttribute("aria-busy", String(pending));
    if (state.current === "review") {
      button.querySelector(".elv2-label span").textContent = pending ? "Adding…" : success ? "Added to Cart" : failed ? "Try Again" : "Add to Cart";
    }
    feedback.dataset.state = state.cartStatus;
    feedback.hidden = state.cartStatus === "idle";
    if (actions) actions.hidden = !success;
    if (pending) {
      title.textContent = "Adding your selection…";
      copy.textContent = "Please wait while your selection is added to the cart.";
    } else if (success) {
      title.textContent = "Added to cart";
      copy.textContent = "Your selection was added and the cart is now open.";
    } else if (failed) {
      title.textContent = "Unable to add to cart";
      copy.textContent = "We could not confirm the cart update. Check your connection and try again.";
    }
    if (focus && success) window.requestAnimationFrame(() => feedback.focus({ preventScroll: true }));
    if (focus && failed) window.requestAnimationFrame(() => button.focus({ preventScroll: true }));
  }

  function setCartStatus(status, focus = false) {
    state.cartStatus = status;
    renderCartStatus(focus);
  }

  async function fetchCartState() {
    const response = await window.fetch("/cart.js", { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  function updateCartBadge(cart) {
    if (!cart || !Number.isFinite(Number(cart.item_count))) return;
    const count = Number(cart.item_count);
    const badge = state.overlay?.querySelector("[data-elv2-cart-count]");
    if (!badge) return;
    badge.textContent = count > 99 ? "99+" : String(count);
    badge.setAttribute("aria-label", `${count} ${count === 1 ? "item" : "items"}`);
  }

  async function refreshCartBadge(cart) {
    try {
      const current = cart && Number.isFinite(Number(cart.item_count)) ? cart : await fetchCartState();
      state.cartSnapshot = current;
      updateCartBadge(current);
      return current;
    } catch (error) {
      console.warn(`[ELOOKUS V2] Cart count refresh failed: ${error.message}`);
      return null;
    }
  }

  function formatMoney(cents, currency = "USD") {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return "—";
    try { return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount / 100); }
    catch (error) { return `$${(amount / 100).toFixed(2)}`; }
  }

  function cartProperties(item) {
    if (Array.isArray(item?.properties)) return item.properties.map((entry) => [String(entry?.name || ""), entry?.value]);
    return Object.entries(item?.properties || {});
  }

  function propertyValue(item, exactKey) {
    const match = cartProperties(item).find(([key]) => key === exactKey);
    return match ? String(match[1] ?? "").trim() : "";
  }

  function customerProperties(item) {
    return cartProperties(item).filter(([key, value]) => key && !key.startsWith("_") && value !== null && value !== undefined && String(value).trim() !== "");
  }

  function normalizedPropertyKey(key) {
    return norm(key).replace(/^elookus\s+/, "").replace(/[^a-z0-9]+/g, " ").trim();
  }

  function findCustomerProperty(item, patterns) {
    const entry = customerProperties(item).find(([key]) => patterns.some((pattern) => pattern.test(normalizedPropertyKey(key))));
    return entry ? String(entry[1]).trim() : "";
  }

  function isMultiPairDiscountTitle(value) {
    const title = norm(value);
    return title.includes("multi-pair") || title.includes("multi pair") || title.includes("every 2nd frame");
  }

  function itemDiscountAllocations(item) {
    if (Array.isArray(item?.line_level_discount_allocations) && item.line_level_discount_allocations.length) {
      return item.line_level_discount_allocations.map((allocation) => ({
        amount: Number(allocation?.amount) || 0,
        title: allocation?.discount_application?.title || allocation?.title || "",
      }));
    }
    return Array.isArray(item?.discounts) ? item.discounts.map((discount) => ({
      amount: Number(discount?.amount) || 0,
      title: discount?.title || "",
    })) : [];
  }

  function itemMultiPairSavings(item) {
    return itemDiscountAllocations(item).reduce((total, allocation) => (
      isMultiPairDiscountTitle(allocation.title) ? total + allocation.amount : total
    ), 0);
  }

  async function variantBasePrice(variantId, handle) {
    const key = String(variantId || "");
    if (!key) return null;
    if (state.productPriceCache.has(key)) return state.productPriceCache.get(key);
    const local = window.ShopifyAnalytics?.meta?.product?.variants?.find((variant) => String(variant.id) === key)?.price;
    if (Number.isFinite(Number(local))) {
      const price = Number(local);
      state.productPriceCache.set(key, price);
      return price;
    }
    if (!handle) return null;
    const request = window.fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: "same-origin", cache: "force-cache", headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed (${response.status})`);
        return response.json();
      })
      .then((product) => {
        const price = Number(product?.variants?.find((variant) => String(variant.id) === key)?.price);
        return Number.isFinite(price) ? price : null;
      })
      .catch(() => null);
    state.productPriceCache.set(key, request);
    const price = await request;
    state.productPriceCache.set(key, price);
    return price;
  }

  function compactLensDescription(value) {
    const text = englishDisplayText(value);
    const parts = text.split(" · ").map((part) => part.trim()).filter(Boolean);
    const solutionAt = parts.findIndex((part) => /\b1\.(?:61|67|74)\b/.test(part));
    if (solutionAt < 1) return text;
    const index = parts[solutionAt].match(/\b1\.(?:61|67|74)\b/)?.[0];
    return index ? [...parts.slice(0, solutionAt), index].join(" · ") : text;
  }

  function lensDescription(item) {
    const summary = findCustomerProperty(item, [/^lens$/]);
    if (summary) return compactLensDescription(summary);
    const privateName = propertyValue(item, "_elookus_lens_name");
    if (privateName) return compactLensDescription(privateName);
    const type = findCustomerProperty(item, [/^lens type$/, /^lens function$/, /basic reading glasses lens function/]);
    const solution = findCustomerProperty(item, [/lens solution(?! id)/, /lens index$/, /clear lens index/, /blue light.*index/, /photochromic.*index/, /tinted.*index/]);
    return compactLensDescription([type, solution].filter(Boolean).filter((entry, index, values) => values.indexOf(entry) === index).join(" · ")) || "No lens upgrade";
  }

  function prescriptionRows(item) {
    const get = (...patterns) => findCustomerProperty(item, patterns);
    const rows = [];
    const summary = get(/^prescription$/);
    const methodSummary = get(/^prescription method$/, /^method$/);
    const pdSummary = get(/^pd$/);
    if (summary) rows.push(["Prescription", englishDisplayText(summary)]);
    if (methodSummary) rows.push(["Method", englishDisplayText(methodSummary)]);
    if (pdSummary) rows.push(["PD", englishDisplayText(pdSummary)]);
    if (rows.length) return rows;
    const type = get(/prescription type/) || (get(/readers power/) ? `Readers ${get(/readers power/)}` : "");
    const method = get(/prescription method/);
    if (type) rows.push(["Type", englishDisplayText(type)]);
    if (method) rows.push(["Method", englishDisplayText(method)]);
    [["OD SPH", /^od sph$/], ["OD CYL", /^od cyl$/], ["OD AXIS", /^od axis$/], ["OS SPH", /^os sph$/], ["OS CYL", /^os cyl$/], ["OS AXIS", /^os axis$/]].forEach(([label, pattern]) => {
      const value = get(pattern);
      if (value) rows.push([label, englishDisplayText(value)]);
    });
    const total = get(/^total pd$/);
    const od = get(/^od pd$/), os = get(/^os pd$/);
    if (total) rows.push(["PD", `Total ${englishDisplayText(total)}`]);
    else if (od || os) rows.push(["PD", [od && `OD ${englishDisplayText(od)}`, os && `OS ${englishDisplayText(os)}`].filter(Boolean).join(" · ")]);
    return rows;
  }

  async function cartItemPricing(item) {
    const quantity = Math.max(1, Number(item.quantity) || 1);
    const privateFrameDollarsValue = propertyValue(item, "_elookus_frame_discount_price");
    const privateFrameValue = propertyValue(item, "_elookus_frame_unit_price");
    const privateLensValue = propertyValue(item, "_elookus_lens_unit_price");
    const privateFrameDollars = privateFrameDollarsValue === "" ? null : Math.round(Number(privateFrameDollarsValue) * 100);
    const privateFrame = privateFrameValue === "" ? null : Number(privateFrameValue);
    const privateLens = privateLensValue === "" ? null : Number(privateLensValue);
    const frameUnit = Number.isFinite(privateFrameDollars) && privateFrameDollars >= 0
      ? privateFrameDollars
      : Number.isFinite(privateFrame) && privateFrame >= 0
        ? privateFrame
        : await variantBasePrice(item.variant_id || item.id, item.handle);
    const candidates = [item.original_price, item.price, item.final_price, Number(item.original_line_price) / quantity, Number(item.line_price) / quantity, Number(item.final_line_price) / quantity]
      .map(Number).filter((value) => Number.isFinite(value) && value >= 0);
    const undiscountedUnit = candidates.length ? Math.max(...candidates) : null;
    const lensUnit = Number.isFinite(privateLens) && privateLens >= 0 ? privateLens : Number.isFinite(frameUnit) && Number.isFinite(undiscountedUnit) ? Math.max(0, Math.round(undiscountedUnit - frameUnit)) : null;
    const itemTotal = Number.isFinite(Number(item.final_line_price)) ? Number(item.final_line_price) : Number(item.line_price);
    const multiPairSavings = itemMultiPairSavings(item);
    const discountQuantity = Math.max(0, Math.min(quantity, Math.floor(Number(propertyValue(item, "_elookus_multipair_discount_qty")) || 0)));
    return { quantity, frameUnit, lensUnit, itemTotal, multiPairSavings, discountQuantity };
  }

  function variantLabel(item) {
    const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    const color = options.find((option) => /color|colour/i.test(option.name || ""))?.value;
    if (color) return color;
    return item.variant_title && !/default title/i.test(item.variant_title) ? item.variant_title : "";
  }

  function cartImageUrl(item) {
    const source = item.featured_image?.url || item.image || "";
    if (!source) return "";
    try {
      const url = new URL(source, location.origin);
      if (/\.shopify\.com$/i.test(url.hostname) || /cdn\.shopify\.com$/i.test(url.hostname)) url.searchParams.set("width", "176");
      return url.toString();
    } catch (error) {
      return source;
    }
  }

  function cartOrderSummary(cart) {
    const total = Math.max(0, Number(cart?.total_price) || 0);
    const merchandise = Math.max(total, Number(cart?.original_total_price) || Number(cart?.items_subtotal_price) || total);
    const multiPairDiscount = (cart?.items || []).reduce((sum, item) => sum + itemMultiPairSavings(item), 0);
    const orderDiscount = (cart?.cart_level_discount_applications || []).reduce((sum, application) => (
      sum + (Number(application?.total_allocated_amount) || 0)
    ), 0);
    const shippingQualifyingTotal = total + orderDiscount;
    const freeShipping = shippingQualifyingTotal >= FREE_SHIPPING_THRESHOLD_CENTS;
    const estimatedShipping = cart?.item_count === 0 || freeShipping ? 0 : STANDARD_SHIPPING_CENTS;
    return {
      merchandise,
      multiPairDiscount,
      orderDiscount,
      totalSavings: Math.max(0, merchandise - total),
      total,
      shippingQualifyingTotal,
      freeShipping,
      estimatedShipping,
      estimatedTotal: total + estimatedShipping,
      shippingDifference: Math.max(0, FREE_SHIPPING_THRESHOLD_CENTS - shippingQualifyingTotal),
    };
  }

  function renderCartOrderSummary(cart) {
    const summary = cartOrderSummary(cart);
    const currency = cart.currency || "USD";
    const shipping = state.overlay.querySelector("[data-elv2-cart-shipping]");
    const host = state.overlay.querySelector("[data-elv2-cart-summary]");
    const shippingProgress = Math.min(1, summary.shippingQualifyingTotal / FREE_SHIPPING_THRESHOLD_CENTS);
    shipping.dataset.freeShippingProgress = summary.freeShipping ? "1" : String(shippingProgress);
    shipping.style.setProperty("--free-shipping-progress", String(shippingProgress));
    shipping.querySelector("[data-elv2-cart-shipping-not-met]").textContent = `Add ${formatMoney(summary.shippingDifference, currency)} more for FREE SHIPPING`;
    shipping.querySelector("[role='progressbar']").setAttribute("aria-valuenow", String(Math.round(shippingProgress * 100)));
    const row = (label, value, className = "") => `<div${className ? ` class="${className}"` : ""}><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`;
    host.innerHTML = [
      row("Merchandise", formatMoney(summary.merchandise, currency)),
      summary.multiPairDiscount > 0 ? row("Multi-Pair Discount", `−${formatMoney(summary.multiPairDiscount, currency)}`) : "",
      summary.orderDiscount > 0 ? row("Order Discount", `−${formatMoney(summary.orderDiscount, currency)}`) : "",
      row("Subtotal (after discounts)", formatMoney(summary.total, currency)),
      row("Estimated shipping", summary.freeShipping ? "FREE" : formatMoney(summary.estimatedShipping, currency)),
      row("Total Savings", formatMoney(summary.totalSavings, currency), "elv2-cart-summary__savings"),
      row("Estimated total", formatMoney(summary.estimatedTotal, currency), "elv2-cart-summary__total"),
    ].join("");
  }

  async function renderCartDrawer(cart, requestId) {
    const host = state.overlay.querySelector("[data-elv2-cart-items]");
    const footer = state.overlay.querySelector("[data-elv2-cart-footer]");
    const status = state.overlay.querySelector("#elv2-cart-status");
    state.cartSnapshot = cart;
    updateCartBadge(cart);
    if (!cart.items?.length) {
      host.innerHTML = `<div class="elv2-cart-empty"><strong>Your cart is empty</strong></div>`;
      renderCartOrderSummary(cart);
      footer.hidden = false;
      status.textContent = "Cart loaded. Your cart is empty.";
      return;
    }
    const prices = await Promise.all(cart.items.map(cartItemPricing));
    if (requestId !== state.cartRequest || !state.cartOpen) return;
    host.innerHTML = cart.items.map((item, index) => {
      const price = prices[index];
      const image = cartImageUrl(item);
      const rxRows = prescriptionRows(item);
      const frameTotal = Number.isFinite(price.frameUnit) ? price.frameUnit * price.quantity : null;
      const lensTotal = Number.isFinite(price.lensUnit) ? price.lensUnit * price.quantity : null;
      const discountedFrameTotal = Number.isFinite(frameTotal) ? Math.max(0, frameTotal - price.multiPairSavings) : null;
      const equation = (unit, total) => price.quantity > 1
        ? `${formatMoney(unit, cart.currency)} × ${price.quantity} = <strong>${formatMoney(total, cart.currency)}</strong>`
        : `<strong>${formatMoney(unit, cart.currency)}</strong>`;
      const frameValue = Number.isFinite(price.frameUnit)
        ? price.quantity === 1 && price.discountQuantity > 0 && price.multiPairSavings > 0
          ? `<s>${formatMoney(price.frameUnit, cart.currency)}</s> <strong>${formatMoney(discountedFrameTotal, cart.currency)}</strong>`
          : equation(price.frameUnit, frameTotal)
        : "Included in item total";
      const lensRow = Number.isFinite(price.lensUnit) && price.lensUnit > 0
        ? `<div><dt>Lens<em>${escapeHtml(lensDescription(item))}</em></dt><dd>${equation(price.lensUnit, lensTotal)}</dd></div>`
        : "";
      const savingsRow = price.discountQuantity > 0 && price.multiPairSavings > 0
        ? `<div class="elv2-cart-prices__saving"><dt>Multi-Pair Savings${price.quantity > 1 ? `<em>${price.discountQuantity} of ${price.quantity} frames discounted</em>` : ""}</dt><dd>−${formatMoney(price.multiPairSavings, cart.currency)}</dd></div>`
        : "";
      return `<article class="elv2-cart-item">
        <div class="elv2-cart-item__top">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" decoding="async" width="88" height="88">` : `<div class="elv2-cart-item__placeholder" aria-hidden="true"></div>`}<div><h3>${escapeHtml(item.product_title || item.title)}</h3>${variantLabel(item) ? `<p>${escapeHtml(variantLabel(item))}</p>` : ""}<div class="elv2-cart-item__controls"><button type="button" data-elv2-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${Math.max(0, price.quantity - 1)}" aria-label="Decrease quantity">−</button><span aria-label="Quantity ${price.quantity}">${price.quantity}</span><button type="button" data-elv2-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${price.quantity + 1}" aria-label="Increase quantity">+</button><button type="button" class="elv2-cart-item__remove" data-elv2-cart-change data-key="${escapeHtml(item.key)}" data-quantity="0">Remove</button></div></div></div>
        <dl class="elv2-cart-prices">
          <div><dt>Frame</dt><dd>${frameValue}</dd></div>
          ${lensRow}
          ${savingsRow}
          <div class="elv2-cart-prices__total"><dt>Subtotal</dt><dd>${escapeHtml(formatMoney(price.itemTotal, cart.currency))}</dd></div>
        </dl>
        <details class="elv2-cart-rx"><summary>Prescription details</summary>${rxRows.length ? `<dl>${rxRows.map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join("")}</dl>` : `<p>No prescription details for this item.</p>`}</details>
      </article>`;
    }).join("");
    renderCartOrderSummary(cart);
    footer.hidden = false;
    status.textContent = `Cart updated. ${cart.item_count} ${cart.item_count === 1 ? "item" : "items"}.`;
    state.overlay.querySelector(".elv2-cart-drawer__close")?.focus({ preventScroll: true });
  }

  async function handleCartDrawerAction(event) {
    const button = event.target.closest("[data-elv2-cart-change]");
    if (!button || button.disabled) return;
    const key = button.dataset.key;
    const quantity = Math.max(0, Math.floor(Number(button.dataset.quantity) || 0));
    if (!key) return;
    const host = state.overlay.querySelector("[data-elv2-cart-items]");
    const status = state.overlay.querySelector("#elv2-cart-status");
    button.disabled = true;
    host.setAttribute("aria-busy", "true");
    status.textContent = "Updating cart.";
    try {
      const response = await window.fetch("/cart/change.js", {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
      let cart = await response.json();
      const synced = await window.ElookusMultiPairCartSync?.sync(cart);
      if (synced?.cart) cart = synced.cart;
      await renderCartDrawer(cart, state.cartRequest);
    } catch (error) {
      status.textContent = "Unable to update the cart. Please try again.";
      console.warn(`[ELOOKUS V2] ${error.message}`);
    } finally {
      host.removeAttribute("aria-busy");
      if (button.isConnected) button.disabled = false;
    }
  }

  async function openCartDrawer(trigger, suppliedCart) {
    const layer = state.overlay.querySelector("[data-elv2-cart-layer]");
    const status = state.overlay.querySelector("#elv2-cart-status");
    const host = state.overlay.querySelector("[data-elv2-cart-items]");
    closeNativeCartDrawer();
    state.cartTrigger = trigger || document.activeElement;
    state.drawerReturnToFlow = state.open;
    state.cartOpen = true;
    state.overlay.hidden = false;
    document.documentElement.classList.add("elv2-page-locked");
    state.overlay.querySelector(".elv2-app").setAttribute("inert", "");
    suspendPortals();
    layer.hidden = false;
    host.innerHTML = `<div class="elv2-cart-loading">Loading cart…</div>`;
    state.overlay.querySelector("[data-elv2-cart-footer]").hidden = true;
    status.textContent = "Loading cart.";
    layer.querySelector(".elv2-cart-drawer__close")?.focus({ preventScroll: true });
    const requestId = ++state.cartRequest;
    try {
      const cart = suppliedCart || await fetchCartState();
      if (requestId !== state.cartRequest || !state.cartOpen) return;
      await renderCartDrawer(cart, requestId);
    } catch (error) {
      if (requestId !== state.cartRequest || !state.cartOpen) return;
      host.innerHTML = `<div class="elv2-cart-empty"><strong>Unable to load cart</strong><span>Close the cart and try again.</span></div>`;
      status.textContent = "Unable to load cart.";
      console.warn(`[ELOOKUS V2] Cart drawer failed: ${error.message}`);
    }
  }

  function closeCartDrawer() {
    if (!state.cartOpen) return;
    const returnTarget = state.cartTrigger;
    state.cartRequest += 1;
    state.cartOpen = false;
    closeNativeCartDrawer();
    state.overlay.querySelector("[data-elv2-cart-layer]").hidden = true;
    state.overlay.querySelector(".elv2-app").removeAttribute("inert");
    resumePortals();
    if (!state.drawerReturnToFlow) {
      state.overlay.hidden = true;
      document.documentElement.classList.remove("elv2-page-locked");
    }
    state.cartTrigger = null;
    window.requestAnimationFrame(() => returnTarget?.isConnected && returnTarget.focus({ preventScroll: true }));
  }

  function handleCartKeydown(event) {
    if (!state.cartOpen) return;
    if (event.key === "Escape") { event.preventDefault(); closeCartDrawer(); return; }
    if (event.key !== "Tab") return;
    const drawer = state.overlay.querySelector(".elv2-cart-drawer");
    const focusable = Array.from(drawer.querySelectorAll('a[href],button:not([disabled]),details > summary,[tabindex]:not([tabindex="-1"])')).filter((element) => element.getClientRects().length);
    if (!focusable.length) { event.preventDefault(); drawer.focus(); return; }
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function setDisplayProperty(form, key, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `properties[${key}]`;
    input.value = String(value);
    input.dataset.elv2CartMeta = "true";
    form.append(input);
  }

  function createPairId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint32Array(2);
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
    else {
      bytes[0] = Math.floor(Math.random() * 0xffffffff);
      bytes[1] = Math.floor(Math.random() * 0xffffffff);
    }
    return `pair-${Date.now().toString(36)}-${bytes[0].toString(36)}${bytes[1].toString(36)}`;
  }

  function forceCartQuantity(form) {
    let input = form.querySelector('[name="quantity"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "quantity";
      input.dataset.elv2CartMeta = "true";
      form.append(input);
    }
    input.value = "1";
    input.setAttribute("value", "1");
  }

  const CUSTOMER_CART_PROPERTIES = new Set(["Prescription", "Prescription Method", "Lens", "PD"]);

  function cartPropertyKey(fieldName) {
    return String(fieldName || "").match(/^properties\[(.*)]$/)?.[1] || "";
  }

  function isTechnicalCartProperty(key) {
    const value = String(key || "").trim();
    if (!value || value.startsWith("_") || CUSTOMER_CART_PROPERTIES.has(value)) return false;
    return /^elookus\s+/i.test(value)
      || /purchase mode/i.test(value)
      || /prescription/i.test(value)
      || /^method$/i.test(value)
      || /^(?:od|os)\s+/i.test(value)
      || /^(?:pd mode|total pd|od pd|os pd|readers power)$/i.test(value)
      || /lens (?:type|function|solution|index)/i.test(value)
      || /(?:clear|blue light filtering|photochromic|tinted).*lens index/i.test(value);
  }

  function privatizeTechnicalCartProperties(formData) {
    const technical = new Map();
    Array.from(formData.entries()).forEach(([fieldName, value]) => {
      const key = cartPropertyKey(fieldName);
      if (!isTechnicalCartProperty(key)) return;
      technical.set(key, value);
    });
    technical.forEach((value, key) => {
      formData.delete(`properties[${key}]`);
      formData.set(`properties[_${key}]`, value);
    });
    CUSTOMER_CART_PROPERTIES.forEach((key) => {
      const fieldName = `properties[${key}]`;
      const values = formData.getAll(fieldName);
      if (!values.length) return;
      const summary = values[values.length - 1];
      formData.delete(fieldName);
      if (String(summary).trim()) formData.set(fieldName, summary);
    });
  }

  function enforceCartRequest(form, pairId, uploadUrl, rxSnapshot = null, rxCurrent = () => true) {
    const apply = (event) => {
      if (!rxCurrent()) {
        // formdata is not cancelable. Remove every payload field so an unverified
        // delayed native serialization cannot send an old prescription/product.
        Array.from(event.formData.keys()).forEach(key => event.formData.delete(key));
        showError("Prescription changed before serialization. Please review your selection and try again.");
        return;
      }
      privatizeTechnicalCartProperties(event.formData);
      if (rxSnapshot) Object.entries(PositiveRxCore.proposedProperties(rxSnapshot,rawRxRevision)).forEach(([key,value]) => event.formData.set(`properties[${key}]`,value));
      event.formData.delete("properties[_ELOOKUS Prescription Upload]");
      if (uploadUrl) event.formData.set("properties[_ELOOKUS Prescription Upload]", uploadUrl);
      event.formData.set("properties[_elookus_pair_id]", pairId);
      event.formData.set("quantity", "1");
    };
    form.addEventListener("formdata", apply);
    return () => form.removeEventListener("formdata", apply);
  }

  function displaySummaries(frameOnly) {
    if (frameOnly) return { Prescription: "Frame only", "Prescription Method": "", Lens: "No prescription lens", PD: "" };
    const type = controlText(checkedControl("type"));
    const method = selectedType() === "readers" ? "" : controlText(checkedControl("method"));
    const functionKey = selectedType() === "readers" ? "function_readers" : "function_prescription";
    const functionText = controlText(checkedControl(functionKey));
    const solutionKey = selectedSolutionKey();
    const solutionText = solutionKey ? controlText(checkedControl(solutionKey)) : "";
    let pd = "";
    if (selectedMethod() === "manual") {
      pd = selectedPdMode() === "total" ? `Total ${valueText("total_pd")}` : [valueText("od_pd") && `OD ${valueText("od_pd")}`, valueText("os_pd") && `OS ${valueText("os_pd")}`].filter(Boolean).join(" · ");
    }
    return {
      Prescription: englishDisplayText(type),
      "Prescription Method": englishDisplayText(method),
      Lens: compactLensDescription([functionText, solutionText].map(englishDisplayText).filter(Boolean).join(" · ")),
      PD: pd,
    };
  }

  async function prepareCartDisplayMetadata(frameOnly, pairId, rxSnapshot = null, rxCurrent = () => true) {
    let context = cartRequestContext();
    if (!context) return null;
    const variantId = context.form.querySelector('[name="id"]')?.value;
    const handle = window.ShopifyAnalytics?.meta?.product?.handle || location.pathname.match(/\/products\/([^/?#]+)/)?.[1];
    const frameUnit = await variantBasePrice(variantId, handle);
    if (!rxCurrent()) throw new Error("Prescription changed while loading price");
    context = cartRequestContext();
    if (!context) return null;
    const { form } = context;
    form.querySelectorAll("[data-elv2-cart-meta]").forEach((input) => input.remove());
    forceCartQuantity(form);
    setDisplayProperty(form, "_elookus_pair_id", pairId);
    const solutionKey = selectedSolutionKey();
    const solutionControl = solutionKey ? checkedControl(solutionKey) : null;
    const lensUnit = frameOnly ? 0 : Math.round(Number(solutionControl?.dataset.addonPrice) * 100);
    const summaries = displaySummaries(frameOnly);
    const lensName = frameOnly ? "Frame only" : summaries.Lens;
    if (Number.isFinite(frameUnit)) {
      setDisplayProperty(form, "_elookus_frame_unit_price", Math.round(frameUnit));
      setDisplayProperty(form, "_elookus_frame_discount_price", (frameUnit / 100).toFixed(2));
      setDisplayProperty(form, "_elookus_multipair_discount_qty", "0");
      setDisplayProperty(form, "_elookus_multipair_discount_base", "0.00");
    }
    if (Number.isFinite(lensUnit)) setDisplayProperty(form, "_elookus_lens_unit_price", lensUnit);
    if (lensName) setDisplayProperty(form, "_elookus_lens_name", lensName);
    setDisplayProperty(form, "_elookus_display_price_source", "variant-base-plus-globo-addon");
    Object.entries(summaries).forEach(([key, value]) => { if (value) setDisplayProperty(form, key, value); });
    if (rxSnapshot) Object.entries(PositiveRxCore.proposedProperties(rxSnapshot,rawRxRevision)).forEach(([key,value]) => setDisplayProperty(form,key,value));
    return context;
  }

  function handleNativeCartDrawerOpen(event) {
    if (!state.nativeCartSuppression) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function closeNativeCartDrawer() {
    document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:close"));
  }

  function beginNativeCartSuppression() {
    window.clearTimeout(state.nativeCartSuppressionTimer);
    state.nativeCartSuppression = true;
  }

  function finishNativeCartSuppression(delay = 0) {
    window.clearTimeout(state.nativeCartSuppressionTimer);
    state.nativeCartSuppressionTimer = window.setTimeout(() => { state.nativeCartSuppression = false; }, delay);
  }

  function handleThemeCartUpdate(event) {
    const cart = event.detail?.cart || event.detail;
    if (cart && Number.isFinite(Number(cart.item_count))) {
      state.cartSnapshot = cart;
      updateCartBadge(cart);
      const pending = state.cartConfirmation;
      if (pending && pending.token === state.cartToken && Number(cart.item_count) > pending.beforeCount) pending.resolve(cart);
      return;
    }
    refreshCartBadge();
  }

  async function waitForCartIncrease(beforeCount, token, eventCartPromise) {
    const delays = [250, 450, 700, 1000];
    for (const delay of delays) {
      const fromEvent = await Promise.race([eventCartPromise, new Promise((resolve) => window.setTimeout(() => resolve(null), delay))]);
      if (fromEvent) return fromEvent;
      if (token !== state.cartToken) return null;
      const nativeError = nativeCartErrorText();
      if (nativeError) throw new Error(nativeError);
      const cart = await fetchCartState();
      state.cartSnapshot = cart;
      if (Number(cart.item_count) > beforeCount) return cart;
    }
    throw new Error("Cart count did not increase before timeout");
  }

  function nativeCartErrorText() {
    const selectors = ".product-form__error-message,[data-product-form-error],[data-cart-error]";
    const error = Array.from(document.querySelectorAll(selectors)).find((element) => !state.overlay.contains(element) && element.getClientRects().length && norm(element.textContent));
    return error?.textContent?.replace(/\s+/g, " ").trim() || "";
  }

  async function addSelectionToCart(frameOnly = false) {
    if (!frameOnly && blockPositiveRxOrder()) return;
    if (!frameOnly && rawRx && (rawRxValidation() || !writeRawRxCanonical())) return showError(rawRxValidation() || "Prescription synchronization is not ready. Please review both eyes.");
    if (!frameOnly && manualLensRoute()) { const key=selectedSolutionKey(); if (!rawRxCandidate(checkedControl(key),state.fields.get(key)?.family)) return showError("Choose a lens that fits both equivalent prescriptions."); }
    const rxStamp=rawRxRequestStamp(), eligibilityStamp=lensEligibilityStamp();
    const rxSnapshot=!frameOnly && rawRx ? rawRxSnapshot() : null;
    const rxCurrent=() => frameOnly || (rxStamp===rawRxRequestStamp() && eligibilityStamp===lensEligibilityStamp() && !positiveRxOrderBlocked() && (!manualLensRoute() || rawRxCandidate(checkedControl(selectedSolutionKey()),state.fields.get(selectedSolutionKey())?.family)));
    if (window.ElookusAvailability && !window.ElookusAvailability.canPurchase()) return;
    window.ElookusAvailability?.clearError();
    if (state.cartStatus === "pending" || state.cartStatus === "success") return;
    const pairId = createPairId();
    const token = ++state.cartToken;
    const analyticsContext = behaviorCartContext(frameOnly);
    const analyticsHandle = analyticsContext?.handle;
    const analyticsFields = analyticsContext?.fields;
    const analyticsStarted = Date.now();
    let analyticsSettled = false;
    function analyticsOutcome(success) {
      if (!analyticsHandle || analyticsSettled) return;
      analyticsSettled = true;
      behaviorCall("detail", analyticsHandle, success ? "configurator_cart_succeeded" : "configurator_cart_failed", {
        ...analyticsFields, elapsed_ms: Math.max(0, Math.min(3600000, Date.now() - analyticsStarted)),
        ...(success ? {} : { failure_code: "cart_unconfirmed" })
      });
    }
    if (analyticsHandle) behaviorPending.add(analyticsHandle);
    if (!frameOnly) behaviorDetail("configurator_cart_requested");
    setCartStatus("pending");
    let before;
    let stopEnforcingRequest = () => {};
    try {
      before = state.cartSnapshot || await fetchCartState();
      if (token !== state.cartToken) return;
      if (!rxCurrent()) throw new Error("Prescription changed during cart preparation");
      const request = await prepareCartDisplayMetadata(frameOnly, pairId, rxSnapshot, rxCurrent);
      if (!request) throw new Error("Native cart form unavailable");
      if (token !== state.cartToken) return;
      const needsUpload = !frameOnly && selectedMethod() === "upload";
      const uploadUrl = needsUpload ? uploadReference(controls("upload").find((control) => control.type === "file")) : "";
      if (needsUpload && !uploadUrl) throw new Error("Prescription upload is not complete");
      if (!rxCurrent()) throw new Error("Prescription changed during cart preparation");
      stopEnforcingRequest = enforceCartRequest(request.form, pairId, uploadUrl, rxSnapshot, rxCurrent);
      let resolveEventCart;
      const eventCartPromise = new Promise((resolve) => { resolveEventCart = resolve; });
      state.cartConfirmation = { token, beforeCount: Number(before.item_count), resolve: resolveEventCart };
      beginNativeCartSuppression();
      if (!rxCurrent()) throw new Error("Prescription changed before cart handoff");
      if (rxSnapshot) { const key=selectedSolutionKey(); if (!rawRxCandidate(checkedControl(key),state.fields.get(key)?.family) || !writeRawRxCanonical()) throw new Error("Prescription or lens availability changed before handoff"); }
      if (!clickNativeCart(request.action)) throw new Error("Native cart action unavailable");
      let cart = await waitForCartIncrease(Number(before.item_count), token, eventCartPromise);
      if (!cart || token !== state.cartToken) return;
      try {
        const synced = await window.ElookusMultiPairCartSync?.sync(cart);
        if (synced?.cart) cart = synced.cart;
      } catch (error) {
        console.warn(`[ELOOKUS V2] Multi-Pair cart properties will retry: ${error.message}`);
        window.ElookusMultiPairCartSync?.schedule();
      }
      setCartStatus("success");
      analyticsOutcome(behaviorCartMatches(cart, pairId));
      updateCartBadge(cart);
      closeNativeCartDrawer();
      const trigger = frameOnly ? state.entry.querySelector("[data-elv2-frame-cart]") : state.overlay.querySelector("[data-elv2-next]");
      openCartDrawer(trigger, cart);
      finishNativeCartSuppression(1000);
    } catch (error) {
      if (token !== state.cartToken) return;
      console.warn(`[ELOOKUS V2] Add to cart was not confirmed: ${error.message}`);
      closeNativeCartDrawer();
      finishNativeCartSuppression();
      setCartStatus("error", true);
      analyticsOutcome(false);
      if (frameOnly) window.ElookusAvailability?.showFrameError(nativeCartErrorText());
    } finally {
      analyticsOutcome(false);
      behaviorPending.delete(analyticsHandle);
      if (analyticsHandle !== behaviorFlow) behaviorCall("end", analyticsHandle);
      stopEnforcingRequest();
      if (state.cartConfirmation?.token === token) state.cartConfirmation = null;
    }
  }

  function addReviewToCart() { addSelectionToCart(false); }

  function submitFrameOnly() {
    if (window.ElookusAvailability && !window.ElookusAvailability.canPurchase()) return;
    state.fields.forEach((field, key) => { if (!key.startsWith("purchase") && key !== "frame_notice") clearField(key); });
    addSelectionToCart(true);
  }

  function openConfirmation() {
    const modal = state.overlay.querySelector(".elv2-confirm");
    suspendPortals();
    const table = modal.querySelector(".elv2-confirm__table");
    const cell = (value, head = false) => `<${head ? "strong" : "span"}>${escapeHtml(value)}</${head ? "strong" : "span"}>`;
    table.innerHTML = [
      ["Eye", "SPH", "CYL", "AXIS"],
      ["OD / Right Eye", valueText("od_sph"), valueText("od_cyl"), numericValue("od_cyl") === 0 ? "None" : valueText("od_axis")],
      ["OS / Left Eye", valueText("os_sph"), valueText("os_cyl"), numericValue("os_cyl") === 0 ? "None" : valueText("os_axis")],
    ].map((row, index) => row.map((value) => cell(value, index === 0)).join("")).join("");
    const pd = selectedPdMode() === "total" ? `Total PD: ${valueText("total_pd")}` : `OD PD: ${valueText("od_pd")} · OS PD: ${valueText("os_pd")}`;
    modal.querySelector(".elv2-confirm__pd").textContent = pd + (rawRx ? " · " + rawRxEquivalentText() : "");
    modal.hidden = false;
    modal.querySelector("[data-elv2-edit]").focus();
  }

  function closeConfirmation() {
    state.overlay.querySelector(".elv2-confirm").hidden = true;
    resumePortals();
  }
  function confirmPrescription() { state.confirmedRx = true; closeConfirmation(); advance(); }

  function goBack() {
    if (uploadBusy()) return showError("Wait for the upload to finish before going back.", fieldGroup("upload"));
    if (state.cartStatus === "pending") return;
    behaviorDetail("configurator_back");
    releasePortalInteraction();
    const ids = route();
    const index = ids.indexOf(state.current);
    if (index > 0) { state.current = ids[index - 1]; render(); }
    else closeOverlay();
  }

  function advance() {
    behaviorDetail("configurator_next");
    releasePortalInteraction();
    const ids = route();
    const index = ids.indexOf(state.current);
    if (index < ids.length - 1) state.current = ids[index + 1];
    render();
    state.overlay.querySelector(".elv2-main").scrollTo({ top: 0, behavior: "smooth" });
  }

  function goNext() {
    if (state.current === "review") { addReviewToCart(); return; }
    if (!validateStep()) return;
    if (state.current === "details" && selectedMethod() === "manual" && !state.confirmedRx) { openConfirmation(); return; }
    advance();
  }

  function solutionFor(control, family) {
    const text = norm(controlText(control));
    const index = (text.match(/1\.(?:61|67|74)/) || [])[0];
    if (!index) return null;
    if (/high astigmatism|高散光/.test(text)) return SOLUTIONS.find((item) => item.id === "1_61_high_astigmatism_blue_light");
    return SOLUTIONS.find((item) => item.family === family && item.index === index && !item.automatic) || null;
  }

  function selectedSolutionKey() {
    const functionKey = selectedType() === "readers" ? "function_readers" : "function_prescription";
    const text = norm(controlText(checkedControl(functionKey)));
    const family = /blue light|防蓝光/.test(text) ? "blue"
      : /photochromic|变色/.test(text) ? "photo"
        : /tinted|染色/.test(text) ? "tinted"
          : /clear lenses|普通透明/.test(text) ? "clear"
            : null;
    if (!family) return null;
    return selectedType() === "readers" ? `solution_readers_${family}` : `solution_${family}`;
  }

  function choiceContainer(control) { return control.closest("label,.gpo-choicelist__item,.gpo-radio,[class*='choice-list'] > *,[class*='radio-list'] > *") || control.parentElement; }

  function suppressNativeChoiceText(control) {
    const label = (control.id && document.querySelector(`label[for="${CSS.escape(control.id)}"]`)) || control.closest("label");
    if (!label) return;
    const erase = (node) => Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) child.textContent = "";
      else if (child.nodeType === Node.ELEMENT_NODE && child !== control && !child.classList?.contains("elv2-choice-copy")) erase(child);
    });
    erase(label);
  }

  function lensThicknessIllustration(solution) {
    const outlines = [
      ["1.61", "M12 12 Q32 48 12 84 L21 84 Q49 48 21 12 Z"],
      ["1.67", "M43 12 Q61 48 43 84 L49 84 Q73 48 49 12 Z"],
      ["1.74", "M74 12 Q90 48 74 84 L78 84 Q98 48 78 12 Z"]
    ];
    return `<div class="elv2-lens-illustration" aria-hidden="true"><svg viewBox="0 0 108 96" focusable="false" xmlns="http://www.w3.org/2000/svg">${outlines.map(([index, path]) => `<path d="${path}" class="${index === solution.index ? "is-current" : ""}"/>`).join("")}</svg></div>`;
  }

  function lensCardFeatures(solution, readersCard = false) {
    const supportedFeatures = ["clear", "blue_light_filtering", "photochromic"].includes(solution.family);
    const profile = (supportedFeatures ? {
      "1.61": "Thin 1.61",
      "1.67": "Super Thin 1.67",
      "1.74": "Ultra Thin 1.74"
    } : {
      "1.61": "Thin · Balanced value",
      "1.67": "Thinner · Slimmer profile",
      "1.74": "Ultra thin · Further thickness reduction"
    })[solution.index] || "";
    const weight = supportedFeatures ? {
      "1.61": "Relatively Light", "1.67": "Super Light", "1.74": "Ultra Light"
    }[solution.index] || "" : "";
    const purpose = {
      clear: readersCard ? "Clear lenses · No coloured tint" : "",
      blue_light_filtering: "Filters some blue-violet light",
      photochromic: "Darkens outdoors · Lightens indoors",
      tinted_prescription: "Consistent tint for outdoor use"
    }[solution.family] || "";
    const coatings = ["clear", "blue_light_filtering", "photochromic"].includes(solution.family) && ["1.61", "1.67", "1.74"].includes(solution.index)
      ? ["Scratch Resistant", "Anti-Reflective", "UV Protection"] : [];
    if (solution.index === "1.74" && ["clear", "blue_light_filtering"].includes(solution.family)) coatings.push("Water Stain Resistant");
    const prescriptionUse = !readersCard && !solution.automatic && ["clear", "blue_light_filtering"].includes(solution.family)
      ? { "1.61": "For Low to Moderate Prescriptions", "1.67": "For Moderate Prescriptions", "1.74": "For Moderate to High Prescriptions" }[solution.index] || "" : "";
    return `<ul class="elv2-lens-features">${[profile, weight, purpose, ...coatings, prescriptionUse].filter(Boolean).map((text) => `<li>${escapeHtml(text)}</li>`).join("")}</ul>`;
  }

  function enrichSolutions() {
    state.fields.forEach((field, key) => {
      if (!key.startsWith("solution_") || !field.family) return;
      controls(key).filter((control) => control.type === "radio").forEach((control) => {
        const choice = choiceContainer(control);
        if (!choice || choice.dataset.elv2SalesCard === "true") return;
        const solution = solutionFor(control, field.family);
        if (!solution) { addDiagnostic(`Could not map solution option: ${controlText(control)}`); return; }
        const visiblePrice = (controlText(control).match(/\$\s*\d+(?:\.\d{1,2})?/) || [solution.price])[0].replace(/\s+/g, "");
        const badge = solutionBadge(solution);
        const readersCard = key === "solution_readers_clear" || key === "solution_readers_blue";
        suppressNativeChoiceText(control);
        choice.dataset.elv2SalesCard = "true";
        choice.dataset.elv2ChoiceValue = solution.en;
        choice.classList.add("elv2-sales-card");
        bindCardSelection(choice, control);
        if (solution.automatic) choice.classList.add("elv2-automatic-solution");
        choice.insertAdjacentHTML("beforeend", `${lensThicknessIllustration(solution)}<div class="elv2-sales-card__copy"><div class="elv2-sales-card__top">${bilingual(readersCard ? "1.61 Thin Reading Lenses" : solution.en, readersCard ? "1.61薄型老花镜片" : solution.zh, "strong")}<span class="elv2-sales-card__price">${escapeHtml(visiblePrice)} <small>USD</small></span></div><span class="elv2-badge" data-recommended="${badge[0] === "Recommended"}">${escapeHtml(badge[0])}</span>${lensCardFeatures(solution, readersCard)}${solution.value ? `<p class="elv2-value">${escapeHtml(solution.value[0])}</p>` : ""}${readersCard ? `<p class="elv2-sales-card__recommendation">For your selected reading power</p>` : ``}</div>`);
        const rangeDetails = choice.querySelector(".elv2-range-details");
        rangeDetails?.addEventListener("toggle", schedulePortalSync);
        rangeDetails?.querySelector("summary")?.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          rangeDetails.open = !rangeDetails.open;
        });
      });
    });
  }

  function solutionBadge(solution) {
    if (!manualLensRoute()) return ["Available", "可选"];
    try {
      const evaluated=PositiveRxCore.evaluate(lensEligibilitySnapshot(),solution.family);
      if (solution.automatic || !evaluated.candidateIds.includes(solution.id)) return ["Not available", "不可选"];
      return evaluated.recommendedIds.includes(solution.id) ? ["Recommended", "推荐"] : ["Available", "可选"];
    } catch { return ["Check prescription", "请核对处方"]; }
  }

  function enrichChoices() {
    state.fields.forEach((field, key) => {
      if (key.startsWith("solution_") || key.startsWith("range_") || ["frame_notice", "later_notice"].includes(key)) return;
      controls(key).filter((control) => control.type === "radio").forEach((control) => {
        const choice = choiceContainer(control);
        if (!choice || choice.dataset.elv2ChoiceCopy === "true") return;
        const text = norm(controlText(control));
        const meta = CHOICE_META.find(([pattern]) => pattern.test(text));
        if (!meta) return;
        const [en, zh, description] = meta[1];
        suppressNativeChoiceText(control);
        choice.dataset.elv2ChoiceCopy = "true";
        choice.dataset.elv2ChoiceValue = en;
        choice.classList.add("elv2-choice-card");
        bindCardSelection(choice, control);
        choice.insertAdjacentHTML("beforeend", `<div class="elv2-choice-copy">${bilingual(en, zh, "strong")}<p>${escapeHtml(description)}</p></div>`);
      });
    });
  }

  function bindCardSelection(choice, control) {
    if (choice.dataset.elv2CardBound === "true") return;
    choice.dataset.elv2CardBound = "true";
    choice.setAttribute("role", "radio");
    choice.setAttribute("tabindex", "0");
    const activate = (event) => {
      if (event?.target?.closest?.(".elv2-range-details")) return;
      if (choice.classList.contains("elv2-automatic-solution") || choice.classList.contains("elv2-rx-unavailable")) { event?.preventDefault(); return; }
      if (event?.target === control && event.type === "click") return;
      event?.preventDefault();
      activateOriginalControl(control);
      choice.setAttribute("aria-checked", "true");
    };
    choice.addEventListener("click", activate);
    choice.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") activate(event);
    });
    control.addEventListener("change", () => choice.setAttribute("aria-checked", String(control.checked)));
    choice.setAttribute("aria-checked", String(control.checked));
  }

  function addDiagnostic(message) {
    if (state.unknown.includes(message)) return;
    state.unknown.push(message);
    console.warn(`[ELOOKUS V2] ${message}`);
  }

  const reviewFrameCache = new Map(), reviewProductCache = new Map();
  let reviewRenderToken = 0;

  function reviewNumber(value) {
    if (value === null || value === undefined || String(value).trim() === "" || !/^[+-]?\d+(?:\.\d+)?$/.test(String(value).trim())) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function reviewPower(value) {
    const number = reviewNumber(value);
    return number === null ? "—" : `${number > 0 ? "+" : ""}${number.toFixed(2)}`;
  }

  function reviewSelection() {
    const form = cartRequestContext()?.form;
    const variantId = String(form?.querySelector('[name="id"]')?.value || "");
    const handle = window.ShopifyAnalytics?.meta?.product?.handle || location.pathname.match(/\/products\/([^/?#]+)/)?.[1] || "";
    const type = selectedType(), method = selectedMethod(), key = selectedSolutionKey();
    const control = key ? checkedControl(key) : null;
    const addon = reviewNumber(control?.dataset.addonPrice);
    const solution = solutionFor(control,state.fields.get(key)?.family);
    const get = field => rawRx && method === "manual" && type !== "readers" ? rawRx[field] : numericValue(field);
    const eyes = ["od","os"].map(eye => {
      const cyl = get(`${eye}_cyl`);
      return { eye, sph: get(`${eye}_sph`), cyl, axis: reviewNumber(cyl) === 0 ? null : get(`${eye}_axis`) };
    });
    const upload = uploadSelections.get(fieldGroup("upload"));
    return {
      variantId, handle, frameTitle: state.overlay.querySelector(".elv2-product__copy strong")?.textContent?.trim() || "Selected frame",
      type, method, typeLabel: englishDisplayText(controlText(checkedControl("type"))),
      lensName: solution ? solution.en : englishDisplayText(controlText(control)),
      lensFunction: englishDisplayText(controlText(checkedControl(type === "readers" ? "function_readers" : "function_prescription"))),
      solutionKey: key, solutionValue: control?.value || "", lensCents: addon !== null && addon >= 0 ? Math.round(addon * 100) : null,
      eyes, readingPower: numericValue("readers_power"), pdMode: selectedPdMode(),
      totalPd: numericValue("total_pd"), odPd: numericValue("od_pd"), osPd: numericValue("os_pd"),
      uploadName: method === "upload" ? String(upload?.name || "") : "",
      uploadStatus: method === "upload" ? fieldGroup("upload")?.dataset.elv2UploadState || "empty" : "",
      uploadConfirmed: method === "upload" && Boolean(uploadReference(controls("upload").find(item => item.type === "file"))),
      notation: rawRx && method === "manual" && type !== "readers" ? rawRxEquivalentText() : ""
    };
  }

  async function reviewFrameDetails(variantId, handle) {
    const key = `${handle}:${variantId}`;
    if (!variantId) return { price: null, compareAt: null, color: "", variantTitle: "" };
    if (reviewFrameCache.has(key)) return reviewFrameCache.get(key);
    const request = (async () => {
      let product = null;
      if (handle) {
        if (!reviewProductCache.has(handle)) reviewProductCache.set(handle,window.fetch(`/products/${encodeURIComponent(handle)}.js`,{ credentials: "same-origin", cache: "force-cache", headers: { Accept: "application/json" } }).then(response => response.ok ? response.json() : null).catch(() => null));
        product = await reviewProductCache.get(handle);
        if (!product) reviewProductCache.delete(handle);
      }
      const variant = product?.variants?.find(item => String(item.id) === variantId);
      const local = window.ShopifyAnalytics?.meta?.product?.variants?.find(item => String(item.id) === variantId);
      const validPrice = value => { const number = reviewNumber(value); return number !== null && number >= 0 ? number : null; };
      const productPrice = validPrice(variant?.price), localPrice = validPrice(local?.price);
      const price = productPrice ?? localPrice;
      const compare = productPrice !== null ? reviewNumber(variant.compare_at_price) : localPrice !== null ? reviewNumber(local.compare_at_price) : null;
      const options = Array.isArray(product?.options) ? product.options : [];
      const colorIndex = options.findIndex(option => /^(?:color|colour)$/i.test(String(typeof option === "string" ? option : option?.name || "")));
      const color = colorIndex >= 0 ? String(variant?.options?.[colorIndex] || variant?.[`option${colorIndex+1}`] || "") : "";
      const title = String(variant?.public_title || variant?.title || local?.public_title || "");
      return { price: price !== null && price >= 0 ? Math.round(price) : null, compareAt: price !== null && compare !== null && compare > price ? Math.round(compare) : null, color, variantTitle: /default title/i.test(title) ? "" : title };
    })().catch(() => ({ price: null, compareAt: null, color: "", variantTitle: "" }));
    reviewFrameCache.set(key,request);
    const result = await request;
    if (result.price === null) reviewFrameCache.delete(key);
    return result;
  }

  function reviewPrescriptionMarkup(selection) {
    const esc = escapeHtml;
    if (selection.type === "readers") {
      return `<section class="elv2-review-section"><h2>Reading power</h2><table class="elv2-review-rx"><caption class="elv2-review-sr">Selected reading power for both eyes</caption><thead><tr><th scope="col">Eye</th><th scope="col">Power</th></tr></thead><tbody>${["OD (Right)","OS (Left)"].map(eye => `<tr><th scope="row">${eye}</th><td>${esc(reviewPower(selection.readingPower))}</td></tr>`).join("")}</tbody></table></section>`;
    }
    if (selection.method !== "manual") return `<section class="elv2-review-section"><h2>Prescription</h2><p>${selection.method === "upload" ? selection.uploadConfirmed ? "Uploaded prescription" : selection.uploadStatus === "pending" ? "Uploading prescription…" : "Upload not confirmed" : "Prescription to be sent later"}</p>${selection.uploadName ? `<p class="elv2-review-muted">${esc(selection.uploadName)}</p>` : ""}</section>`;
    const rows = selection.eyes.map(eye => `<tr><th scope="row">${eye.eye === "od" ? "OD (Right)" : "OS (Left)"}</th><td>${esc(reviewPower(eye.sph))}</td><td>${esc(reviewPower(eye.cyl))}</td><td>${reviewNumber(eye.cyl) === 0 ? "None" : esc(reviewNumber(eye.axis) === null ? "—" : String(eye.axis))}</td></tr>`).join("");
    const pd = value => reviewNumber(value) === null ? "—" : `${Number(value)} mm`;
    const pdRows = selection.pdMode === "dual" ? [["OD (Right)",pd(selection.odPd)],["OS (Left)",pd(selection.osPd)]] : [["Total PD",pd(selection.totalPd)]];
    return `<section class="elv2-review-section"><h2>Prescription</h2><table class="elv2-review-rx"><caption class="elv2-review-sr">Your entered prescription</caption><thead><tr><th scope="col">Eye</th><th scope="col">SPH</th><th scope="col">CYL</th><th scope="col">AXIS</th></tr></thead><tbody>${rows}</tbody></table><div class="elv2-review-pd"><h3>PD</h3><dl>${pdRows.map(([label,value]) => `<div><dt>${esc(label)}</dt><dd>${esc(value)}</dd></div>`).join("")}</dl></div>${selection.notation ? `<details class="elv2-review-equivalent"><summary>Equivalent prescription</summary><p class="elv2-review-muted">${esc(selection.notation)}</p></details>` : ""}</section>`;
  }

  function reviewMarkup(selection, frame = null) {
    const esc = escapeHtml;
    const money = cents => cents === null || cents === undefined ? "Price unavailable" : formatMoney(cents);
    const framePrice = frame ? money(frame.price) : "Loading…";
    const frameLabel = frame?.color ? `<p class="elv2-review-muted">Color: ${esc(frame.color)}</p>` : frame?.variantTitle ? `<p class="elv2-review-muted">Variant: ${esc(frame.variantTitle)}</p>` : "";
    const subtotal = frame && frame.price !== null && selection.lensCents !== null ? frame.price + selection.lensCents : null;
    return `<section class="elv2-review-section"><div class="elv2-review-heading"><h2>Frame</h2><div class="elv2-review-price">${frame?.compareAt !== null && frame?.compareAt !== undefined ? `<s aria-label="Original frame price">${esc(formatMoney(frame.compareAt))}</s>` : ""}<strong>${esc(framePrice)}</strong></div></div><p class="elv2-review-name">${esc(selection.frameTitle)}</p>${frameLabel}</section><section class="elv2-review-section"><div class="elv2-review-heading"><h2>Lens</h2><strong class="elv2-review-price">${esc(money(selection.lensCents))}</strong></div>${selection.typeLabel ? `<p>${esc(selection.typeLabel)}</p>` : ""}${selection.lensFunction ? `<p>${esc(selection.lensFunction)}</p>` : ""}<p class="elv2-review-name">${esc(selection.lensName || "No lens selected")}</p></section>${reviewPrescriptionMarkup(selection)}<section class="elv2-review-total" aria-label="Subtotal for this pair"><span>Subtotal for this pair</span><strong>${esc(frame ? money(subtotal) : "Loading…")}</strong></section>`;
  }

  function updateSummary() {
    const token = ++reviewRenderToken;
    if (state.current !== "review" || !state.open) return;
    const summary = state.overlay.querySelector(".elv2-summary");
    if (!summary) return;
    const selection = reviewSelection(), stamp = JSON.stringify(selection), overlay = state.overlay;
    const control = checkedControl(selection.solutionKey), group = fieldGroup(selection.solutionKey);
    summary.innerHTML = reviewMarkup(selection);
    reviewFrameDetails(selection.variantId,selection.handle).then(frame => {
      if (token !== reviewRenderToken || !state.open || state.current !== "review" || state.overlay !== overlay || !summary.isConnected || !overlay.isConnected) return;
      if (checkedControl(selection.solutionKey) !== control || fieldGroup(selection.solutionKey) !== group || (control && (!control.isConnected || !group?.contains(control))) || JSON.stringify(reviewSelection()) !== stamp) return;
      summary.innerHTML = reviewMarkup(selection,frame);
      schedulePortalSync();
    });
  }

  function lensFunctionHeader() {
    const help = guideLink("elookus-lens-guide", "?").replace("<a ", '<a class="elv2-guide-question" aria-label="Lens features explained" ');
    return `<header class="elv2-panel__header"><div class="elv2-function-heading">${bilingual("Lens Function", "镜片功能", "h1")}${help}</div></header>`;
  }



  function lensBundleHeader() {
    const help = guideLink("elookus-lens-guide", "?").replace("<a ", '<a class="elv2-guide-question" aria-label="Understand lens index and thickness" ');
    return `<header class="elv2-panel__header"><div class="elv2-bundle-heading">${bilingual("Lens Bundle", "镜片方案", "h1")}${help}</div></header>`;
  }



  function renderSolutionNotice(note) {
    if (!note) return;
    let message = "";
    if (lensHighAstigmatism()) message = "No high-astigmatism lenses are currently available for this prescription. Please upload your prescription for review.";
    else if (manualLensRoute() && !controls(selectedSolutionKey()).some(control => control.type === "radio" && rawRxCandidate(control,state.fields.get(selectedSolutionKey())?.family))) message = "No lenses in this category are available for your prescription. Please choose another category or upload your prescription for review.";
    note.textContent = message;
    setHidden(note,!message);
  }

  function handlePdToggle(event) {
    const toggle = event.currentTarget;
    const mode = toggle.checked ? "dual" : "total";
    const pattern = mode === "dual" ? /dual pd|左右|单眼/i : /total pd|总瞳距/i;
    const control = controls("pd_mode").find((item) => item.type === "radio" && !item.disabled && pattern.test(item.getAttribute("original-value") || item.value));
    if (!control || !activateOriginalControl(control)) {
      toggle.checked = selectedPdMode() === "dual";
      return showError("PD options are not ready. Please try again.", toggle);
    }
    state.confirmedRx = false;
    render();
  }

  function syncPdToggle() {
    const toggle = state.overlay?.querySelector("[data-elv2-dual-toggle]");
    if (!toggle) return;
    toggle.checked = selectedPdMode() === "dual";
    const group = fieldGroup("pd_mode");
    toggle.disabled = !group || isAppHidden(group) || !controls("pd_mode").some((item) => item.type === "radio" && !item.disabled);
    // The checkbox mirrors the original application controls; these are no longer focus targets.
    controls("pd_mode").forEach((item) => { item.tabIndex = -1; item.setAttribute("aria-hidden", "true"); });
  }

  function ensureDefaultPdMode() {
    // Default only the input format, never a prescription measurement.
    if (!state.open || state.current !== "details" || selectedMethod() !== "manual" || checkedControl("pd_mode")) return;
    const group = fieldGroup("pd_mode");
    if (!group || isAppHidden(group)) return;
    const total = controls("pd_mode").find((control) => control.type === "radio" && !control.disabled && /total pd|总瞳距/i.test(control.getAttribute("original-value") || control.value));
    if (total) activateOriginalControl(total);
  }

  function ensureDefaultCyl() {
    if (rawRx) return;
    // Select the real Globo value through the same mirror change path as user input.
    // Never infer a zero at validation/cart time, or populate inactive upload/Readers data.
    if (!state.open || state.current !== "details" || state.rxPicking || state.rxPendingSnapshot || selectedMethod() !== "manual" || !["single_vision", "prescription_reading"].includes(selectedType())) return;
    for (const key of ["od_cyl", "os_cyl"]) {
      if (checkedControl(key)) continue;
      const group = fieldGroup(key);
      if (!group || isAppHidden(group)) continue;
      const select = group.querySelector("select[data-elv2-native-rx-select]");
      if (!select || select.disabled) continue;
      const zero = Array.from(select.options).find((option) => !option.disabled && option.value && /^0(?:\.0+)?$/.test(String(option.value).trim()));
      if (!zero) continue;
      select.value = zero.value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  function render() {
    if (!rawRx && positiveRxDraftEnabled() && selectedMethod()==="manual") {
      const key=["od_cyl","os_cyl"].find(key => Number(rawRxMirrors.get(key)?.value)>0 || Number(rawRxMirrors.get(key)?.value)<-2);
      if (key) acceptRawRxEdit(key,rawRxMirrors.get(key).value);
    }
    refreshRawRxOptions();
    ensureDefaultCyl();
    ensureDefaultPdMode();
    syncPdToggle();
    syncAxis("od"); syncAxis("os");
    state.fields.forEach((field) => normalizeFieldCopy(field.group, field.key));
    const purchase = checkedControl("purchase");
    setHidden(state.entry.querySelector(".elv2-frame-confirm"), !purchase || !/frame only|仅购买镜框/i.test(controlText(purchase)));
    const pdMode = selectedPdMode();
    const totalSlot = state.overlay.querySelector('[data-elv2-slot="total_pd"]');
    const odSlot = state.overlay.querySelector('[data-elv2-slot="od_pd"]');
    const osSlot = state.overlay.querySelector('[data-elv2-slot="os_pd"]');
    setHidden(totalSlot, pdMode === "dual");
    setHidden(odSlot, pdMode !== "dual");
    setHidden(osSlot, pdMode !== "dual");
    const ids = route();
    if (!ids.includes(state.current)) state.current = ids[0];
    state.panels.forEach((panel, id) => setHidden(panel, id !== state.current));
    const currentIndex = ids.indexOf(state.current);
    const method = selectedMethod();
    renderRxEntry();
    syncMobilePdPickers();
    if (!mobileRxPickerIsValid()) closeMobileRxPicker();
    if (!desktopRxDropdownIsValid()) closeDesktopRxDropdown(false);
    state.overlay.querySelectorAll(".elv2-steps > div").forEach((item) => {
      const index = ids.indexOf(item.dataset.step);
      const skipped = index < 0;
      const meta = STEPS.find((candidate) => candidate.id === item.dataset.step);
      const enNode = item.querySelector(".elv2-label > span");
      let en = meta?.en || "";
      if (skipped && item.dataset.step === "method" && selectedType() === "readers") en = "Not Required";
      if (skipped && item.dataset.step === "details") {
        if (method === "later") en = "Send Later";
        else if (method === "upload") en = "Prescription Uploaded";
        else if (selectedType() === "readers") en = "Not Required";
      }
      if (enNode) enNode.textContent = en;
      setHidden(item, skipped);
      const number = item.querySelector(":scope > span");
      if (number) number.textContent = index + 1;
      item.classList.toggle("is-current", index === currentIndex);
      item.classList.toggle("is-complete", index >= 0 && index < currentIndex);
      item.classList.toggle("is-skipped", skipped);
      if (index === currentIndex) item.setAttribute("aria-current", "step");
      else item.removeAttribute("aria-current");
      item.setAttribute("aria-label", `${item.querySelector(":scope > span")?.textContent || ""}. ${en}`);
    });
    const step = STEPS.find((item) => item.id === state.current);
    const position = ids.indexOf(state.current) + 1;
    const mobileProgressTitle = state.overlay.querySelector(".elv2-mobile-progress strong");
    const mobileStepLabel = MOBILE_STEP_LABELS[step.id] || [step.en, step.zh];
    mobileProgressTitle.textContent = mobileStepLabel[0];
    mobileProgressTitle.setAttribute("aria-label", step.en);
    mobileProgressTitle.title = step.en;
    state.overlay.querySelector(".elv2-mobile-progress span").textContent = `${position} / ${ids.length}`;
    const back = state.overlay.querySelector("[data-elv2-back]");
    back.querySelector(".elv2-label span").textContent = position === 1 ? "Back to product" : "Back";
    const next = state.overlay.querySelector("[data-elv2-next]");
    next.querySelector(".elv2-label span").textContent = state.current === "review" ? "Add to Cart" : "Continue";
    renderCartStatus();
    const pureSelectionStep = ["type", "method", "function", "solution"].includes(state.current);
    setHidden(next, pureSelectionStep);
    const solutionNote = state.panels.get("solution")?.querySelector(".elv2-filter-note");
    renderSolutionNotice(solutionNote);
    updateSummary();
    enrichChoices();
    enrichSolutions();
    behaviorView();
    schedulePortalSync();
  }

  function normalizeFieldCopy(group, key) {
    const fieldLabels = {
      type: "Prescription Type",
      readers_power: "Readers Power",
      method: "Prescription Method",
      upload: "Prescription Upload",
      od_sph: "OD SPH",
      os_sph: "OS SPH",
      od_cyl: "OD CYL",
      os_cyl: "OS CYL",
      od_axis: "OD AXIS",
      os_axis: "OS AXIS",
      pd_mode: "PD Mode",
      total_pd: "Total PD (mm)",
      od_pd: "Right PD (mm)",
      os_pd: "Left PD (mm)",
      function_prescription: "Lens Function",
      function_readers: "Lens Function",
      solution_clear: "Lens Index",
      solution_blue: "Lens Index",
      solution_photo: "Lens Index",
      solution_tinted: "Lens Index",
      solution_readers_clear: "Lens Index",
      solution_readers_blue: "Lens Index",
    };
    const fieldLabel = fieldLabels[key];
    const labelRoot = group.querySelector(":scope > .gpo-label,:scope > label.gpo-label,:scope > .gpo-element__label,:scope > legend");
    const labelNode = labelRoot?.querySelector(".label-content") || labelRoot;
    if (fieldLabel && labelNode) {
      labelNode.textContent = fieldLabel;
      group.dataset.elv2Label = fieldLabel;
    }
    if (["function_prescription", "function_readers"].includes(key)) {
      if (!group.hasAttribute("role")) group.setAttribute("role","group");
      if (!group.hasAttribute("aria-label") && !group.hasAttribute("aria-labelledby")) group.setAttribute("aria-label","Lens Function");
    }
    if (key === "pd_mode") {
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const original = norm(input.getAttribute("original-value") || input.value);
        const valueNode = group.querySelector(`label[for="${CSS.escape(input.id)}"] .choicelist-item__value`) || group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!valueNode) return;
        if (/total pd|总瞳距/.test(original)) valueNode.textContent = "Total PD";
        else if (/dual pd|左右单眼瞳距/.test(original)) valueNode.textContent = "Dual PD";
      });
    }
    const placeholder = group.querySelector(".dropdown-button__value");
    if (placeholder) {
      const empty = !group.querySelector('input[type="radio"]:checked');
      const looksLikePlaceholder = /select|请选择/i.test(placeholder.textContent);
      placeholder.dataset.elv2Placeholder = String(empty && looksLikePlaceholder);
      if (empty && looksLikePlaceholder) placeholder.textContent = "Select";
    }
    if (key === "later_notice") group.textContent = "Production remains on hold until we receive and review your prescription.";
  }
  function ensureMountedGroupClasses(group, field) {
    if (field.step === "entry") {
      group.classList.add("elv2-source-field");
      return;
    }
    group.classList.add("elv2-portal-field");
    if (["od_sph", "os_sph", "od_cyl", "os_cyl", "od_axis", "os_axis"].includes(field.key)) group.classList.add("elv2-rx-field");
    if (field.key === "pd_mode") group.classList.add("elv2-pd-mode-field");
    if (["total_pd", "od_pd", "os_pd"].includes(field.key)) group.classList.add("elv2-pd-value-field");
    const slot = portalSlot(field);
    if (slot) state.portals.set(field.key, slot);
  }

  function mountGroup(group, field) {
    group.dataset.elv2Field = field.key;
    group.dataset.elv2FieldId = field.id;
    group.dataset.elv2Step = field.step;
    group.dataset.elv2Label = group.querySelector("legend,.gpo-label,.gpo-element__label,label")?.textContent?.replace(/\s+/g, " ").trim() || field.key;
    state.fields.set(field.key, { ...field, group });
    if (field.key === "upload") configureUploadField(group);
    if (["od_pd", "os_pd"].includes(field.key)) configureDualPdField(group);
    if (field.key === "total_pd") configureTotalPdField(group);
    if (field.key === "readers_power") configureMobileReadersPicker(group);
    if (NATIVE_RX_KEYS.has(field.key)) configureNativeRxDropdown(group, field.key);
    ensureMountedGroupClasses(group, field);
    normalizeFieldCopy(group, field.key);
  }

  function syncGroups() {
    const known = new Set(state.groups);
    state.fields.forEach((field) => ensureMountedGroupClasses(field.group, field));
    collectGroups(state.root).filter((group) => !known.has(group) && !state.entry.contains(group) && !state.overlay.contains(group)).forEach((group) => {
      const field = identify(group);
      if (field) mountGroup(group, field);
      else addDiagnostic(`Unmapped Globo field: ${identitySignature(group) || labelSignature(group) || "unknown"}`);
      state.groups.push(group);
    });
    enrichChoices();
    enrichSolutions();
  }

  function handleChange(event) {
    if (state.clearing || rawRxWriting) return;
    const group = event.target.closest("[data-elv2-field]");
    if (!group) return;
    const key = group.dataset.elv2Field;
    if (rawRx && (NATIVE_RX_KEYS.has(key) || ["type","method","pd_mode","total_pd","od_pd","os_pd"].includes(key))) { rawRxRevision+=1; state.confirmedRx=false; }
    // Preserve a related correction across coalesced input/change events.
    if (state.current === "details" && state.detailsError?.keys.includes(key)) state.detailsError.dirty = true;
    behaviorSelection(key);
    if (state.cartStatus !== "idle" && state.cartStatus !== "pending") {
      state.cartToken += 1;
      setCartStatus("idle");
    }
    if (event.type === "change" && group === state.portalOpen) window.requestAnimationFrame(() => clearPortalOpen(group));
    if (["type", "method", "od_sph", "os_sph", "od_cyl", "os_cyl", "od_axis", "os_axis", "pd_mode"].includes(key)) state.confirmedRx = false;
    if (key === "purchase") { purchaseModeChanged(event.target); return; }
    window.clearTimeout(state.conditionTimer);
    state.conditionTimer = null;
    if (key === "type" && selectedType() !== "readers") selectRxMethod("manual");
    const fast = scheduleAutoAdvance(key);
    if (!fast) {
      const token = state.autoToken;
      state.conditionTimer = window.setTimeout(() => {
        state.conditionTimer = null;
        if (token !== state.autoToken) return;
        syncGroups(); cleanInvalidRoutes(); render();
        refreshDetailsError(key);
      }, 160);
    }
  }

  function scheduleAutoAdvance(key) {
    window.clearTimeout(state.autoTimer);
    state.autoTimer = null;
    if (state.autoFrame) window.cancelAnimationFrame(state.autoFrame);
    state.autoFrame = 0;
    const token = ++state.autoToken;
    const origin = state.current;
    const method = selectedMethod();
    const fastTarget = origin === "type" && key === "type" && selectedType() !== "readers" ? "details"
      : origin === "method" && key === "method" && method === "manual" ? "details"
        : origin === "method" && key === "method" && method === "later" ? "function"
          : origin === "function" && key.startsWith("function_") ? "solution"
            : origin === "solution" && key.startsWith("solution_") ? "review"
              : null;
    if (fastTarget) {
      const ready = () => {
        const fieldReady = (fieldKey) => {
          const group = fieldGroup(fieldKey);
          return Boolean(group && !isAppHidden(group) && controls(fieldKey).some((control) => !control.disabled));
        };
        if (fastTarget === "method") return fieldReady("method");
        if (fastTarget === "details") return ["od_sph", "os_sph", "od_cyl", "os_cyl", "pd_mode"].every(fieldReady);
        if (fastTarget === "function") return fieldReady(selectedType() === "readers" ? "function_readers" : "function_prescription");
        if (fastTarget === "solution") {
          const expected = selectedSolutionKey();
          const available = Array.from(state.fields.keys()).filter((fieldKey) => fieldKey.startsWith("solution_") && fieldReady(fieldKey));
          return Boolean(expected && available.length === 1 && available[0] === expected);
        }
        const field = state.fields.get(key);
        const control = checkedControl(key);
        const ids = route();
        return Boolean(field?.family && control && !control.disabled && !isAppHidden(field.group) && solutionFor(control, field.family) && ids[ids.indexOf("solution") + 1] === "review");
      };
      const deadline = window.performance.now() + 640;
      const check = () => {
        state.autoFrame = 0;
        if (token !== state.autoToken || state.current !== origin) return;
        if (ready()) {
          syncGroups(); cleanInvalidRoutes();
          if (token === state.autoToken && state.current === origin && validateStep()) advance();
          return;
        }
        if (window.performance.now() < deadline) state.autoFrame = window.requestAnimationFrame(check);
        else { syncGroups(); cleanInvalidRoutes(); render(); }
      };
      state.autoFrame = window.requestAnimationFrame(check);
      return true;
    }
    state.autoTimer = window.setTimeout(() => {
      state.autoTimer = null;
      if (token !== state.autoToken) return;
      syncGroups(); cleanInvalidRoutes(); render();
      refreshDetailsError(key);
      if (state.current === "type") {
        if (key === "type" && selectedType() !== "readers" && validateStep()) advance();
        else if (key === "readers_power" && selectedType() === "readers" && validateStep()) advance();
      } else if (state.current === "method") {
        if (key === "method" && ["manual", "later"].includes(selectedMethod()) && validateStep()) advance();
        else if (key === "upload" && selectedMethod() === "upload" && validateStep()) advance();
      } else if (state.current === "function" && key.startsWith("function_") && validateStep()) advance();
      else if (state.current === "solution" && key.startsWith("solution_") && validateStep()) advance();
    }, 380);
    return false;
  }

  function handleRootMutations(mutations) {
    const meaningful = mutations.filter((mutation) => {
      if (state.overlay.contains(mutation.target)) return false;
      if (mutation.target.closest?.(".gpo-dropdown,.dropdown-list,[role='listbox'],[role='option']")) return false;
      if (mutation.type === "childList") return addsGloboGroup(mutation);
      const group = mutation.target;
      if (!(group instanceof HTMLElement) || !group.hasAttribute("data-elv2-field")) return false;
      if (["data-clo-display", "hidden", "aria-hidden"].includes(mutation.attributeName)) return true;
      if (mutation.attributeName === "class") {
        const field = state.fields.get(group.dataset.elv2Field);
        const requiredClass = field?.step === "entry" ? "elv2-source-field" : "elv2-portal-field";
        if (requiredClass && !group.classList.contains(requiredClass)) return true;
        return visibilityClassSignature(mutation.oldValue) !== visibilityClassSignature(group.className);
      }
      if (mutation.attributeName === "style") return visibilityStyleSignature(mutation.oldValue) !== visibilityStyleSignature(group.getAttribute("style"));
      return false;
    });
    if (!meaningful.length) return;
    if (meaningful.some((mutation) => mutation.type === "childList" || mutation.attributeName === "class")) syncGroups();
    scheduleRender();
  }

  function unbindRoot(root) {
    state.rootObserver?.disconnect();
    state.rootObserver = null;
    if (!root) return;
    root.removeEventListener("change", handleChange);
    root.removeEventListener("input", handleChange);
    root.removeEventListener("pointerdown", handlePortalPointerDown, true);
    root.removeEventListener("focusin", handlePortalFocusIn, true);
    root.removeEventListener("focusout", handlePortalFocusOut, true);
    root.removeEventListener("touchstart", handlePortalTouchStart, true);
    root.removeEventListener("touchmove", handlePortalTouchMove, true);
    root.removeEventListener("touchend", handlePortalTouchEnd, true);
    root.removeEventListener("touchcancel", handlePortalTouchCancel, true);
    root.removeEventListener("click", suppressDraggedPortalClick, true);
  }

  function bindRoot(root) {
    root.addEventListener("change", handleChange);
    root.addEventListener("input", handleChange);
    root.addEventListener("pointerdown", handlePortalPointerDown, true);
    root.addEventListener("focusin", handlePortalFocusIn, true);
    root.addEventListener("focusout", handlePortalFocusOut, true);
    root.addEventListener("touchstart", handlePortalTouchStart, { passive: true, capture: true });
    root.addEventListener("touchmove", handlePortalTouchMove, { passive: false, capture: true });
    root.addEventListener("touchend", handlePortalTouchEnd, { passive: true, capture: true });
    root.addEventListener("touchcancel", handlePortalTouchCancel, { passive: true, capture: true });
    root.addEventListener("click", suppressDraggedPortalClick, true);
    const observerOptions = { subtree: true, childList: true, attributes: true, attributeOldValue: true, attributeFilter: ["class", "style", "hidden", "aria-hidden", "data-clo-display"] };
    state.rootObserver = new MutationObserver(handleRootMutations);
    state.rootObserver.observe(root, observerOptions);
  }

  function variantInstanceIntact(candidate) {
    const purchase = state.fields.get("purchase")?.group;
    return Boolean(candidate && candidate === state.root && state.root.isConnected && state.entry?.isConnected && state.overlay?.isConnected && purchase?.isConnected);
  }

  function rebindVariantRoot(nextRoot) {
    const groups = collectGroups(nextRoot);
    if (!groups.length || !state.entry || !state.overlay) return false;
    if (state.open) closeOverlay();
    else releasePortalInteraction();
    rawRx=null; rawRxRevision+=1; rawRxSources.clear(); rawRxMirrors.clear(); state.rxPickerRaw=null;
    unbindRoot(state.root);
    state.root?.classList.remove("elv2-globo-root");
    if (state.portalFrame) window.cancelAnimationFrame(state.portalFrame);
    if (state.renderFrame) window.cancelAnimationFrame(state.renderFrame);
    if (state.autoFrame) window.cancelAnimationFrame(state.autoFrame);
    window.clearTimeout(state.autoTimer);
    window.clearTimeout(state.conditionTimer);
    state.uploadObservers.forEach((observer) => observer.disconnect());
    state.uploadObservers.clear();
    state.fields.clear();
    state.portals.clear();
    state.groups = [];
    state.portalFrame = 0;
    state.renderFrame = 0;
    state.autoFrame = 0;
    state.autoTimer = null;
    state.conditionTimer = null;
    state.portalOpen = null;
    state.portalsSuspended = false;
    state.confirmOpen = false;
    state.current = "type";
    state.open = false;
    state.confirmedRx = false;
    state.cartActions = [];
    state.overlay.hidden = true;
    state.overlay.querySelectorAll("[data-elv2-slot],.elv2-field-slot").forEach((slot) => { slot.style.minHeight = ""; });
    state.root = nextRoot;
    groups[0].before(state.entry);
    nextRoot.append(state.overlay);
    groups.forEach((group) => {
      const field = identify(group);
      if (field) mountGroup(group, field);
      else addDiagnostic(`Unmapped Globo field: ${identitySignature(group) || labelSignature(group) || "unknown"}`);
    });
    state.groups = groups;
    nextRoot.classList.add("elv2-globo-root");
    bindRoot(nextRoot);
    captureProduct();
    findCartActions();
    positionEntry();
    render();
    return true;
  }

  function clearVariantPending() {
    document.documentElement.classList.remove("elv2-variant-pending");
    window.clearTimeout(state.variantRevealTimer);
    state.variantRevealTimer = 0;
  }

  function scheduleVariantRefresh() {
    if (rawRx) Array.from(NATIVE_RX_KEYS).forEach(clearField);
    rawRxRevision+=1; state.rxPickerRaw=null; state.confirmedRx=false; clearRawRxMetadata();
    document.documentElement.classList.add("elv2-variant-pending");
    window.clearTimeout(state.variantRevealTimer);
    state.variantRevealTimer = window.setTimeout(clearVariantPending, 1200);
    if (state.open) closeOverlay();
    window.clearTimeout(state.variantTimer);
    state.variantTimer = window.setTimeout(() => {
      state.variantTimer = 0;
      const candidate = findRoot();
      if (!candidate) return;
      if (!variantInstanceIntact(candidate)) {
        rebindVariantRoot(candidate);
        clearVariantPending();
        return;
      }
      captureProduct();
      state.cartActions = [];
      findCartActions();
      positionEntry();
      schedulePortalSync();
      clearVariantPending();
    }, 120);
  }

  function initialize(root) {
    state.root = root;
    const groups = collectGroups(root);
    if (!groups.length) return false;
    const entry = createUi();
    groups[0].before(entry);
    createNav(); captureProduct();
    groups.forEach((group) => {
      const field = identify(group);
      if (field) mountGroup(group, field);
      else addDiagnostic(`Unmapped Globo field: ${identitySignature(group) || labelSignature(group) || "unknown"}`);
    });
    state.groups = groups;
    root.classList.add("elv2-globo-root");
    state.overlay.addEventListener("change", handleChange);
    state.overlay.addEventListener("input", handleChange);
    bindRoot(root);
    state.overlay.querySelector(".elv2-main").addEventListener("scroll", schedulePortalSync, { passive: true });
    window.addEventListener("resize", schedulePortalSync, { passive: true });
    document.addEventListener("on:variant:change", scheduleVariantRefresh, true);
    findCartActions();
    positionEntry();
    render();
    document.documentElement.classList.add("elv2-lens-ready");
    document.getElementById("elookus-lens-v2-status")?.remove();
    document.dispatchEvent(new CustomEvent("elookus:lens-flow-v2-ready", { detail: { fieldMap: Object.keys(FIELD_MAP).length } }));
    return true;
  }

  function boot() {
    const root = findRoot();
    if (root && initialize(root)) return;
    let attempts = 0;
    const timer = window.setInterval(() => {
      const candidate = findRoot();
      if (candidate && initialize(candidate)) window.clearInterval(timer);
      else if (++attempts > 80) { window.clearInterval(timer); console.warn("[ELOOKUS V2] Globo fields were not found."); }
    }, 250);
  }

  function scheduleBoot() {
    window.setTimeout(boot, 0);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", scheduleBoot, { once: true });
  else scheduleBoot();
})();



/* Independent context help. Does not read or write lens configuration or cart state. */
(() => {
  "use strict";
  if (document.documentElement.dataset.elookusGuideBound) return;
  document.documentElement.dataset.elookusGuideBound = "true";
  const guides = {
    "elookus-prescription": {
      title: "Read your prescription",
      items: [
        "Don’t have a copy of your prescription? Ask the eye care professional who performed your eye exam for a copy.",
        "OD / RE means right eye; OS / LE means left eye. Keep each value with its eye label.",
        "SPH is spherical power. CYL and AXIS describe cylinder correction. Copy signs and numbers exactly as written.",
        "Do not calculate new values or change a sign to fit a dropdown. If a value is missing, unclear, or unavailable, check with your eye care professional."
      ]
    },
    "elookus-pd-guide": {
      title: "Understand your PD",
      items: [
        "Pupillary distance (PD) is the distance between the centres of your pupils, measured in millimetres.",
        "Total / single PD is one combined number. Dual PD has separate right and left values; they do not have to match.",
        "Keep distance or near labels with the numbers. Do not divide a total PD to invent separate right and left measurements.",
        "Check your glasses prescription. If PD is missing or unclear, ask your clinic or optician for your measured PD."
      ]
    },
    "elookus-frame-size": {
      title: "Read frame sizes",
      items: [
        "Lens width and height describe one lens opening. Bridge width is the marked distance between the lens openings.",
        "Temple length follows the arm from hinge to tip. Total frame width spans the frame front; do not calculate it by adding lens widths and bridge.",
        "Compare the same measurements with a comfortable pair. Bridge shape, cheek clearance, and how the arms sit also affect fit."
      ]
    },
    "elookus-face-shape": {
      title: "Explore frame shapes",
      items: [
        "Start with comfort: a suitable overall width, a comfortable bridge, and stable temples.",
        "Face shape is a style starting point, not a rule. Compare curved and angular frames, then fine and bold rims.",
        "Compare two frames in similar lighting, changing one detail at a time. Choose the pair that feels comfortable and suits your style."
      ]
    },
    "elookus-lens-purpose": {
      title: "Choose your lens purpose",
      items: [
        "Single vision serves one main viewing distance. Use the prescription provided for distance or for near work.",
        "Readers use the reading power you have been advised to use. Prescription Reading uses your near-vision glasses prescription.",
        "An ADD value alone is not a complete reading prescription. Do not calculate new values yourself; ask your eye care professional if unsure."
      ]
    },
    "elookus-lens-guide": {
      title: "Lens thickness & features",
      items: [
        "For the same prescription, frame, and lens design, a higher index can make a lens thinner. Final thickness depends on the complete combination.",
        "1.61 balances thickness and cost. 1.67 and 1.74 offer thinner options where available. The choices shown in the lens flow determine availability.",
        "Clear lenses have no added tint. Photochromic lenses darken outdoors and gradually lighten indoors; tinted lenses keep a consistent colour.",
        "Blue-light filtering is an optional lens feature. Choose the features that fit how you plan to use your glasses."
      ]
    },
    "elookus-care": {
      title: "Cleaning & care",
      items: [
        "Rinse gently with lukewarm water to remove loose dust, then use a clean lens cloth with light pressure.",
        "Store glasses in a clean case. Avoid hot water and high heat, including a hot car.",
        "Use both hands to put glasses on and take them off. Do not forcefully bend the frame; ask an optician if the fit needs adjustment."
      ]
    }
  };
  let dialog, trigger, hadScrollLock;
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };
  function closeGuide() {
    if (dialog?.open) dialog.close();
  }
  function buildDialog() {
    dialog = make("dialog", "elookus-guide-dialog");
    dialog.setAttribute("aria-modal", "true");
    const header = make("header", "elookus-guide-dialog__header");
    header.append(make("h2", "elookus-guide-dialog__title"));
    const close = make("button", "elookus-guide-dialog__close", "Close");
    close.type = "button";
    close.setAttribute("aria-label", "Close guide");
    close.addEventListener("click", closeGuide);
    header.append(close);
    dialog.append(header, make("div", "elookus-guide-dialog__body"));
    const footer = make("footer", "elookus-guide-dialog__footer");
    const back = make("button", "elookus-guide-dialog__return");
    back.type = "button";
    back.addEventListener("click", closeGuide);
    const full = make("a", "elookus-guide-dialog__full", "Read full tutorial (opens in a new tab)");
    full.target = "_blank";
    full.rel = "noopener";
    footer.append(back, full);
    dialog.append(footer);
    dialog.addEventListener("cancel", (event) => { event.preventDefault(); closeGuide(); });
    dialog.addEventListener("keydown", (event) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); closeGuide(); }
    });
    dialog.addEventListener("click", (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeGuide();
    });
    dialog.addEventListener("close", () => {
      if (!hadScrollLock) document.documentElement.classList.remove("elookus-guide-open");
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      trigger = null;
    });
    document.body.append(dialog);
  }
  document.addEventListener("click", (event) => {
    const link = event.target.closest?.("a[data-elookus-guide]");
    const guide = link && guides[link.dataset.elookusGuide];
    if (!guide || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!window.HTMLDialogElement || typeof HTMLDialogElement.prototype.showModal !== "function") return;
    event.preventDefault();
    event.stopPropagation();
    if (!dialog) buildDialog();
    if (dialog.open) return;
    trigger = link;
    const inFlow = Boolean(link.closest(".elv2-overlay"));
    dialog.setAttribute("aria-label", guide.title);
    dialog.querySelector("h2").textContent = guide.title;
    const body = dialog.querySelector(".elookus-guide-dialog__body");
    body.replaceChildren(make("p", "elookus-guide-dialog__note", inFlow ? "Your selections stay in place while you read." : "Close this guide to continue browsing."));
    const list = make("ul");
    guide.items.forEach((item) => list.append(make("li", "", item)));
    body.append(list);
    dialog.querySelector(".elookus-guide-dialog__return").textContent = inFlow ? "Return to lens selection" : "Back to product";
    dialog.querySelector(".elookus-guide-dialog__full").href = link.href;
    hadScrollLock = document.documentElement.classList.contains("elookus-guide-open");
    document.documentElement.classList.add("elookus-guide-open");
    dialog.showModal();
    body.scrollTop = 0;
    dialog.querySelector(".elookus-guide-dialog__close").focus({ preventScroll: true });
  }, true);
})();


